/**
 * The `atlasCamera` probe: what `tools/preview/dojo/arms/` and the rest of the
 * headless instruments read off `scene.userData.atlasCamera`.
 *
 * Moved out of src/atlas.ts unchanged. The seam holds no state of its own -
 * every member is a read of the mount's closure, reached through `ProbeDeps`,
 * whose members the mount hands over as accessors so that nothing here
 * snapshots a live value at construction. The members that read nothing
 * outside another seam ride on that seam's own `probe` and are folded in at
 * the bottom, as they were.
 */

import * as THREE from 'three';
import { hexCorners } from './dtmPlot.ts';
import { BLOCK_MIN_D, OBSTACLE_STRIDE } from './rockField.ts';
import { ensureAddresses } from './plotName.ts';
import {
  CREW_HOLDER, getClaim, setClaim, releaseClaim, cellAt, claimedCellsAt,
  claimsVersion, setClaimsDebug, claimsDebug,
} from './claims.ts';
import { dayFraction, sunAngles, softClimate } from './plotField.ts';
import { isPyramidPlot } from './pyramidPlot.ts';
import { DESCENT_RUNG } from './descentQueue.ts';
import { type CreditTier } from './tileSource.ts';
import {
  formatTileKey,
  neighboursOf,
  parentOf as tileParentOf, parseTileKey, tileForDirection,
  type TileKey,
} from './tileTree.ts';
import { atlasToBody, inheritedHeightRangeM, TILE_MESH_QUADS } from './tileMesh.ts';
import {
  LABEL_CLEAR_MARGIN, LABEL_CLEAR_RAMP,
  LABEL_PICK_MIN_OPACITY,
  NAME_FADE_GONE_HALF_DEG,
  ORRERY_SUN_COLOR,
  ORRERY_SUN_INTENSITY,
  UNITS_PER_KM,
  type SolRegionDisc,
} from './orrery.ts';
import type { SolPhotosphereReading } from './solPhotosphere';
import { simApi } from '../sim/api.ts';
import { RING_FOOTPRINT_TAPS, RING_MU_SUN_FLOOR, RING_MU_VIEW_FLOOR } from './rings.ts';
import { corridorBandIndexAt } from './corridor.ts';
import { bandAt, isOnScreen, type Band, type FramingScope } from './camera.ts';
import { type WorldVec } from '../world/frame.ts';
import type { SunCoverReading } from './sunCover.ts';
import type { BodyRow } from '../world/types';
import { gameClock, MISSION_ANCHOR_T } from '../world/clock.ts';
import type { CascadesHandle } from './cascades';
import type { GlobeMaterialHandle } from './globeMaterial';
import type { InputHandle } from './input';
import type { BodyManifestHandle } from './manifestPanel';
import type { InspectorHandle } from './inspector';
import type { LayerPrefsHandle } from './panels/layerPrefs';
import type { PickHandle } from './pick';
import type { StructureViewHandle } from './structureView';
import type { SiteChipsHandle } from './panels/siteChips';
import type { SolarScrubHandle } from './solarScrub';
import type { AtlasLive, AtlasState, Site } from '../atlas';
import type { PyramidViewHandle } from './pyramidView.ts';
import type { DescentLoadHandle } from './descentLoad.ts';
import type { InjectsHandle } from './injects.ts';
import type { CameraRigHandle } from './cameraRig.ts';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { HexViewHandle } from './hexView.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { DescentAnchorHandle } from './descentAnchor.ts';
import type { ShaderWarmHandle } from './shaderWarm.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import type { AtmosphereViewHandle } from './atmosphereView.ts';
import type { FarFieldHandle } from './farField.ts';
import type { MountHandle } from './mount.ts';
import type { DojoPaintsHandle } from './dojoPaints.ts';
import type { PlotBuildHandle } from './plotBuild.ts';
import type { ExposureHandle } from './exposure.ts';
import type { SurfaceRadiusHandle } from './surfaceRadius.ts';
import type { PlotControlsHandle } from './panels/plotControls.ts';
import type { NavigationHandle } from './navigation.ts';
import type { PlotGeometryHandle } from './plotGeometry.ts';
import type { RoverDriveHandle } from './roverDrive.ts';
import type { BodySwitchHandle } from './bodySwitch.ts';
import type { SelectionHandle } from './selection.ts';
import type { PlotSunHandle } from './plotSun.ts';
import type { PlotMountHandle } from './plotMount.ts';
import type { FrameGovernorHandle } from './frameGovernor.ts';
import type { OverlayViewHandle } from './overlayView.ts';
import type { WorldHostHandle, WorldHostState } from './worldHost.ts';
import type { SiteGroundHandle } from './siteGround.ts';
import type { CatalogueStarsHandle } from '../scene/sky.ts';
import type { SiteRegistryState } from './siteRegistry.ts';
import type { ZoomLadderHandle } from './zoomLadder.ts';
import type { FocusGroundHandle } from './focusGround.ts';
import type { ViewClockHandle } from './viewClock.ts';
import { TEST_SWITCHES } from '../urlFlags.ts';
import { seaDebug, syncSeaDebug } from './seaSurface.ts';

export interface ProbeDeps {
  readonly pyramidView: PyramidViewHandle;
  readonly descentLoad: DescentLoadHandle;
  readonly injects: InjectsHandle;
  readonly cameraRig: CameraRigHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly hexViewSeam: HexViewHandle;
  readonly focusRig: FocusRigHandle;
  readonly descentAnchorHandle: DescentAnchorHandle;
  readonly shaderWarm: ShaderWarmHandle;
  readonly bodyScene: BodySceneHandle;
  readonly atmosphereView: AtmosphereViewHandle;
  readonly farField: FarFieldHandle;
  readonly mount: MountHandle;
  readonly dojoPaints: DojoPaintsHandle;
  readonly plotBuild: PlotBuildHandle;
  readonly exposureHandle: ExposureHandle;
  readonly surfaceRadius: SurfaceRadiusHandle;
  readonly plotControls: PlotControlsHandle;
  readonly navigation: NavigationHandle;
  readonly plotGeometry: PlotGeometryHandle;
  readonly roverDrive: RoverDriveHandle;
  readonly bodySwitch: BodySwitchHandle;
  readonly selection: SelectionHandle;
  readonly plotSun: PlotSunHandle;
  readonly plotMount: PlotMountHandle;
  readonly frameGovernor: FrameGovernorHandle;
  readonly overlayView: OverlayViewHandle;
  readonly worldHost: WorldHostHandle;
  readonly siteGround: SiteGroundHandle;
  readonly stars: CatalogueStarsHandle;
  readonly siteRegistry: SiteRegistryState;
  readonly ladder: ZoomLadderHandle;
  readonly focusGround: FocusGroundHandle;
  readonly viewClockHandle: ViewClockHandle;
  readonly worldHostState: WorldHostState;
  readonly live: AtlasLive;

  readonly _q: THREE.Quaternion;
  readonly _v: THREE.Vector3;
  readonly BAND_EXPOSURE: Readonly<Record<Band, number>>;
  readonly BEHIND_AIR_ORDER: -50;
  readonly cascades: CascadesHandle;
  readonly detailGroup: THREE.Group<THREE.Object3DEventMap>;
  readonly EXPOSURE_EASE_PER_FRAME: 0.01;
  readonly GLOBE_DEPTH_PUSH_RADII: 0.25;
  readonly globeMaterial: GlobeMaterialHandle;
  readonly GRID_STAGES: ReadonlyArray<{ readonly label: string; readonly fill: number; readonly cells: number; readonly parcels: number }>;
  readonly input: InputHandle;
  readonly inspector: InspectorHandle;
  readonly layerPrefs: LayerPrefsHandle;
  readonly manifest: BodyManifestHandle;
  readonly overlay: HTMLDivElement;
  readonly pick: PickHandle;
  readonly structureView: StructureViewHandle;
  readonly renderer: THREE.WebGLRenderer;
  readonly root: HTMLDivElement;
  readonly site: () => Site | undefined;
  readonly siteChips: SiteChipsHandle;
  readonly solarScrub: SolarScrubHandle;
  solsSinceAnchorLive(): number;
  readonly state: AtlasState;
  readonly tileGroup: THREE.Group<THREE.Object3DEventMap>;
}

/**
 * The layer `shapeSilhouette` renders the focused body's mesh alone on.
 *
 * A silhouette is the BODY against the background, and the haze shell, the
 * leaves and the star field all draw over or around the same disc. Nothing else
 * in the Atlas uses a layer, so moving the mesh onto one for the probe's own
 * render and putting it straight back costs no traversal anywhere else.
 */
const SILHOUETTE_LAYER = 7;

export function createProbe(deps: ProbeDeps) {
  const EXPOSURE_MIN = Math.min(...Object.values(deps.BAND_EXPOSURE));
  const EXPOSURE_MAX = Math.max(...Object.values(deps.BAND_EXPOSURE));

  /**
   * `descent-dojo --land --inject land-no-reaim`: the reuse path's own skip.
   *
   * Read off the URL and not only a setter, because the press this is about
   * happens inside one click and a switch thrown from outside arms after it.
   */
  const landNoReaim = typeof location !== 'undefined' && TEST_SWITCHES
    && new URLSearchParams(location.search).get('landnoreaim') === '1';

  function goTo(bodyId: string, lat: number | null, lon: number | null, altM: number): void {
    deps.state.bodyId = bodyId;
    // Held while the rows are still a round trip away: the registry's own
    // load handler is where this ask can first be read as a rung.
    if (deps.bodyScene.state.bodyRows.length === 0) deps.bodyScene.state.pendingFocus = { bodyId, lat, lon, altM };
    deps.focusRig.focusCam.goTo(bodyId, lat, lon, altM);
    deps.live.cameraDirty = true;
    deps.navigation.syncTierToAltitude();
    // With the rows already in hand the replay above never fires, so this is
    // the only place a LAND onto a mounted Atlas can pick up the rung's aim
    // at the surveyed site; without it the descent keeps the sub-ship point.
    deps.navigation.applyState({ reframe: deps.bodyScene.state.pendingFocus === null && deps.state.tier > 0 && !landNoReaim, altM });
  }

  /**
   * `descent-dojo --inject shape-globe-evict` and `--inject
   * shape-globe-count-after-load`.
   *
   * Which body's field was dropped and which tally the globes are counting on,
   * so a run reads back what it armed rather than assuming the hook took.
   */
  const shapeGlobeDebug: { countAfterLoad: boolean; evictedBody: string | null } = {
    countAfterLoad: false, evictedBody: null,
  };

  /**
   * The registry fields the LADDER body's climate has to stand in for, read
   * off the row rather than off `bodyClimate`'s cache: the sky arms ask about
   * the body the page loaded, which is the URL's answer before anything is
   * focused.
   */
  const climateMissing = (): readonly string[] => {
    const row = deps.bodyReaders.rowById(deps.bodyReaders.ladderBody());
    return row ? softClimate(row, deps.bodyReaders.rowById).missing : [];
  };

  const _shapeV = new THREE.Vector3();
  const _shapeEye = new THREE.Vector3();
  const _shapeQ = new THREE.Quaternion();

  /** How many claim reads the queue has out; see `holdTileFetches`. */
  const claimHoldsOut = (): number => deps.descentLoad.descentQueue.stats().rungs[DESCENT_RUNG.CLAIM]?.out ?? 0;

  /**
   * The eye and the pivot against the drawn LEAVES, for the bands above the
   * walkaround: `plotClearance`'s other half.
   *
   * Same criterion as the plot's, read off the pyramid instead of the whole-file
   * mesh, so `surface-clamp` can gate every notch from the CLAIM edge down and
   * not only the two frames where a plot exists to ask.
   */
  function tileClearance(): unknown {
    const node = deps.bodyScene.state.bodyNode;
    const body = deps.pyramidView.tileBody;
    if (!node || body === null) return null;
    const eyeR = Math.hypot(
      deps.mount.cameraWorld.x - node.world.x, deps.mount.cameraWorld.y - node.world.y, deps.mount.cameraWorld.z - node.world.z,
    );
    const groundR = deps.pyramidView.groundRadiusUnder(body, deps.mount.cameraWorld);
    if (groundR === null) return null;
    const lat = deps.focusRig.focusCam.state.latDeg;
    const lon = deps.focusRig.focusCam.state.lonDeg;
    const pivot = lat === null || lon === null ? null : deps.focusRig.focusBodies.surface(body, lat, lon);
    const pivotG = pivot ? deps.pyramidView.groundRadiusUnder(body, pivot) : null;
    const pivotR = pivot
      ? Math.hypot(pivot.x - node.world.x, pivot.y - node.world.y, pivot.z - node.world.z)
      : null;
    return {
      source: 'tiles',
      eyeM: +(eyeR - groundR).toFixed(3),
      pivotM: pivotR !== null && pivotG !== null ? +(pivotR - pivotG).toFixed(3) : null,
      eyeRadiusM: +eyeR.toFixed(1),
      groundRadiusM: +groundR.toFixed(1),
      datumRadiusM: deps.surfaceRadius.leafDatumM(),
      anchorRadiusM: deps.surfaceRadius.datumRadiusM(body),
      eyeMinusDatumM: +(eyeR - deps.surfaceRadius.leafDatumM()).toFixed(1),
      exag: deps.solarScrub.solar.exag,
    };
  }

  /**
   * The level this ground is drawn at, or null when it is drawn finer than this
   * key - which is itself a level boundary, from the other side.
   */
  function drawnLevelOf(key: TileKey): number | null {
    let k: TileKey | null = key;
    while (k) {
      if (deps.pyramidView.tileMeshes.has(formatTileKey(k))) return k.level;
      k = tileParentOf(k);
    }
    return null;
  }

  /**
   * Real tile edges on screen, for `ground-dojo --seam`.
   *
   * A `sibling` edge is shared by two tiles at the same level and must match
   * exactly; a `level` edge is where the quadtree changes level and cannot,
   * which is what the skirt covers. Both come back as the projected segment the
   * seam metric works in (`tools/preview/spectral.py`, `line_mask`), so the
   * instrument never has to know how a quad key becomes pixels.
   */
  function tileSeams(): unknown[] {
    const r = deps.mount.surface.getBoundingClientRect();
    deps.mount.camera.updateMatrixWorld();
    const out: unknown[] = [];
    for (const [id, handle] of deps.pyramidView.tileMeshes) {
      // A mesh held invisible by a split is not on screen, and its edge against
      // the coarser tile still covering that ground is not a seam anybody sees.
      if (deps.pyramidView.tileHidden.has(id)) continue;
      const key = parseTileKey(id);
      const nbs = neighboursOf(key);
      handle.object.updateMatrixWorld();
      for (let e = 0; e < 4; e++) {
        const nb = nbs[e]!;
        // A face edge is a different animal - two grids meeting at a right
        // angle - and is not what this measures.
        if (nb.face !== key.face) continue;
        const other = drawnLevelOf(nb);
        if (other === key.level) continue;
        const ends = handle.edgeEnds(e);
        // The whole projected segment, not a visibility verdict: a tile edge is
        // tens of kilometres long and almost never has both ends on screen, so
        // whether there is a usable stretch of it is decided by clipping it to
        // the crop, which the instrument does. What has to be answered here is
        // whether the ends are in FRONT of the camera at all, because a point
        // behind it projects to a mirrored position that would clip as if it
        // were on screen.
        const px = ends.map((v) => {
          const w = handle.object.localToWorld(v.clone());
          const view = w.clone().applyMatrix4(deps.mount.camera.matrixWorldInverse);
          const p = w.clone().project(deps.mount.camera);
          return {
            x: r.left + (p.x * 0.5 + 0.5) * r.width,
            y: r.top + (-p.y * 0.5 + 0.5) * r.height,
            front: view.z < -deps.mount.camera.near,
          };
        });
        const [a, b] = px as [(typeof px)[number], (typeof px)[number]];
        out.push({
          id,
          edge: e,
          level: key.level,
          other,
          kind: 'level',
          skirted: handle.skirted,
          x0: a.x,
          y0: a.y,
          x1: b.x,
          y1: b.y,
          lenPx: Math.hypot(b.x - a.x, b.y - a.y),
          onFrame: a.front && b.front,
        });
      }
    }
    return out;
  }

  /**
   * Do two same-level neighbours share their edge exactly?
   *
   * The claim behind not skirting a sibling boundary: an edge vertex's
   * half-sample normal stencil reads the apron sample on one side and the
   * neighbour's own first interior sample on the other, and those are the same
   * two numbers in the same order on both tiles - so the shared edge is
   * computed from identical data. This measures it instead: corresponding
   * vertices of the two meshes' shared edge, in the tile group's own frame.
   * Cross-face edges are skipped, being two grids meeting at a right angle
   * rather than a shared edge.
   *
   * Reported in metres AND as a fraction of the tile's own mesh cell, which is
   * the number with a threshold attached. A position attribute is float32 about
   * the tile's centre, so its quantum scales with the tile: 2.5 mm at L7 and 2
   * cm at L3, which is a hundred-thousandth of a triangle either way. Data the
   * two tiles disagreed about would be metres on a cell of one kilometre.
   */
  function tileEdgeMatch(): unknown {
    let pairs = 0;
    let maxM = 0;
    let maxCells = 0;
    const gaps: number[] = [];
    const cells: number[] = [];
    const radius = deps.pyramidView.tileSource?.manifest()?.datum_radius_m ?? deps.bodyReaders.registryDatumM(deps.pyramidView.tileBody);
    for (const [id, handle] of deps.pyramidView.tileMeshes) {
      const key = parseTileKey(id);
      const nbs = neighboursOf(key);
      for (let e = 0; e < 4; e++) {
        const nb = nbs[e]!;
        if (nb.face !== key.face) continue;
        const other = deps.pyramidView.tileMeshes.get(formatTileKey(nb));
        if (!other) continue;
        // Each pair once: the neighbour's matching edge is e flipped within
        // its own axis, and only the lower key walks it.
        if (formatTileKey(nb) < id) continue;
        const mine = handle.edgeVerts(e);
        const theirs = other.edgeVerts(e ^ 1);
        if (mine.length !== theirs.length) continue;
        pairs++;
        const a = handle.object.position;
        const b = other.object.position;
        let worst = 0;
        for (let k = 0; k < mine.length; k += 3) {
          worst = Math.max(worst, Math.hypot(
            mine[k]! + a.x - (theirs[k]! + b.x),
            mine[k + 1]! + a.y - (theirs[k + 1]! + b.y),
            mine[k + 2]! + a.z - (theirs[k + 2]! + b.z),
          ));
        }
        const cellM = (Math.PI * radius) / (2 * 2 ** key.level * TILE_MESH_QUADS);
        // Divided by the exaggeration, because the claim is that two tiles
        // read the same GROUND: a vertex's offset from its own tile centre
        // carries the stretched height, so its float32 rounding scales with
        // the slider and a display transform would otherwise move a geometric
        // gate. A real disagreement is metres and fails at any exaggeration.
        const trueM = worst / Math.max(1e-6, deps.solarScrub.solar.exag);
        gaps.push(trueM);
        cells.push(trueM / cellM);
        maxM = Math.max(maxM, trueM);
        maxCells = Math.max(maxCells, trueM / cellM);
      }
    }
    gaps.sort((x, y) => x - y);
    cells.sort((x, y) => x - y);
    return {
      pairs,
      exag: deps.solarScrub.solar.exag,
      maxM,
      maxCells,
      medianM: gaps.length > 0 ? gaps[Math.floor(gaps.length / 2)]! : 0,
      medianCells: cells.length > 0 ? cells[Math.floor(cells.length / 2)]! : 0,
      drawn: deps.pyramidView.tileMeshes.size,
      levels: drawnTileLevels(),
    };
  }

  /** The levels currently on screen, coarsest first, for the instruments. */
  function drawnTileLevels(): number[] {
    const seen = new Set<number>();
    for (const handle of deps.pyramidView.tileMeshes.values()) {
      seen.add(handle.object.userData['tileLevel'] as number);
    }
    return [...seen].sort((a, b) => a - b);
  }

  const _reentryAnchor = new THREE.Vector3();
  // `rockProbe`'s scratch. Its own, because it runs while the frame's are live.
  const _probeInv = new THREE.Matrix4();
  const _probeA = new THREE.Vector3();
  const _probeB = new THREE.Vector3();
  const _probeC = new THREE.Vector3();
  const _probeD = new THREE.Vector3();

  /** `descent-dojo --labels`: `suppress` is the paint diff the arm reads a
   *  label's own pixels with; the other two are its controls. */
  const labelDebug: { suppress: boolean; noDeclutter: boolean; surfaceGate: boolean } =
    { suppress: false, noDeclutter: false, surfaceGate: false };

  const _ringQ = new THREE.Quaternion();
  const _ringSunProbe = new THREE.Vector3();
  const _ringCamProbe = new THREE.Vector3();
  const _ringPoint = new THREE.Vector3();
  const _ringWorld = new THREE.Vector3();
  const _ringDir = new THREE.Vector3();
  const _ringNdc = new THREE.Vector3();
  const _ringNdcDr = new THREE.Vector3();
  const _ringNdcDa = new THREE.Vector3();
  const _ringView = new THREE.Vector3();
  const _ringCamLocal = new THREE.Vector3();

  return Object.defineProperties({
    get altitude(): number { return deps.focusRig.altitude(); },
    /** Who set the altitude last, and whether the ground ease a stand asked for is still owed. */
    get standEase(): { wheelZoomed: boolean; owed: boolean; focusGroundM: number } {
      const cam = deps.focusRig.focusCam;
      return { wheelZoomed: cam.wheelZoomed, owed: cam.standEaseOwed, focusGroundM: deps.focusGround.state.focusGroundM };
    },
    get band(): Band { return deps.focusRig.band(); },
    get tier(): number { return deps.state.tier; },
    get bodyId(): string { return deps.focusRig.focusCam.state.bodyId; },
    /**
     * The site the ladder is showing, by `sites.json`'s own id - null on a
     * hand-off pinned off the loaded site's own survey, even though `site()`
     * itself still names it. `grounded-handoff-check.mjs`'s outer-hex and
     * off-site legs read this.
     */
    get siteId(): string | null { return deps.descentAnchorHandle.displaySite()?.id ?? null; },
    // `sites` and `siteChips` moved to the site-chips module's own probe:
    // both read nothing outside that seam.
    /**
     * What the URL asked for that the registry does not have.
     *
     * Read by the dojos instead of a frame: a refused body or site draws
     * nothing, and a black stage is the one reading an instrument must never
     * take for a clean descent.
     */
    get refused(): { body: string | null; site: string | null; known: readonly string[] } {
      return {
        body: deps.bodySwitch.state.bodyRefused,
        site: deps.bodySwitch.state.siteRefused,
        known: deps.bodySwitch.state.bodyRefused !== null ? deps.bodyScene.state.bodyRows.map((r) => r.id) : deps.siteRegistry.bodySites.map((st) => st.id),
      };
    },
    /**
     * The exposure table, its bounds and where the ease has got to.
     *
     * `descent-dojo --colour` reads the bounds off the app rather than carrying
     * a copy: a criterion holding its own copy of the table passes a build
     * whose table has moved.
     */
    get exposure(): unknown {
      // Scaled by the same per-body factor updateExposure eases toward, and
      // the whole table with it - a scaled target beside an unscaled table
      // would fail the dojo's own table[band] === target check. SYSTEM is
      // exempted from that scale, so the table exempts it too.
      const rawScale = deps.exposureHandle.bodyExposureScale(deps.state.bodyId);
      const scale = deps.exposureHandle.bodyScaleAppliesHere() ? rawScale : 1;
      const table = Object.fromEntries(
        (Object.entries(deps.BAND_EXPOSURE) as [Band, number][]).map(([k, v]) => {
          const applies = deps.exposureHandle.exposureDebug.meterAtSystem || k !== 'SYSTEM';
          return [k, v * (applies ? rawScale : 1)] as const;
        }),
      ) as Record<Band, number>;
      return {
        band: deps.focusRig.band(),
        target: table[deps.focusRig.band()],
        current: +deps.exposureHandle.state.exposure.toFixed(5),
        easePerFrame: deps.EXPOSURE_EASE_PER_FRAME,
        min: EXPOSURE_MIN * scale,
        max: EXPOSURE_MAX * scale,
        table,
      };
    },
    /**
     * Where the ladder is aimed: the surveyed site's own centre, and null on a
     * hand-off pinned off that site's baked survey, where there is no surveyed
     * ground to ring. `grounded-handoff-check.mjs`'s SURVEYED and UNSURVEYED
     * legs read this as whether the ring is drawn at all.
     */
    get anchor(): { lat: number; lon: number } | null { return deps.descentAnchorHandle.anchorLatLon(); },
    /** The ground under the EYE, body-fixed: where a descent is coming down. */
    get subCamera(): { lat: number; lon: number; radiusM: number } {
      return deps.hexViewSeam.cameraGroundPoint();
    },
    /**
     * How much of that aim the camera is actually holding.
     *
     * `body-sweep --focus` reads it: a first focus frames the body's own centre
     * and the site is the aim only once the ground is the subject, so the
     * convergence is 0 through ORBIT and 1 below the band under it.
     */
    focusAim(): unknown {
      return { bodyId: deps.focusRig.focusCam.state.bodyId, anchor: deps.descentAnchorHandle.anchorLatLon(), ...deps.focusRig.focusCam.aim() };
    },
    /**
     * Put a pre-fix pose back: the aim pinned on the site, or the basis on it.
     *
     * `sitePivot` is the aim from the first frame - the state that framed
     * Jezero at ORBIT and left Mars's lower third off screen - and `siteAxis`
     * is the basis alone, which framed the body's centre and still turned it
     * about the site. `orbitPivot` is the eye's radius swinging with the pitch
     * about the focus point at every convergence, which is what put one
     * reported ORBIT altitude at two distances from the body's centre.
     * `yawAxisFromBasis` is the yaw reference re-picked from the turning basis
     * instead of from the body's pole, which flipped the whole frame partway
     * down the band on a body whose pole is near the ecliptic normal.
     * `yawAxisFromPole` is that reference picked off the pole but still crossed
     * with the live up rather than carried from the pole, which the site's own
     * blend walks across on a zoom and the body's spin twice a day.
     * `body-sweep --focus --inject site-pivot`, `--inject site-axis`,
     * `descent-dojo --pivot --inject orbit-pivot` and `descent-dojo --crossing
     * --inject basis-yaw-axis` are their arms.
     */
    setFocusDebug(next: {
      sitePivot?: boolean; siteAxis?: boolean; orbitPivot?: boolean; yawAxisFromBasis?: boolean;
      yawAxisFromPole?: boolean;
    }): void {
      if (next.sitePivot === undefined && next.siteAxis === undefined
          && next.orbitPivot === undefined && next.yawAxisFromBasis === undefined
          && next.yawAxisFromPole === undefined) return;
      if (next.sitePivot !== undefined) deps.focusRig.focusCam.setLegacySitePivot(next.sitePivot);
      if (next.siteAxis !== undefined) deps.focusRig.focusCam.setLegacySiteAxis(next.siteAxis);
      if (next.orbitPivot !== undefined) deps.focusRig.focusCam.setLegacyOrbitPivot(next.orbitPivot);
      if (next.yawAxisFromBasis !== undefined) deps.focusRig.focusCam.setLegacyYawAxis(next.yawAxisFromBasis);
      if (next.yawAxisFromPole !== undefined) deps.focusRig.focusCam.setLegacyPoleYawAxis(next.yawAxisFromPole);
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    /**
     * Hold the focus on the body's own centre through a reframe. DEV only.
     *
     * `descent-dojo --pivot` derives its tolerance from a sweep with no pitch
     * term in it, which is `solve()`'s centre-anchored branch in
     * src/atlas/camera.ts; on a body with a surveyed site the reframe
     * re-attaches that site, so the sweep lands back on the blended pose the
     * arm's own control changes and the reference stops being a reference.
     */
    setCentreAnchorHold(on: boolean): void {
      if (!import.meta.env.DEV) return;
      deps.bodyScene.state.centreAnchorHold = on;
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    get fovDeg(): number { return deps.mount.camera.fov; },
    /** Meshes a reopened pyramid is still drawing from the tree it replaced. */
    get tilesHeld(): number { return deps.pyramidView.tileHeld.size; },
    /**
     * The pyramid's streamer, as the descent dojo records it.
     *
     * The tree's own bookkeeping plus what this module did with it: what is
     * resident against the budget, what is in flight, and which levels are
     * actually drawn. Null until the first descent past the streaming altitude
     * opens a tree at all.
     */
    get tiles(): unknown {
      const stats = deps.pyramidView.tileTree?.stats() ?? null;
      if (!stats) {
        return { streaming: deps.pyramidView.tilesActive(), open: false, error: deps.pyramidView.tileError,
          legacyTileGate: deps.pyramidView.tileDebug.legacyTileGate, wholeRelease: deps.pyramidView.tileDebug.wholeRelease };
      }
      return {
        streaming: deps.pyramidView.tilesActive(),
        open: true,
        maxLevel: deps.pyramidView.tileMaxLevel,
        reliefOn: deps.pyramidView.tileReliefOn.value,
        reliefEvalP95Ms: +deps.pyramidView.reliefEvalP95Ms.toFixed(3),
        reliefEvalSamples: deps.pyramidView.reliefEvalSamples,
        reliefEvalBuilds: deps.pyramidView.reliefEvalBuilds,
        reliefWorkerP95Ms: +deps.pyramidView.reliefWorkerP95Ms.toFixed(3),
        reliefWorkerSamples: deps.pyramidView.reliefWorkerSamples,
        reliefWorkerEvals: deps.pyramidView.reliefWorkerEvals,
        /** The deepest GLOBAL level; a drawn level at or above it is MOLA/MDIM ground. */
        globalFloor: deps.pyramidView.tileGlobalFloor,
        /**
         * Tile requests issued per role, counted in tileSource's own fetch
         * path, plus the displaced globe's own L0 height tiles: those are
         * height tile requests the app made and the network log holds them.
         */
        roleFetches: deps.pyramidView.tileSource
          ? (() => {
            const r = deps.pyramidView.tileSource.roleFetches();
            return { ...r, height: r.height + (deps.bodyScene.state.shapeGlobes?.heightFetches() ?? 0) };
          })()
          : null,
        /** Tiles mounted carrying an elevation payload, since this pyramid opened. */
        heightMounts: deps.pyramidView.tileHeightMounts,
        drawn: deps.pyramidView.tileMeshes.size,
        pending: deps.pyramidView.tilePending.size,
        remeshing: deps.pyramidView.tileRemesh.size,
        triangles: deps.pyramidView.tileTriangles,
        levels: drawnTileLevels(),
        resident: stats.resident,
        residentMb: +(stats.residentBytes / (1024 * 1024)).toFixed(1),
        inFlight: stats.inFlight,
        queued: stats.queued,
        fetched: stats.fetched,
        aborted: stats.aborted,
        superseded: stats.superseded,
        timedOut: stats.timedOut,
        failed: stats.failed,
        overBudget: stats.overBudget,
        complete: stats.complete,
        pinned: stats.pinned,
        textures: deps.pyramidView.tileTextures.size,
        inherited: [...deps.pyramidView.tileMeshes.values()].filter(
          (h) => h.albedoFromId !== null && !h.albedoFromId.startsWith('site:')).length,
        /**
         * Drawn tiles carrying an artificial fill, and whether the switch is
         * letting them draw it.
         *
         * A mesh counts here when it or an ancestor of it landed a
         * `.art.webp`, so the reading separates "the layer is off" from "this
         * ground has no fill" - two ways to draw nothing that an instrument
         * has to be able to tell apart.
         */
        artificialTiles: [...deps.pyramidView.tileMeshes.values()].filter((h) => h.artificialTextured).length,
        /** Drawn tiles whose material shades by the body's artificial relief. */
        reliefTiles: [...deps.pyramidView.tileMeshes.values()].filter((h) => h.reliefShaded).length,
        /** Visible tiles whose vertices the relief displaces: what `groundPointAt` marches against. */
        reliefDisplacedTiles: [...deps.pyramidView.tileMeshes.values()]
          .filter((h) => h.reliefDisplaced && h.object.visible).length,
        /**
         * Drawn tiles carrying the body's own globe map as their fill, apart
         * from `artificialTiles` above: no `.art.webp` was requested for any
         * of these and yet they show a fill, which is the fallback and not
         * the streamed role.
         */
        artificialGlobeTiles: [...deps.pyramidView.tileMeshes.values()]
          .filter((h) => h.artificialGlobeTextured).length,
        artificialOn: deps.pyramidView.tileArtificial.drawn(),
        siteImages: deps.pyramidView.siteAlbedo?.loaded ?? 0,
        siteTextured: [...deps.pyramidView.tileMeshes.values()].filter(
          (h) => h.albedoFromId?.startsWith('site:') === true).length,
        // How far the region image's affine per tile is from the true mapping.
        siteUvTexels: +(deps.pyramidView.siteAlbedo?.worstResidualTexels ?? 0).toFixed(3),
        exag: deps.solarScrub.solar.exag,
        skirts: deps.pyramidView.tileDebug.skirts,
        geomorph: deps.pyramidView.tileDebug.geomorph,
        splitPx: deps.pyramidView.tileDebug.splitPx,
        holdSplits: deps.pyramidView.tileDebug.holdSplits,
        wholeRelease: deps.pyramidView.tileDebug.wholeRelease,
        orthoTint: deps.pyramidView.tileDebug.orthoTint,
        siteImage: deps.pyramidView.tileDebug.siteImage,
        retired: deps.pyramidView.tileRetired.size,
        hidden: deps.pyramidView.tileHidden.size,
        // Covers ready to go that the per-frame release cap held back.
        releasing: deps.pyramidView.tileReleaseWaiting,
        releaseFrameWorst: deps.pyramidView.tileReleaseFrameWorst,
        releasePerFrame: deps.pyramidView.RELEASE_PER_FRAME,
        injectStepM: deps.pyramidView.tileDebug.injectStepM,
        injectTiles: [...deps.pyramidView.tileDebug.injectTiles],
        injectEdges: deps.pyramidView.tileDebug.injectEdges,
        heightFallback: deps.pyramidView.tileDebug.heightFallback,
        legacyHorizon: deps.pyramidView.tileDebug.legacyHorizon,
        legacyTileGate: deps.pyramidView.tileDebug.legacyTileGate,
        /** Whether the relief arc is driving refinement; the control's readback. */
        ringUnbudgeted: deps.descentLoad.ringUnbudgeted,
        /** Whether the horizon-band hole is punched; the control's readback. */
        ringHole: deps.descentLoad.ringHole,
        /** Whether the queue abandons its in-flight bytes; the control's readback. */
        ringChurn: deps.descentLoad.ringChurn,
        reliefM: deps.pyramidView.tileDebug.legacyHorizon
          ? null
          : Math.round(deps.bodyReaders.reliefAboveDatumM(deps.pyramidView.tileBody, deps.surfaceRadius.datumRadiusM(deps.pyramidView.tileBody) ?? 0)),
        // Leaves drawing an ancestor's relief because their own level has none.
        heightInherited: [...deps.pyramidView.tileMeshes.values()].filter((h) => h.heightFromId !== null).length,
        error: deps.pyramidView.tileError,
      };
    },
    /**
     * Toggles skirts, the geomorph and the split threshold, or moves the
     * per-tile injected edge-ring step (`injectStepM`, a probe the seam-check
     * scales by its own on-screen size) - each change queues an
     * insertion-ordered remesh swap rather than rebuilding in place.
     *
     * A call carries any number of flags at once and every one of them is
     * applied; what they ask for together is one rebuild, at the widest scope
     * any of them needs.
     */
    /** `descent-dojo --coverage --inject glare-over-disc` and `--inject
     *  hot-exposure`: see `SolHandle.setDebug`. */
    setSolDebug(next: { glareOverDisc?: boolean; hotExposure?: boolean }): void {
      deps.bodyScene.state.orrery?.sol.setDebug(next);
      deps.live.needsRender = true;
    },
    /**
     * `descent-dojo --live-sun`: the live photosphere's own switches.
     *
     * `live` is the LIVE SUN row, `artificial` the ARTIFICIAL FILL row as it
     * reaches the region list, and `load` is the model fetch a player triggers
     * by opening Sol. `frozen` holds the date the shader carries rotation from,
     * `noSpots` forces the region count to zero, `eagerRecord` fetches the
     * big record file immediately rather than waiting for the clock to enter
     * its own range, and `whiteDisc` draws the live layer on the bare tinted
     * disc again instead of the 304 A map - the four controls. `rate` draws a
     * held clock as a warp rung would (null hands back the clock's own), and
     * `strobe` ignores the rate, the control for the granule turnover fade.
     */
    setSolPhotosphere(next: {
      live?: boolean; artificial?: boolean; load?: boolean;
      frozen?: boolean; noSpots?: boolean; eagerRecord?: boolean; whiteDisc?: boolean;
      rate?: number | null; strobe?: boolean;
    }): void {
      if (next.load === true) deps.bodyScene.state.orrery?.loadSolPhotosphere();
      if (next.live !== undefined) deps.bodyScene.state.orrery?.setSolLive(next.live);
      if (next.artificial !== undefined) deps.bodyScene.state.orrery?.setArtificialFill(next.artificial);
      if (next.frozen !== undefined || next.noSpots !== undefined
        || next.eagerRecord !== undefined || next.whiteDisc !== undefined
        || next.rate !== undefined || next.strobe !== undefined) {
        deps.bodyScene.state.orrery?.setSolPhotosphereDebug({
          frozen: next.frozen, noSpots: next.noSpots, eagerRecord: next.eagerRecord,
          whiteDisc: next.whiteDisc, rate: next.rate, strobe: next.strobe,
        });
      }
      deps.live.needsRender = true;
    },
    /** What the disc's live layer holds this frame, numbers and the note. */
    solPhotosphereReading(): SolPhotosphereReading | null {
      return deps.bodyScene.state.orrery?.solPhotosphereReading() ?? null;
    },
    /** Where each drawn region sits on the billboard, in the disc's own radii. */
    solRegionDisc(): readonly SolRegionDisc[] {
      return deps.bodyScene.state.orrery?.solRegionDisc() ?? [];
    },
    setTileDebug(next: {
      skirts?: boolean; geomorph?: boolean; splitPixelsPerSample?: number;
      injectStepM?: number; injectTiles?: readonly string[]; injectEdges?: number;
      holdSplits?: boolean; wholeRelease?: boolean; orthoTint?: boolean; siteImage?: boolean;
      legacyHorizon?: boolean; coverageFill?: boolean; legacyTileGate?: boolean;
    }): void {
      // The widest rebuild this call asks for: a new tree, every drawn mesh, or
      // only the named ones, raised by each flag that rebuilds something. A
      // call naming no changed flag gets the whole set, as its tail always did.
      let newTree = false;
      let allMeshes = false;
      let someMeshes = false;
      let asked = false;
      const someTiles = new Set<string>();

      if (next.coverageFill !== undefined && next.coverageFill !== deps.pyramidView.tileDebug.coverageFill) {
        // The ref is read when a mesh's material is built, so every drawn tile
        // has to be rebuilt for the chunk to come or go.
        deps.pyramidView.tileDebug.coverageFill = next.coverageFill;
        allMeshes = true;
        asked = true;
        deps.live.needsRender = true;
      }
      if (next.legacyTileGate !== undefined && next.legacyTileGate !== deps.pyramidView.tileDebug.legacyTileGate) {
        // Read per frame by `tilesActive()`, so the next frame streams what the
        // rule now allows: nothing already drawn changes with it.
        deps.pyramidView.tileDebug.legacyTileGate = next.legacyTileGate;
        asked = true;
        deps.live.needsRender = true;
      }
      if (next.legacyHorizon !== undefined && next.legacyHorizon !== deps.pyramidView.tileDebug.legacyHorizon) {
        // The horizon rule is read when the tree is built, so this is a new
        // tree rather than a rebuild of the meshes - the same as the split
        // threshold below.
        deps.pyramidView.tileDebug.legacyHorizon = next.legacyHorizon;
        newTree = true;
        asked = true;
        deps.live.needsRender = true;
      }
      if (next.holdSplits !== undefined && next.holdSplits !== deps.pyramidView.tileDebug.holdSplits) {
        deps.pyramidView.tileDebug.holdSplits = next.holdSplits;
        // Whatever is retired now was retired under the old rule, so it goes
        // rather than sitting on screen for a rule that no longer applies.
        if (!deps.pyramidView.tileDebug.holdSplits) {
          for (const id of [...deps.pyramidView.tileRetired]) deps.pyramidView.dropTileMesh(id);
          deps.pyramidView.resolveTileCover();
        }
      }
      if (next.wholeRelease !== undefined && next.wholeRelease !== deps.pyramidView.tileDebug.wholeRelease) {
        deps.pyramidView.tileDebug.wholeRelease = next.wholeRelease;
        // The pre-fix release keeps no steps, so the ones kept now go; they
        // are invisible, so nothing on screen changes.
        if (deps.pyramidView.tileDebug.wholeRelease) {
          for (const id of [...deps.pyramidView.tileRetired]) if (deps.pyramidView.tileHidden.has(id)) deps.pyramidView.dropTileMesh(id);
        }
        deps.pyramidView.resolveTileCover();
      }
      if ((next.orthoTint !== undefined && next.orthoTint !== deps.pyramidView.tileDebug.orthoTint)
        || (next.siteImage !== undefined && next.siteImage !== deps.pyramidView.tileDebug.siteImage)) {
        if (next.orthoTint !== undefined) deps.pyramidView.tileDebug.orthoTint = next.orthoTint;
        if (next.siteImage !== undefined) deps.pyramidView.tileDebug.siteImage = next.siteImage;
        // Only the leaves inside a footprint sample the orthoimage at all.
        for (const id of deps.pyramidView.siteLeafKeys()) someTiles.add(id);
        someMeshes = true;
        asked = true;
        deps.live.needsRender = true;
      }
      const coverChanged = (next.skirts !== undefined && next.skirts !== deps.pyramidView.tileDebug.skirts)
        || (next.geomorph !== undefined && next.geomorph !== deps.pyramidView.tileDebug.geomorph);
      if (next.skirts !== undefined) deps.pyramidView.tileDebug.skirts = next.skirts;
      if (next.geomorph !== undefined) deps.pyramidView.tileDebug.geomorph = next.geomorph;
      if (coverChanged) allMeshes = true;
      if (next.splitPixelsPerSample !== undefined
        && next.splitPixelsPerSample !== deps.pyramidView.tileDebug.splitPx) {
        // The threshold is read once, when the tree is built, so changing it is
        // a new tree rather than a rebuild of the meshes.
        deps.pyramidView.tileDebug.splitPx = next.splitPixelsPerSample;
        newTree = true;
        asked = true;
        deps.live.needsRender = true;
      }
      const wasInjected = [...deps.pyramidView.tileDebug.injectTiles];
      if (next.injectStepM !== undefined) deps.pyramidView.tileDebug.injectStepM = next.injectStepM;
      if (next.injectEdges !== undefined) deps.pyramidView.tileDebug.injectEdges = next.injectEdges;
      if (next.injectTiles !== undefined) {
        deps.pyramidView.tileDebug.injectTiles = new Set(next.injectTiles);
      }
      // Only the named tiles when only the injection moved: a whole rebuild is
      // 450 meshes at two a frame, and the arm changes the injection per pose.
      if (!coverChanged && (next.injectStepM !== undefined || next.injectTiles !== undefined
        || next.injectEdges !== undefined)) {
        for (const id of wasInjected) someTiles.add(id);
        for (const id of deps.pyramidView.tileDebug.injectTiles) someTiles.add(id);
        someMeshes = true;
        asked = true;
      }
      if (!asked) allMeshes = true;

      if (newTree) {
        // The tree itself is gone, so there is nothing left drawn to remesh.
        deps.pyramidView.releaseTiles();
        deps.live.needsRender = true;
      } else if (allMeshes) {
        deps.pyramidView.remeshTiles();
      } else if (someMeshes) {
        deps.pyramidView.remeshTiles(someTiles);
      }
      // Else only the streaming gate moved, and its redraw is above.
    },
    /**
     * The costliest frames of pyramid work since the last read.
     *
     * `reset` empties the list, so a caller reading it per leg is reading that
     * leg's own frames and not the run's.
     */
    /**
     * The last tick's own cost and how much of it was inside `render`.
     *
     * `tileFrames` only records a frame the STREAMER did work on, so a frame
     * that cost hundreds of milliseconds with nothing mounted or built leaves
     * no record at all - and that is exactly the frame an instrument has to
     * attribute. These two say whether the cost was inside the draw (a GPU or
     * compositor stall, which is what a shared card produces) or outside it.
     */
    get tickMs(): number { return +deps.pyramidView.lastTickMs.toFixed(2); },
    get renderMs(): number { return +deps.pyramidView.lastRenderMs.toFixed(2); },
    tileFrames(reset = false): unknown[] {
      const out = deps.pyramidView.tileWorst.map((r) => ({ ...r }));
      if (reset) deps.pyramidView.tileWorst.length = 0;
      return out;
    },
    /** Real pyramid seams on screen: a level boundary, projected to pixels. */
    tileSeams(): unknown[] { return tileSeams(); },
    /**
     * What the traversal says should be drawn, as keys.
     *
     * The MESH set lags this by the per-frame build budget, and ground in here
     * that no mesh covers is the hole `tile-swap-check.mjs` measures.
     * Instrument only: it allocates a string per drawn tile.
     */
    tileDrawSet(): string[] { return [...deps.pyramidView.tileDrawn.keys()]; },
    /** The credit line's plain text, empty when nothing MDIM/MOLA is drawn. */
    get creditText(): string { return deps.pyramidView.creditText; },
    /** Which product tiers are behind the drawn set, in display order. */
    get creditSources(): readonly CreditTier[] { return deps.pyramidView.creditSourceIds; },
    /** `layout-check --self`: hides the credit without touching what is drawn. */
    setCreditHidden(hidden: boolean): void {
      deps.pyramidView.creditForceHidden = hidden;
      deps.pyramidView.applyCreditVisibility();
    },
    /**
     * `layout-check --self`: the other lie - an empty source list while the
     * pyramid draws global tiles, which is what the assertion has to catch
     * without reading the app's own conclusion about visibility.
     */
    setCreditSourcesBlank(blank: boolean): void {
      if (deps.pyramidView.creditForceBlank === blank) return;
      deps.pyramidView.creditForceBlank = blank;
      deps.pyramidView.creditMixKey = null;
      deps.pyramidView.updateCredit();
    },
    /** How far two same-level neighbours' shared edge vertices are apart, metres. */
    tileEdgeMatch(): unknown { return tileEdgeMatch(); },

    /**
     * How far the eye and the pivot are above the ground under them, in metres.
     *
     * `surface-clamp.mjs`'s criterion, and the one with no image of its own: a
     * pivot under the terrain aims the eye into it, and the frame that comes
     * back is sky and nothing else, which reads as a load failure
     * rather than as a camera fault. The PIVOT is the number that fails - the
     * eye read +2.3 m on the build that drew nothing. Null outside SURFACE or
     * before the mesh lands, which are the states with no ground to be above.
     * The plot answers, so the reading and the floor use one height field.
     */
    /**
     * Every drawn leaf whose own level carries no height, and how much relief
     * its mesh got out of the ancestor it borrowed one from.
     *
     * `surface-clamp --relief`. `ancestorRangeM` is that ancestor's OWN range
     * over the same footprint, so the floor the reading is gated against
     * follows the ground it is over rather than being a constant.
     */
    tileRelief(): unknown {
      const rows: {
        id: string; level: number; fromId: string | null; levelsUp: number | null;
        rangeM: number; ancestorRangeM: number | null;
      }[] = [];
      for (const [id, tile] of deps.pyramidView.tileDrawn) {
        if (tile.height !== null) continue;
        const handle = deps.pyramidView.tileMeshes.get(id);
        if (!handle) continue;
        // The traversal's own ref, not `heightRefFor`: with the fallback off
        // the floor still has to be readable, or the control has nothing to
        // fail against.
        const ref = deps.pyramidView.tileDraw.get(id)?.height ?? null;
        rows.push({
          id,
          level: tile.level,
          fromId: handle.heightFromId,
          levelsUp: ref?.levelsUp ?? null,
          rangeM: handle.heightRangeM,
          ancestorRangeM: ref ? inheritedHeightRangeM(ref) : null,
        });
      }
      rows.sort((a, b) => (b.ancestorRangeM ?? 0) - (a.ancestorRangeM ?? 0));
      return {
        body: deps.pyramidView.tileBody,
        heightFallback: deps.pyramidView.tileDebug.heightFallback,
        heightFloorLevel: deps.pyramidView.tileSource?.manifest()?.roles.get('height')?.floor_level ?? null,
        deepestLevel: deps.pyramidView.tileGlobalFloor,
        drawn: deps.pyramidView.tileDrawn.size,
        meshed: deps.pyramidView.tileMeshes.size,
        leaves: rows,
      };
    },
    /**
     * The drawn ground at a lat/lon on the focused body, metres over the datum
     * with exaggeration taken out, off the visible mesh the ground probe stands on.
     *
     * Moves the eye: pins the descent anchor there and goes to `altM`, then waits
     * until the finest level the tree is cut to is drawn there and done morphing.
     */
    async groundHeightAt(
      latDeg: number, lonDeg: number, opts: { altM?: number; timeoutMs?: number } = {},
    ): Promise<{
      h: number; level: number; wantLevel: number; tile: string; waitedMs: number;
      displaced: boolean; reliefOn: number; fieldM: number | null;
    } | { err: string }> {
      const body = deps.bodyReaders.ladderBody();
      if (!body) return { err: 'no body is focused' };
      deps.descentAnchorHandle.pinDescentAnchor({ body, lat: latDeg, lon: lonDeg });
      goTo(body, latDeg, lonDeg, opts.altM ?? 8);
      const t0 = performance.now();
      const timeoutMs = opts.timeoutMs ?? 30_000;
      for (;;) {
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        const want = deps.pyramidView.tileMaxLevel;
        const p = deps.pyramidView.tileBody === body ? deps.pyramidView.tileHeightProbe(latDeg, lonDeg, true) : null;
        const waitedMs = Math.round(performance.now() - t0);
        if (p && p.level >= want && (deps.pyramidView.tileDraw.get(p.id)?.morph ?? 1) >= 1) {
          const sampler = deps.bodyScene.state.orrery?.byId(body)?.tileRelief ?? null;
          const la = (latDeg * Math.PI) / 180;
          const lo = (lonDeg * Math.PI) / 180;
          const cellM = (Math.PI * (deps.pyramidView.tileSource?.manifest()?.datum_radius_m ?? 0)) / (2 * 2 ** p.level * TILE_MESH_QUADS);
          return {
            h: +p.h.toFixed(3), level: p.level, wantLevel: want, tile: p.id, waitedMs,
            displaced: deps.pyramidView.tileMeshes.get(p.id)?.reliefDisplaced ?? false,
            reliefOn: deps.pyramidView.tileReliefOn.value,
            fieldM: sampler ? +sampler.heightM(Math.cos(la) * Math.cos(lo), Math.cos(la) * Math.sin(lo), Math.sin(la), cellM).toFixed(3) : null,
          };
        }
        if (waitedMs > timeoutMs) {
          return { err: `L${want} not drawn at ${latDeg}/${lonDeg} after ${waitedMs} ms (deepest L${p?.level ?? 'none'})` };
        }
      }
    },
    plotClearance(): unknown {
      if (!deps.cameraRig.surfaceRegime() || !deps.live.plot || !deps.live.plotField) return tileClearance();
      return {
        source: 'plot',
        eyeM: +deps.live.plot.clearanceAboveGround(deps.cameraRig.camPos).toFixed(3),
        pivotM: +deps.live.plot.clearanceAboveGround(deps.cameraRig.orbit.lookAt).toFixed(3),
        reliefM: deps.live.plotField.meta.elevation.relief_m,
        minM: deps.live.plotField.meta.elevation.min_m,
        exag: deps.solarScrub.solar.exag,
        selected: deps.live.selectedSub !== null,
      };
    },
    /**
     * What the plot mesh is painting its ground with, and where it came from.
     *
     * `surface-clamp --handover`'s colour half: the plot draws the same ground
     * the leaves under it do, so its own colour has to be the body's - the
     * inherited colour off the pyramid, or the body's own ramp - and never a
     * constant tuned on another body.
     */
    plotPaint(): unknown {
      const row = deps.bodyReaders.rowById(deps.state.bodyId);
      const ramp = deps.globeMaterial.albedoRampFor(row);
      return {
        bodyId: deps.state.bodyId,
        /** The registry's own art colour for this body, linear rgb. */
        albedoColor: row ? deps.worldHost.linearHex(row.albedo_color) : null,
        /** Declared in the row, or the neutral pair DtmPlot falls back to. */
        albedoRamp: ramp ?? null,
        declaresPalette: row?.plot_palette !== undefined,
        /** The Atlas's own light, which is what lights the plot's ground now. */
        atlasLight: deps.globeMaterial.atlasGroundLight(),
        hasPlot: deps.live.plot !== null,
        ...(deps.live.plot ? deps.live.plot.paint() : {}),
      };
    },
    /** `descent-dojo --stand`'s imagery reading draws the plot both ways; false with no ortho bound. */
    setPlotOrtho(on: boolean): boolean {
      return deps.live.plot?.setOrthoShown(on) ?? false;
    },
    /** Why the plot build gave up for this session, or null while it has not. */
    plotError(): string | null {
      return deps.descentLoad.state.plotError;
    },
    /**
     * The ground under the drawn eye, read from BOTH fields at the same lat/lon.
     *
     * The whole-file plot mesh and the tile leaves draw the same ground on
     * either side of the SURFACE edge, so a step there is the two height fields
     * disagreeing over one point rather than the eye having moved between two
     * notches. `surface-clamp --handover` reads the pair from the PLOT side,
     * where both are mounted at once.
     */
    groundUnderEye(): unknown {
      const node = deps.bodyScene.state.bodyNode;
      const body = deps.pyramidView.tileBody;
      if (!node || body === null) return null;
      const meta = deps.live.plotField?.meta ?? null;
      const onPlot = deps.cameraRig.surfaceRegime() && deps.live.plot !== null && deps.live.plotField !== null;
      const probe = new THREE.Vector3();
      let latDeg: number;
      let lonDeg: number;
      if (onPlot && deps.live.plot) {
        deps.live.plot.group.updateMatrixWorld();
        deps.mount.camera.updateMatrixWorld();
        // Group-local is the region's own metres: +x east, +z south.
        const local = deps.live.plot.group.worldToLocal(deps.mount.camera.getWorldPosition(probe));
        const ll = deps.plotGeometry.latLonAt(local.x, local.z);
        latDeg = ll.lat;
        lonDeg = ll.lon;
      } else {
        probe.set(
          deps.mount.cameraWorld.x - node.world.x, deps.mount.cameraWorld.y - node.world.y, deps.mount.cameraWorld.z - node.world.z,
        ).applyQuaternion(deps._q.copy(node.quaternion).invert());
        if (probe.lengthSq() === 0) return null;
        probe.normalize();
        latDeg = THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(probe.y, -1, 1)));
        lonDeg = THREE.MathUtils.radToDeg(Math.atan2(-probe.z, probe.x));
        if (lonDeg < 0) lonDeg += 360;
      }
      const xz = deps.plotGeometry.regionXZAt(latDeg, lonDeg);
      const inRegion = xz !== null && meta !== null
        && Math.abs(xz.x) <= meta.bbox_m.width * 0.5 && Math.abs(xz.z) <= meta.bbox_m.height * 0.5;
      // The ground the rig is pivoting on, in the same region metres: what says
      // whether the eye moved because the wheel turned or because the handover
      // put the pivot somewhere else.
      let pivot: { xM: number; zM: number } | null = null;
      if (onPlot && deps.live.plot) {
        deps.live.plot.group.updateMatrix();
        const p = new THREE.Vector3().copy(deps.cameraRig.orbit.lookAt)
          .applyMatrix4(_probeInv.copy(deps.live.plot.group.matrix).invert());
        pivot = { xM: +p.x.toFixed(1), zM: +p.z.toFixed(1) };
      } else {
        const a = deps.descentAnchorHandle.regionAnchorLatLon();
        const axz = a ? deps.plotGeometry.regionXZAt(a.lat, a.lon) : null;
        if (axz) pivot = { xM: +axz.x.toFixed(1), zM: +axz.z.toFixed(1) };
      }
      return {
        source: onPlot ? 'plot' : 'tiles',
        eyeXM: xz ? +xz.x.toFixed(1) : null,
        eyeZM: xz ? +xz.z.toFixed(1) : null,
        pivot,
        clusterExtentM: deps.live.plot ? +deps.live.plot.clusterExtentM.toFixed(1) : null,
        latDeg: +latDeg.toFixed(6),
        lonDeg: +lonDeg.toFixed(6),
        /** Metres over the PYRAMID's datum, the leaves' own number. */
        tileM: deps.pyramidView.tileHeightAt(latDeg, lonDeg),
        tileDatumRadiusM: deps.surfaceRadius.leafDatumM(),
        /** Metres over the REGION bake's datum, the plot FIELD's own number. */
        plotM: xz && deps.live.plotField ? +deps.live.plotField.heightAt(xz.x, xz.z).toFixed(3) : null,
        /**
         * The same, off the mesh the frame actually DRAWS, group offset and all.
         *
         * Not the field: `liftMeshM` moves the ground without moving the field,
         * and a criterion reading the field would call that clean.
         */
        plotDrawnM: xz && deps.live.plot && meta
          ? +(meta.elevation.min_m + deps.live.plot.meshHeightAt(xz.x, xz.z) / Math.max(1e-6, deps.solarScrub.solar.exag)
            + deps.live.plot.group.position.y).toFixed(3)
          : null,
        plotDatumRadiusM: deps.bodyReaders.registryDatumM(body),
        plotDatum: meta?.elevation.datum ?? null,
        inRegion,
        exag: deps.solarScrub.solar.exag,
      };
    },
    /**
     * The walkaround's drawn mesh against the leaves at the same body-fixed place, across the patch.
     *
     * Each point of the mesh is carried out through the group's own world
     * placement, so where it lands on the body owes nothing to `latLonAt`'s
     * region frame; the leaves are then read at that direction.
     */
    plotAgainstLeaves(halfSpanM = 700, steps = 9): unknown {
      const node = deps.bodyScene.state.bodyNode;
      const plot = deps.live.plot;
      if (!node || !plot || !deps.live.plotField || !deps.cameraRig.surfaceRegime()) return { err: 'no walkaround plot mounted' };
      plot.group.updateWorldMatrix(true, false);
      const invQ = deps._q.copy(node.quaternion).invert();
      const exag = deps.solarScrub.solar.exag;
      const datum = deps.surfaceRadius.leafDatumM();
      const p = new THREE.Vector3();
      const diffs: number[] = [];
      let worst: { xM: number; zM: number; lat: number; lon: number; plotAboveLeavesM: number } | null = null;
      let unanswered = 0;
      for (let j = 0; j < steps; j++) {
        for (let i = 0; i < steps; i++) {
          const x = -halfSpanM + (2 * halfSpanM * i) / (steps - 1);
          const z = -halfSpanM + (2 * halfSpanM * j) / (steps - 1);
          plot.group.localToWorld(p.set(x, plot.meshHeightAt(x, z), z));
          p.set(
            p.x + deps.mount.cameraWorld.x - node.world.x,
            p.y + deps.mount.cameraWorld.y - node.world.y,
            p.z + deps.mount.cameraWorld.z - node.world.z,
          ).applyQuaternion(invQ);
          const r = p.length();
          const lat = THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(p.y / r, -1, 1)));
          let lon = THREE.MathUtils.radToDeg(Math.atan2(-p.z, p.x));
          if (lon < 0) lon += 360;
          const h = deps.pyramidView.tileHeightAt(lat, lon);
          if (h === null) { unanswered++; continue; }
          const d = r - (datum + h * exag);
          diffs.push(d);
          if (!worst || Math.abs(d) > Math.abs(worst.plotAboveLeavesM)) {
            worst = { xM: x, zM: z, lat: +lat.toFixed(5), lon: +lon.toFixed(5), plotAboveLeavesM: +d.toFixed(3) };
          }
        }
      }
      const n = diffs.length;
      const rms = n ? Math.sqrt(diffs.reduce((s, d) => s + d * d, 0) / n) : null;
      return {
        centreLatDeg: deps.live.plotField.meta.center_lat_deg,
        centreLonDeg: deps.live.plotField.meta.center_lon_deg,
        halfSpanM, points: n, unanswered,
        rmsM: rms === null ? null : +rms.toFixed(3),
        maxAbsM: worst ? Math.abs(worst.plotAboveLeavesM) : null,
        worst,
      };
    },
    /** Plot height at each cell centre against the leaves at its `latLonAt`; `pair` reads the equirect pair, which a polar bake must fail. */
    plotFrameAgainstLeaves(): unknown {
      const field = deps.live.plotField;
      if (!field || isPyramidPlot(field.meta)) return { err: 'no region plot mounted' };
      const m = field.meta;
      const datumStep = deps.bodyReaders.registryDatumM(deps.bodyReaders.ladderBody()) - deps.surfaceRadius.leafDatumM();
      const read = (toLatLon: (x: number, z: number) => { lat: number; lon: number }) => {
        const d: number[] = [];
        for (const h of m.hexes ?? []) {
          const ll = toLatLon(h.x_m, h.z_m);
          const leaf = deps.pyramidView.tileHeightAt(ll.lat, ll.lon);
          if (leaf !== null) d.push(Math.abs(field.heightAt(h.x_m, h.z_m) + datumStep - leaf));
        }
        d.sort((a, b) => a - b);
        const q = (p: number) => (d.length ? +(d[Math.min(d.length - 1, Math.floor(p * d.length))] ?? 0).toFixed(2) : null);
        return { points: d.length, medianAbsM: q(0.5), p90AbsM: q(0.9) };
      };
      const f = m.frame;
      return {
        projection: f?.projection ?? null,
        cells: m.hexes?.length ?? 0,
        frame: read(deps.plotGeometry.latLonAt),
        pair: f ? read((x, z) => ({ lat: m.center_lat_deg - z / f.m_per_deg_lat, lon: (((m.center_lon_deg + x / f.m_per_deg_lon) % 360) + 360) % 360 })) : null,
      };
    },
    /**
     * The DRAWN camera against the ground under it, cheap enough for every frame.
     *
     * `surface-clamp --frames`. `plotClearance` reads the FOCUS rig's eye, so a
     * frame where something else owns `camera` reads clean there however wrong
     * the image is; this one reads `camera` itself. `elevationDeg` is the view
     * direction against the eye's own local up - 0 is tangential to the ground,
     * -90 is straight down - which is not the rig's orbit pitch: on a centre
     * anchor the eye sits at that pitch around the body and still looks at nadir.
     */
    eyePose(): unknown {
      const node = deps.bodyScene.state.bodyNode;
      const body = deps.pyramidView.tileBody;
      if (!node || body === null) return null;
      const dir = new THREE.Vector3();
      const up = new THREE.Vector3();
      deps.mount.camera.getWorldDirection(dir);
      // The world frame is camera-relative and in metres, so the drawn eye is
      // the focus eye plus whatever the drawn camera is off the origin by,
      // which is nothing once the plot is registered: `cameraWorld` IS the eye
      // in every band. In the walkaround the mesh answers for the ground;
      // everywhere else the leaves do.
      const eyeW = {
        x: deps.mount.cameraWorld.x + deps.mount.camera.position.x,
        y: deps.mount.cameraWorld.y + deps.mount.camera.position.y,
        z: deps.mount.cameraWorld.z + deps.mount.camera.position.z,
      };
      const radial = new THREE.Vector3(
        eyeW.x - node.world.x, eyeW.y - node.world.y, eyeW.z - node.world.z);
      const toSun = new THREE.Vector3(-node.world.x, -node.world.y, -node.world.z);
      const r = radial.length();
      if (r === 0) return null;
      radial.divideScalar(r);
      let clearanceM: number | null = null;
      let source: string;
      if (deps.cameraRig.surfaceRegime() && deps.live.plot && deps.live.plotField) {
        // `dir` is read off the drawn camera, so the plot's own up goes out to
        // that frame rather than the reverse.
        up.copy(deps.plotMount.plotUpWorld);
        clearanceM = +deps.live.plot.clearanceAboveGround(deps.cameraRig.camPos).toFixed(3);
        source = 'plot';
      } else {
        const groundR = deps.pyramidView.groundRadiusUnder(body, eyeW);
        up.copy(radial);
        clearanceM = groundR === null ? null : +(r - groundR).toFixed(3);
        source = 'tiles';
      }
      return {
        source,
        band: deps.focusRig.band(),
        altitudeM: +deps.focusRig.altitude().toFixed(2),
        clearanceM,
        // The RENDERED eye's own radial distance over the datum - distinct from
        // `clearanceM`, which also moves when the GROUND reading under a still
        // eye changes. The #297 bounce needs the two told apart.
        eyeAltitudeM: +(r - deps.surfaceRadius.leafDatumM()).toFixed(3),
        // How much of the ground under the eye is a correction still closing,
        // so an instrument can tell a glide from a jump without inferring it.
        groundGlideM: +deps.pyramidView.groundGlideM.toFixed(3),
        elevationDeg: +THREE.MathUtils.radToDeg(
          Math.asin(THREE.MathUtils.clamp(dir.dot(up), -1, 1)),
        ).toFixed(3),
        // The same geometry that lights a tile: the body's own position against
        // the drawn eye's radial. What says whether a dark frame is the fault
        // or the night side.
        sunAltitudeDeg: toSun.lengthSq() === 0 ? null : +THREE.MathUtils.radToDeg(
          Math.asin(THREE.MathUtils.clamp(radial.dot(toSun.normalize()), -1, 1)),
        ).toFixed(2),
        // The RIG's own offset: how far the eye stands from the frame its own
        // metres are measured in. Outside the walkaround that frame is the
        // scene and `camPos` is the drawn camera; inside it, it is the plot's
        // origin, which is where the walkaround rig has always measured from -
        // the drawn camera merely used to sit there too. An offset with no mesh
        // under it is the local rig holding the band with nothing to hold,
        // which is what `surface-clamp --frames` gates on.
        offsetUnits: +deps.cameraRig.camPos.length().toFixed(5),
        // Where the DRAWN camera is, against the scene origin the whole Atlas
        // is camera-relative to. Zero in every band, since the plot registers
        // with the world frame the way every other node does.
        drawnOffsetUnits: +deps.mount.camera.position.length().toFixed(5),
        nearUnits: deps.mount.camera.near,
        plotMesh: deps.live.plot !== null,
      };
    },
    /**
     * Is the drawn camera standing on the ground, looking out over it?
     *
     * `surface-clamp --stand`. `eyePose` answers where the eye is and which way
     * it points; this answers the question the manifest's own "ground to
     * SURFACE" label makes - a person's height over the ground with the horizon
     * in frame - which needs the horizon's own place in the frame beside the
     * pose. The horizon is derived from the shell the ground under the eye is
     * on, not from the datum, so a body kilometres below its own datum is not
     * read against a horizon it does not have.
     */
    standingPose(): unknown {
      const node = deps.bodyScene.state.bodyNode;
      const body = deps.pyramidView.tileBody;
      if (!node || body === null) return null;
      const eyeW = {
        x: deps.mount.cameraWorld.x + deps.mount.camera.position.x,
        y: deps.mount.cameraWorld.y + deps.mount.camera.position.y,
        z: deps.mount.cameraWorld.z + deps.mount.camera.position.z,
      };
      const radial = new THREE.Vector3(
        eyeW.x - node.world.x, eyeW.y - node.world.y, eyeW.z - node.world.z);
      const r = radial.length();
      if (r === 0) return null;
      radial.divideScalar(r);
      const onPlot = deps.cameraRig.surfaceRegime() && deps.live.plot !== null && deps.live.plotField !== null;
      // `--stand --inject field-height`'s control: read the pyramid's own
      // stored field under the eye even where the plot rig is up, the same
      // tileHeightAt path an off-plot pose always used.
      const legacyPlotHeight = onPlot && deps.injects.state.legacyFieldHeight;
      const groundR = onPlot && !legacyPlotHeight ? null : deps.pyramidView.groundRadiusUnder(body, eyeW);
      const eyeHeightM = onPlot && deps.live.plot && !legacyPlotHeight
        ? +deps.live.plot.clearanceAboveGround(deps.cameraRig.camPos).toFixed(3)
        : groundR === null ? null : +(r - groundR).toFixed(3);
      const dir = new THREE.Vector3();
      deps.mount.camera.getWorldDirection(dir);
      const up = onPlot ? new THREE.Vector3().copy(deps.plotMount.plotUpWorld) : radial;
      const elevationDeg = +THREE.MathUtils.radToDeg(
        Math.asin(THREE.MathUtils.clamp(dir.dot(up), -1, 1))).toFixed(3);
      // The shell the ground under the eye is on. On the plot the mesh is the
      // ground and the local frame is y-up, so the horizon is level by
      // construction and there is no sphere to dip below.
      const shellR = groundR ?? (onPlot ? r - (eyeHeightM ?? 0) : null);
      const horizonDeg = shellR !== null && shellR > 0 && r > shellR
        ? +(-THREE.MathUtils.radToDeg(Math.acos(Math.min(1, shellR / r)))).toFixed(4)
        : null;
      // Where that horizon lands in the frame: the view's own azimuth carried
      // to the horizon's elevation, a long way out, and projected. Directions
      // are shared between the scene axes and the camera-relative draw frame -
      // the two differ by a translation alone - so no rotation is needed.
      let horizonNdcY: number | null = null;
      if (horizonDeg !== null) {
        const h = new THREE.Vector3().copy(dir).addScaledVector(up, -dir.dot(up));
        if (h.lengthSq() > 1e-12) {
          h.normalize();
          const hr = THREE.MathUtils.degToRad(horizonDeg);
          const ray = new THREE.Vector3().copy(h).multiplyScalar(Math.cos(hr))
            .addScaledVector(up, Math.sin(hr));
          const reach = Math.max(1e3, Math.sqrt(Math.max(1, 2 * (shellR ?? 1) * Math.max(0.1, eyeHeightM ?? 1))));
          const p = new THREE.Vector3().copy(deps.mount.camera.position).addScaledVector(ray, reach);
          horizonNdcY = +p.project(deps.mount.camera).y.toFixed(4);
        }
      }
      // Where the eye is in a frame fixed to the BODY, so a ground track can be
      // differenced across two samples: in scene axes the eye's displacement
      // between two reads is mostly the orrery's own motion, 2,300 km of it per
      // read on Earth, which says nothing about where a drag put the person.
      // Body-fixed metres off the tiles, and the plot's own local metres on the
      // plot mesh, where the camera-relative offset is what moves.
      const anchorLat = deps.focusRig.focusCam.state.latDeg;
      const anchorLon = deps.focusRig.focusCam.state.lonDeg;
      const anchorW = anchorLat === null || anchorLon === null ? null
        : deps.surfaceRadius.surfaceWorld(node, anchorLat, anchorLon,
          (deps.surfaceRadius.datumRadiusM(body) ?? node.row.radius_m) + deps.focusGround.groundAt(body, anchorLat, anchorLon));
      const anchorRangeM = anchorW === null ? null
        : +Math.hypot(eyeW.x - anchorW.x, eyeW.y - anchorW.y, eyeW.z - anchorW.z).toFixed(2);
      deps.descentAnchorHandle._landV.set(eyeW.x - node.world.x, eyeW.y - node.world.y, eyeW.z - node.world.z)
        .applyQuaternion(deps.descentAnchorHandle._landQ.copy(node.quaternion).invert());
      const trackM = onPlot && deps.live.plot
        ? deps.cameraRig.camPos.clone()
        : deps.descentAnchorHandle._landV;
      return {
        band: deps.focusRig.band(),
        // Which of the three the camera is descending onto, which is the fault
        // itself: `centre` is a nadir view whatever the pyramid drew.
        anchored: deps.descentAnchorHandle.displaySite() !== undefined ? 'site'
          : deps.focusRig.focusCam.state.latDeg === null || deps.focusRig.focusCam.state.lonDeg === null ? 'centre' : 'landing',
        source: onPlot ? 'plot' : 'tiles',
        altitudeM: +deps.focusRig.altitude().toFixed(2),
        eyeHeightM,
        elevationDeg,
        horizonDeg,
        horizonNdcY,
        halfFovDeg: +(deps.mount.camera.fov / 2).toFixed(3),
        pitchDeg: +THREE.MathUtils.radToDeg(deps.focusRig.focusCam.state.pitch).toFixed(3),
        standingFloorM: deps.injects.state.legacyStanding ? null : deps.focusRig.STANDING_EYE_M,
        legacyStanding: deps.injects.state.legacyStanding,
        landing: deps.descentAnchorHandle.landing?.body === deps.bodyReaders.ladderBody()
          ? { lat: +deps.descentAnchorHandle.landing.lat.toFixed(4), lon: +deps.descentAnchorHandle.landing.lon.toFixed(4) } : null,
        trackFrame: onPlot ? 'plot' : 'body-fixed',
        trackM: { x: trackM.x, y: trackM.y, z: trackM.z },
        // How far off the landing point the eye ended up. The aim is AT the
        // anchor, so this is both what the person is looking at and the radius
        // a dragged pixel of yaw swings them around.
        anchorRangeM,
      };
    },
    /**
     * The eye against the ground the renderer DREW, by ray and not by the
     * camera's own height model.
     *
     * `standingPose`'s height comes from `groundRadiusUnder`, which samples the
     * same tile field the standing clamp is solved on, so an arm gated on it
     * cannot catch a pose the drawn mesh disagrees with. This casts a ray in
     * from outside the body down the eye's own radial and reports what it hits:
     * the pyramid's visible leaves, the orrery globe under them, and in the
     * walkaround the plot mesh. `radialOffsetM` moves the probe point off the
     * eye, which is how the reader is read against a deliberately sunk pose.
     */
    drawnGroundUnder(radialOffsetM = 0): unknown {
      const node = deps.bodyScene.state.bodyNode;
      if (!node) return { err: 'no focused body' };
      deps.mount.worldFrame.applyCameraRelative(deps.mount.cameraWorld);
      deps.mount.scene.updateMatrixWorld(true);
      // Three's raycaster tests invisible objects too, and a covered leaf is
      // held in the group with `visible` false rather than removed.
      const visibleMeshes = (root: THREE.Object3D): THREE.Mesh[] => {
        const out: THREE.Mesh[] = [];
        if (!root.visible) return out;
        root.traverseVisible((o) => { if ((o as THREE.Mesh).isMesh) out.push(o as THREE.Mesh); });
        return out;
      };
      const rc = new THREE.Raycaster();
      // The walkaround's own mesh is displaced in its VERTEX SHADER, so a ray
      // against its geometry reads the undisplaced lattice - 156 m under the
      // ground at Jezero. There is no reading to give here that is not the
      // plot's own field, so the arm keeps site bodies on `plotClearance` and
      // this says so rather than answering with a number it did not measure.
      if (deps.cameraRig.surfaceRegime() && deps.live.plot) {
        const eyeP = deps.cameraRig.camPos.clone();
        eyeP.y += radialOffsetM;
        return {
          frame: 'plot',
          clusterExtentM: deps.live.plot.clusterExtentM,
          drawnHeightM: null,
          modelHeightM: +deps.live.plot.clearanceAboveGround(eyeP).toFixed(3),
          note: 'the plot mesh is displaced in the vertex shader; no ray reads it',
        };
      }
      const centre = new THREE.Vector3(
        node.world.x - deps.mount.cameraWorld.x, node.world.y - deps.mount.cameraWorld.y, node.world.z - deps.mount.cameraWorld.z);
      const radial = deps.mount.camera.position.clone().sub(centre);
      const r0 = radial.length();
      if (r0 === 0) return { err: 'the eye is at the body centre' };
      radial.divideScalar(r0);
      const eyeR = r0 + radialOffsetM;
      const margin = Math.max(5000, 0.25 * eyeR);
      rc.set(centre.clone().addScaledVector(radial, eyeR + margin), radial.clone().negate());
      rc.far = eyeR + margin;
      const radiusOf = (hits: THREE.Intersection[]): number | null =>
        hits.length === 0 ? null : +hits[0]!.point.distanceTo(centre).toFixed(3);
      const leafHits = rc.intersectObjects(visibleMeshes(deps.tileGroup), false);
      const globe = deps.bodyScene.state.orrery?.byId(deps.bodyReaders.ladderBody())?.mesh ?? null;
      const globeHits = globe && globe.visible ? rc.intersectObject(globe, false) : [];
      const cw = deps.mount.cameraWorld;
      const reliefT = deps.pyramidView.drawnGroundHit(
        { x: rc.ray.origin.x + cw.x, y: rc.ray.origin.y + cw.y, z: rc.ray.origin.z + cw.z }, rc.ray.direction, rc.far);
      const leafR = reliefT === undefined ? radiusOf(leafHits)
        : reliefT === null ? null : +(eyeR + margin - reliefT).toFixed(3);
      const globeR = radiusOf(globeHits);
      // The ground is the LEAF where one is drawn: the globe underlay is pushed
      // back in depth and renders front faces only, so from inside it it is not
      // drawn at all and it is not what anyone stands on. It is read anyway,
      // because "the eye is inside the mesh" does not say which mesh - on three
      // bodies the L0 globe runs 170 to 200 m over the leaves at the clamp.
      const drawnR = leafR ?? globeR;
      const eyeW = {
        x: deps.mount.cameraWorld.x + deps.mount.camera.position.x,
        y: deps.mount.cameraWorld.y + deps.mount.camera.position.y,
        z: deps.mount.cameraWorld.z + deps.mount.camera.position.z,
      };
      const modelR = deps.pyramidView.tileBody === null ? null : deps.pyramidView.groundRadiusUnder(deps.pyramidView.tileBody, eyeW);
      // A leaf mounts on its parent's lattice and morphs onto its own, so a ray
      // against the position attribute is the drawn vertex only at morph 1.
      let hitId: string | null = null;
      const hitObj = leafHits[0]?.object ?? null;
      if (hitObj) for (const [id, h] of deps.pyramidView.tileMeshes) if (h.object === hitObj) { hitId = id; break; }
      const hitMorph = hitId === null ? null : deps.pyramidView.tileDraw.get(hitId)?.morph ?? 1;
      return {
        frame: 'body',
        eyeRadiusM: +eyeR.toFixed(3),
        leafRadiusM: leafR,
        globeRadiusM: globeR,
        drawnRadiusM: drawnR,
        modelRadiusM: modelR === null ? null : +modelR.toFixed(3),
        datumRadiusM: deps.surfaceRadius.leafDatumM(),
        drawnHeightM: drawnR === null ? null : +(eyeR - drawnR).toFixed(3),
        leafHeightM: leafR === null ? null : +(eyeR - leafR).toFixed(3),
        modelHeightM: modelR === null ? null : +(eyeR - modelR).toFixed(3),
        globeOverEyeM: globeR === null ? null : +(globeR - eyeR).toFixed(3),
        leafHits: leafHits.length,
        globeHits: globeHits.length,
        hitId,
        hitMorph,
        exag: deps.solarScrub.solar.exag,
      };
    },
    /**
     * `atmoAltitudeM`'s own ground radius for the body named by id, off
     * `cruiseGroundUnderEyeM` directly rather than through `atmoNode()`: reads
     * the live eye against that body whether or not its atmosphere is mounted,
     * where `atmoNode()` returns null and `atmoAltitudeM` never reaches the
     * reader at all on a body with no air to draw.
     *
     * DEV only. A shipped build has no use for it and this returns null there.
     */
    airGroundUnderEyeM(id: string): unknown {
      if (!import.meta.env.DEV) return null;
      const node = deps.bodyScene.state.orrery?.byId(id) ?? null;
      if (!node) return { err: `no orrery node for ${id}` };
      const dx = deps.mount.cameraWorld.x - node.world.x;
      const dy = deps.mount.cameraWorld.y - node.world.y;
      const dz = deps.mount.cameraWorld.z - node.world.z;
      const r = Math.hypot(dx, dy, dz);
      return { groundM: deps.surfaceRadius.cruiseGroundUnderEyeM(node, dx, dy, dz, r), eyeRadiusM: r };
    },
    /**
     * The SURFACE near plane against the ground the bottom of the frame sees.
     *
     * `surface-clamp --band`'s geometric half. The ONE near plane the whole
     * Atlas shares is metres already, with no conversion left to apply: the
     * reading is in metres both ways, and the ratio is what says whether the
     * ground under the eye is inside the clip. The bottom edge is sampled
     * across its width because the corners are depressed further than its
     * centre and a straight screen edge is what the fault looks like.
     */
    plotNearGeometry(): unknown {
      if (!deps.cameraRig.surfaceRegime() || !deps.live.plot || !deps.live.plotField) return null;
      const eyeM = deps.live.plot.clearanceAboveGround(deps.cameraRig.camPos);
      const rays: Array<{ ndcX: number; distM: number | null }> = [];
      const _v = new THREE.Vector3();
      const _d = new THREE.Vector3();
      for (let i = 0; i <= 8; i++) {
        const ndcX = -1 + (2 * i) / 8;
        _v.set(ndcX, -1, 0.5).unproject(deps.mount.camera);
        _d.copy(_v).sub(deps.mount.camera.position).normalize();
        const t = deps.live.plot.rayGroundDistanceM(deps.mount.camera.position, _d);
        rays.push({ ndcX: +ndcX.toFixed(3), distM: t === null ? null : +t.toFixed(3) });
      }
      const hits = rays.map((r) => r.distM).filter((d): d is number => d !== null);
      const bottomMinM = hits.length ? Math.min(...hits) : null;
      // The geometric horizon of a plane at eye height: the forward azimuth
      // with its vertical component removed, projected back to the frame. The
      // vertical is the plot's own up carried out to the drawn frame, not that
      // frame's +y, which is the ecliptic's.
      deps.mount.camera.getWorldDirection(_d);
      _d.addScaledVector(deps.plotMount.plotUpWorld, -_d.dot(deps.plotMount.plotUpWorld));
      const horizonNdcY = _d.lengthSq() < 1e-12
        ? null
        : +_v.copy(deps.mount.camera.position).addScaledVector(_d.normalize(), 10).project(deps.mount.camera).y.toFixed(4);
      const nearM = deps.mount.camera.near;
      return {
        source: 'plot',
        clusterExtentM: +deps.live.plot.clusterExtentM.toFixed(1),
        nearM: +nearM.toFixed(4),
        farM: +deps.mount.camera.far.toExponential(3),
        eyeM: +eyeM.toFixed(3),
        pivotM: +deps.live.plot.clearanceAboveGround(deps.cameraRig.orbit.lookAt).toFixed(3),
        distanceM: +deps.cameraRig.orbit.distance.toFixed(3),
        pitchRad: +Math.max(deps.cameraRig.plotPitchFloor(), deps.focusRig.focusCam.state.pitch).toFixed(4),
        tiltRad: +deps.cameraRig.plotTilt.toFixed(4),
        fovDeg: deps.mount.camera.fov,
        aspect: +deps.mount.camera.aspect.toFixed(4),
        eyeFloorM: deps.cameraRig.plotEyeFloorM(),
        pitchFloorRad: deps.cameraRig.plotPitchFloor(),
        rays,
        bottomMinM,
        ratio: bottomMinM === null ? null : +(bottomMinM / nearM).toFixed(2),
        horizonNdcY,
        horizonInFrame: horizonNdcY !== null && Math.abs(horizonNdcY) <= 1,
      };
    },
    /**
     * The scatter's own blockers, the rig that steers at them, and where each
     * one lands on the pane.
     *
     * `descent-dojo --boulders` reads its rocks here rather than reproducing
     * the placement: the arm's whole claim is about the field's OWN rocks, and
     * a reimplementation of `createRockField` would be measuring itself. It
     * reports where a rock IS, never how close anything got to one - the arm
     * computes that from positions it sampled itself.
     *
     * DEV only. A shipped build has no use for it and this returns null there.
     */
    rockProbe(maxRocks = 48): unknown {
      if (!import.meta.env.DEV) return null;
      if (!deps.live.plot || !deps.live.plotField || !deps.live.plot.rocks) return null;
      const rocks = deps.live.plot.rocks;
      deps.live.plot.group.updateMatrixWorld();
      // From the plot's OWN frame, which is what the rig is in: the drawn
      // camera is at the scene origin inside the claim.
      const toLocal = _probeInv.copy(deps.live.plot.group.matrix).invert();
      const pivot = _probeA.copy(deps.cameraRig.orbit.lookAt).applyMatrix4(toLocal);
      const eye = _probeB.copy(deps.cameraRig.camPos).applyMatrix4(toLocal);
      // The probe's forward must track applyMovement's own heading convention.
      const fwd = _probeC.set(-Math.sin(deps.focusRig.focusCam.state.yaw), 0, Math.cos(deps.focusRig.focusCam.state.yaw))
        .transformDirection(toLocal);
      const exag = deps.solarScrub.solar.exag;
      const rows: unknown[] = [];
      const half = Math.tan((deps.mount.camera.fov * Math.PI) / 360);
      const probeSun = sunAngles(
        deps.live.plotField.meta.center_lat_deg, deps.solarScrub.sunDeclinationDeg(deps.live.plot.climate),
        (deps.solarScrub.solar.solPhase - 0.5) * 2 * Math.PI);
      // Toward the sun along the ground, in the plot's own azimuth convention:
      // the axis a rock's lit half is measured across on the pane.
      const sunAzR = deps.plotSun.localSunAzimuth(probeSun.az);
      const sunHx = Math.sin(sunAzR);
      const sunHz = -Math.cos(sunAzR);
      // `rockProbe(0)` is the collision legs' per-frame read: the counts and
      // the rig, with the row loop skipped, so sampling every frame costs the
      // projection of nothing.
      for (let i = 0; maxRocks > 0 && i < rocks.blockerCount; i++) {
        const b = rocks.blocker(i);
        if (!b) continue;
        const [bx, bz, r, baseY, height, meshR] = b;
        const midY = baseY * exag + height * 0.5;
        const distM = Math.hypot(bx - eye.x, midY - eye.y, bz - eye.z);
        _probeD.set(bx, midY, bz);
        deps.live.plot.group.localToWorld(_probeD);
        _probeD.project(deps.mount.camera);
        const ndcX = _probeD.x;
        const ndcY = _probeD.y;
        const front = _probeD.z < 1;
        _probeD.set(bx + sunHx * 2 * r, midY, bz + sunHz * 2 * r);
        deps.live.plot.group.localToWorld(_probeD);
        _probeD.project(deps.mount.camera);
        rows.push({
          x: +bx.toFixed(3), z: +bz.toFixed(3), r: +r.toFixed(3),
          // The same rock's footprint measured off its drawn vertices, so a
          // check can fail a stored radius that undershoots the mesh instead
          // of reading the same field the collision code does.
          meshR: +meshR.toFixed(3),
          baseY: +baseY.toFixed(3), height: +height.toFixed(3),
          topM: +(baseY * exag + height).toFixed(3),
          distM: +distM.toFixed(2),
          ndcX: +ndcX.toFixed(5), ndcY: +ndcY.toFixed(5),
          // The rock's own radius as a fraction of the half-frame, which is the
          // NDC the centre above is in. Vertical: NDC y is the axis the fov is.
          ndcR: +(r / Math.max(distM, 1e-6) / half).toFixed(5),
          // And its own HALF-HEIGHT in the same unit. A rock is a squashed,
          // part-buried dome - a metre across and a third of a metre proud - so
          // a circle of its radius is mostly the ground around it.
          ndcRY: +(height * 0.5 / Math.max(distM, 1e-6) / half).toFixed(5),
          // Where a point two radii sunward of the centre lands, so an arm can
          // split the rock into a lit and a shadowed half without knowing how
          // the plot is oriented under the camera.
          sunNdcX: +_probeD.x.toFixed(5), sunNdcY: +_probeD.y.toFixed(5),
          onScreen: Math.abs(ndcX) <= 1 && Math.abs(ndcY) <= 1 && front,
        });
      }
      rows.sort((a, b2) => (a as { distM: number }).distM - (b2 as { distM: number }).distM);
      return {
        source: 'rock-probe',
        floorM: BLOCK_MIN_D,
        blockers: rocks.blockerCount,
        obstacles: rocks.obstacles.length / OBSTACLE_STRIDE,
        drawn: rocks.count,
        exag,
        eyeFloorM: deps.cameraRig.plotEyeFloorM(),
        driving: deps.roverDrive.state.driving,
        pivot: { x: +pivot.x.toFixed(3), y: +pivot.y.toFixed(3), z: +pivot.z.toFixed(3) },
        eye: { x: +eye.x.toFixed(3), y: +eye.y.toFixed(3), z: +eye.z.toFixed(3) },
        forward: { x: +fwd.x.toFixed(5), z: +fwd.z.toFixed(5) },
        yawDeg: +((deps.focusRig.focusCam.state.yaw * 180) / Math.PI).toFixed(3),
        // The plot's own analytic sun, the one its shaders and the scatter
        // share. Azimuth is the plot's convention: 0 toward -z, +90 toward +x.
        sunAltitudeDeg: +((probeSun.alt * 180) / Math.PI).toFixed(3),
        sunAzimuthDeg: +((deps.plotSun.localSunAzimuth(probeSun.az) * 180) / Math.PI).toFixed(3),
        solPhase: +deps.solarScrub.solar.solPhase.toFixed(5),
        rocks: rows.slice(0, maxRocks),
      };
    },
    /**
     * Where the plot's surface mesh DRAWS the ground, at plot-local xz.
     *
     * `rock-ground-check.mjs`'s ruler arm: the app's own `meshHeightAt`, so the
     * instrument validates the sampler the rocks and the wheels are placed by
     * against its GPU probe rather than a reimplementation of it. Null outside
     * SURFACE or before the mesh lands.
     */
    plotMeshHeightAt(x: number, z: number): number | null {
      return deps.live.plot ? deps.live.plot.meshHeightAt(x, z) : null;
    },
    /**
     * Whether the plot mesh agrees with the camera that is actually drawing
     * it, frame by frame.
     *
     * `surface-clamp --ascent`'s criterion: `plotVisible` used to lag
     * `plotCameraActive` by the ~2 s it takes the eased `descent` to clear its
     * own visibility floor on the way back up, drawing the plot against
     * whatever `focusCam.apply()` had already put the eye at.
     */
    plotState(): unknown {
      return {
        plotVisible: deps.plotMount.dtmGroup.visible,
        plotCameraActive: deps.cameraRig.plotCamera(),
        descent: deps.ladder.descent,
        frameTick: deps.injects.state.frameTick,
      };
    },
    /**
     * `surface-clamp --reentry`: the drawn eye, the drawn look-at, the anchor
     * `enterPlotRegime` aims re-entry at, and the rover's own world position -
     * everything the arm needs to read the whip in #280 without a screenshot.
     * All four are in the same camera-relative scene frame `camera.position`
     * and `orbit.lookAt` already share, so distances between them are plain
     * vector differences with no body-fixed conversion.
     */
    reentryFrame(): unknown {
      deps.cameraRig.plotPivot(_reentryAnchor);
      deps.live.plot?.liftAboveGround(_reentryAnchor, 0);
      let roverW: { x: number; y: number; z: number } | null = null;
      if (deps.live.plot?.rover) {
        deps.live.plot.rover.focus(deps.roverDrive.roverFocus);
        deps.live.plot.group.updateMatrix();
        deps.roverDrive.roverFocus.applyMatrix4(deps.live.plot.group.matrix);
        roverW = { x: +deps.roverDrive.roverFocus.x.toFixed(6), y: +deps.roverDrive.roverFocus.y.toFixed(6), z: +deps.roverDrive.roverFocus.z.toFixed(6) };
      }
      return {
        t: performance.now(),
        band: deps.focusRig.band(),
        plotCameraActive: deps.cameraRig.plotCamera(),
        driving: deps.roverDrive.state.driving,
        followRover: deps.roverDrive.state.followRover,
        eye: { x: +deps.cameraRig.camPos.x.toFixed(6), y: +deps.cameraRig.camPos.y.toFixed(6), z: +deps.cameraRig.camPos.z.toFixed(6) },
        lookAt: { x: +deps.cameraRig.orbit.lookAt.x.toFixed(6), y: +deps.cameraRig.orbit.lookAt.y.toFixed(6), z: +deps.cameraRig.orbit.lookAt.z.toFixed(6) },
        anchor: { x: +_reentryAnchor.x.toFixed(6), y: +_reentryAnchor.y.toFixed(6), z: +_reentryAnchor.z.toFixed(6) },
        rover: roverW,
      };
    },
    /**
     * `descent-dojo --mover`: the pivot `applyMovement` drags, the camera's
     * own forward, and its own right - all three in the plot frame
     * `orbit.lookAt` is already in, so a sweep can compare a WASD-driven
     * displacement against the drawn camera without re-deriving either from
     * yaw. `right` is read off `camera.matrixWorld`'s own local +X column
     * rather than from `forward`, so a strafe axis that only agrees with
     * this file's own derivation of forward cannot pass by circularity.
     */
    moverProbe(): unknown {
      const dir = deps.farField.worldDirToPlot(deps.mount.camera.getWorldDirection(new THREE.Vector3()));
      const right = deps.farField.worldDirToPlot(
        new THREE.Vector3().setFromMatrixColumn(deps.mount.camera.matrixWorld, 0));
      return {
        band: deps.focusRig.band(),
        tier: deps.state.tier,
        yawDeg: THREE.MathUtils.radToDeg(deps.focusRig.focusCam.state.yaw),
        // `orbit.lookAt` is in metres, so the delta is already a distance.
        clusterExtentM: deps.live.plot ? deps.live.plot.clusterExtentM : null,
        lookAt: { x: +deps.cameraRig.orbit.lookAt.x.toFixed(6), y: +deps.cameraRig.orbit.lookAt.y.toFixed(6), z: +deps.cameraRig.orbit.lookAt.z.toFixed(6) },
        forward: { x: +dir.x.toFixed(6), y: +dir.y.toFixed(6), z: +dir.z.toFixed(6) },
        right: { x: +right.x.toFixed(6), y: +right.y.toFixed(6), z: +right.z.toFixed(6) },
      };
    },
    /**
     * The fraction of the pane the globe sphere owns, depth test and all.
     *
     * Not whether the sphere is in the scene but whether it WINS pixels: it is
     * repainted a key colour into an offscreen target of the pane's own aspect,
     * the frame is counted, and the material is put back. Tone mapping is off
     * for the probe so the key colour arrives unmoved and the test can be exact.
     */
    globePaneFraction(): number {
      const mesh = deps.bodyReaders.bodyMesh();
      if (!mesh || !mesh.visible) return 0;
      const w = 192;
      const h = Math.max(1, Math.round(w / Math.max(0.1, deps.cameraRig.aspect())));
      const rt = new THREE.WebGLRenderTarget(w, h);
      rt.texture.name = 'globe-pane-probe';
      const was = mesh.material as THREE.Material;
      const key = new THREE.MeshBasicMaterial({ color: 0xff00ff });
      key.depthWrite = was.depthWrite;
      const prevTarget = deps.renderer.getRenderTarget();
      const prevTone = deps.renderer.toneMapping;
      let owned = 0;
      try {
        mesh.material = key;
        deps.renderer.toneMapping = THREE.NoToneMapping;
        deps.renderer.setRenderTarget(rt);
        deps.mount.subtree.activate();
        deps.mount.worldFrame.applyCameraRelative(deps.mount.cameraWorld);
        deps.renderer.render(deps.mount.scene, deps.mount.camera);
        const px = new Uint8Array(w * h * 4);
        deps.renderer.readRenderTargetPixels(rt, 0, 0, w, h, px);
        for (let i = 0; i < px.length; i += 4) {
          if (px[i]! > 250 && px[i + 1]! < 8 && px[i + 2]! > 250) owned++;
        }
      } finally {
        deps.renderer.setRenderTarget(prevTarget);
        deps.renderer.toneMapping = prevTone;
        mesh.material = was;
        key.dispose();
        rt.dispose();
        deps.live.needsRender = true;
      }
      return +(owned / (w * h)).toFixed(4);
    },
    /**
     * Draw the focused body as its datum sphere instead of as its own shape.
     *
     * The negative control for `descent-dojo --shape`: the state before this
     * lane, where Phobos and Vesta were spheres from orbit and took their shape
     * only when the height tiles drew under the camera.
     */
    setShapeDebug(next: { sphereGlobe?: boolean; lateSwap?: boolean; roundStandIn?: boolean }): void {
      const s = deps.injects.state;
      // `--arrival --inject shape-late`: no hold, so the sphere draws until the shape lands.
      if (next.lateSwap !== undefined) s.shapeLateSwap = next.lateSwap;
      if (next.roundStandIn !== undefined) s.shapeRoundStandIn = next.roundStandIn;
      const round = s.shapeLateSwap || s.shapeRoundStandIn
        || (next.sphereGlobe ?? s.shapeSphereDebug);
      deps.bodyScene.state.orrery?.setRoundGlobes(round);
      if (next.sphereGlobe === undefined || next.sphereGlobe === deps.injects.state.shapeSphereDebug) return;
      deps.injects.state.shapeSphereDebug = next.sphereGlobe;
      if (deps.injects.state.shapeSphereDebug) {
        for (const b of deps.bodyScene.state.orrery?.bodies ?? []) deps.bodyScene.state.orrery?.setShapeGeometry(b.row.id, null);
      } else {
        deps.bodyScene.state.shapeGlobes?.request(deps.bodyReaders.ladderBody());
      }
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    get shapeDebug(): { sphereGlobe: boolean; lateSwap: boolean; roundStandIn: boolean } {
      const s = deps.injects.state;
      return { sphereGlobe: s.shapeSphereDebug, lateSwap: s.shapeLateSwap, roundStandIn: s.shapeRoundStandIn };
    },
    /**
     * Drop a body's resident shape field, and choose the tally the globes count
     * a load on.
     *
     * The entry goes and the re-ask follows in the same breath, so the field's
     * six L0 faces reach the wire inside the arm's own read rather than
     * whenever something next happens to focus that body: a descent that never
     * re-asks is a run in which the tally is not exercised at all. Only the
     * `evictBody` this call passes is dropped - a later call moving the tally
     * alone must not drop a second field under the reading. An explicit `null`
     * puts the arming back, so it does not outlive the read that armed it.
     */
    setShapeGlobeDebug(next: { evictBody?: string | null; countAfterLoad?: boolean }): {
      bodyId: string | null; evicted: boolean; countAfterLoad: boolean;
    } {
      if (next.countAfterLoad !== undefined) shapeGlobeDebug.countAfterLoad = next.countAfterLoad;
      deps.bodyScene.state.shapeGlobes?.setCountDebug({ afterLoad: shapeGlobeDebug.countAfterLoad });
      const id = next.evictBody ?? null;
      let evicted = false;
      if (next.evictBody !== undefined) shapeGlobeDebug.evictedBody = next.evictBody;
      if (id !== null) {
        evicted = deps.bodyScene.state.shapeGlobes?.evict(id) ?? false;
        if (evicted) deps.bodyScene.state.shapeGlobes?.request(id);
      }
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
      return { bodyId: id, evicted, countAfterLoad: shapeGlobeDebug.countAfterLoad };
    },
    /** The shape field the globes hold for a body, and the tally they count it on. */
    get shapeGlobe(): unknown {
      const id = shapeGlobeDebug.evictedBody ?? deps.bodyReaders.ladderBody();
      const st = deps.bodyScene.state.shapeGlobes?.state(id) ?? null;
      return {
        bodyId: id,
        evictedBody: shapeGlobeDebug.evictedBody,
        countAfterLoad: shapeGlobeDebug.countAfterLoad,
        heightFetches: deps.bodyScene.state.shapeGlobes?.heightFetches() ?? null,
        // This body's own share of the tally above, which is what a reading of
        // this body's field is stated in.
        heightFetchesBody: deps.bodyScene.state.shapeGlobes?.heightFetchesFor(id) ?? null,
        status: st === null ? null : st.status,
        wireBytes: st === null ? null : st.wireBytes,
      };
    },
    /** `descent-dojo --limb`: what the focused globe's own sphere is cut from. */
    globeSegments(): unknown {
      const id = deps.bodyReaders.ladderBody();
      const b = deps.bodyScene.state.orrery?.byId(id) ?? null;
      if (!b) return { err: 'no focused body' };
      const geo = b.mesh.geometry as THREE.SphereGeometry;
      const p = (geo.parameters ?? null) as { widthSegments?: number; heightSegments?: number } | null;
      return {
        bodyId: id,
        // A body drawn as its own shape carries a cubesphere and no parameters:
        // the limb arm reads round bodies and refuses the rest by name.
        displaced: geo.name.startsWith('shape-globe'),
        widthSegments: p?.widthSegments ?? null,
        heightSegments: p?.heightSegments ?? null,
      };
    },
    /**
     * `descent-dojo --limb --inject coarse-globe`: cut the focused body's
     * sphere at another tessellation, which is what puts the polygon back.
     */
    setGlobeSegments(widthSegments: number, heightSegments: number): unknown {
      const id = deps.bodyReaders.ladderBody();
      if (!deps.bodyScene.state.orrery?.byId(id)) return { err: 'no focused body' };
      deps.bodyScene.state.orrery.setGlobeSegments(id, widthSegments, heightSegments);
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
      return { bodyId: id, widthSegments, heightSegments };
    },
    /**
     * What the focused body's globe is built from, and the pose to project it
     * from.
     *
     * The basis vectors are in the pyramid's own body-fixed frame - z the
     * north pole, x the prime meridian - which is the frame
     * `faceUvForDirection` addresses and the frame the criterion decodes the
     * L0 tiles in. `atlasToBody`'s (x, -z, y) is the whole conversion.
     */
    shapeState(): unknown {
      const id = deps.bodyReaders.ladderBody();
      const b = deps.bodyScene.state.orrery?.byId(id) ?? null;
      const st = deps.bodyScene.state.shapeGlobes?.state(id) ?? null;
      const geo = b?.mesh.geometry ?? null;
      const posAttr = geo?.getAttribute('position') ?? null;
      const q = _shapeQ.identity();
      if (b) q.copy(b.quaternion).invert();
      const toBody = (v: THREE.Vector3): [number, number, number] => atlasToBody(
        v.applyQuaternion(q),
      );
      const m = deps.mount.camera.matrixWorld;
      const eye = b
        ? _shapeEye.copy(deps.mount.camera.position).sub(b.mesh.position)
        : _shapeEye.set(0, 0, 0);
      return {
        bodyId: id,
        status: st?.status ?? 'none',
        sphereGlobe: deps.injects.state.shapeSphereDebug,
        displaced: geo?.name.startsWith('shape-globe') ?? false,
        quads: st?.quads ?? 0,
        wireBytes: st?.wireBytes ?? 0,
        error: st?.error ?? null,
        vertices: posAttr?.count ?? 0,
        triangles: geo?.index ? geo.index.count / 3 : 0,
        fieldDatumRadiusM: st?.datumRadiusM ?? null,
        drawRadiusM: b?.drawRadiusM ?? null,
        rowRadiusM: b?.row.radius_m ?? null,
        meshScale: b?.mesh.scale.x ?? null,
        visible: b?.mesh.visible ?? false,
        trueAngularDiameterDeg: b?.mesh.userData['trueAngularDiameterDeg'] ?? null,
        drawnAngularDiameterDeg: b?.mesh.userData['drawnAngularDiameterDeg'] ?? null,
        drawnLeaves: deps.pyramidView.tileMeshes.size,
        // Each drawn leaf's own radial extent about the body centre, so a limb
        // the globe does not carry can be pinned on the ground that does.
        leaves: [...deps.pyramidView.tileMeshes.entries()].map(([id, handle]) => {
          const o = handle.object;
          const pos = o.geometry.getAttribute('position');
          let lo = Infinity;
          let hi = 0;
          if (pos) {
            for (let i = 0; i < pos.count; i++) {
              const r = _shapeV.fromBufferAttribute(pos, i).add(o.position).length();
              if (r < lo) lo = r;
              if (r > hi) hi = r;
            }
          }
          return {
            id, visible: o.visible, verts: pos?.count ?? 0,
            minRadiusM: +lo.toFixed(1), maxRadiusM: +hi.toFixed(1),
          };
        }),
        // The app's own handoff rule, read rather than reproduced: the arm's
        // stops are relative to it and it is not this lane's to set.
        streamFromM: deps.pyramidView.tileStreamAltM(),
        underlay: deps.pyramidView.globeUnderlay,
        pose: {
          fovDeg: deps.mount.camera.fov,
          aspect: deps.mount.camera.aspect,
          // Camera basis and eye in the pyramid frame, metres.
          right: toBody(_shapeV.setFromMatrixColumn(m, 0)),
          up: toBody(_shapeV.setFromMatrixColumn(m, 1)),
          forward: toBody(_shapeV.setFromMatrixColumn(m, 2).negate()),
          eye: toBody(_shapeV.copy(eye)),
          distanceM: eye.length(),
        },
      };
    },
    /**
     * The focused body's own drawn silhouette, as limb radii in pixels.
     *
     * The mesh alone: it is repainted a key colour on a layer only the probe's
     * camera sees, so the mask is the body and not the haze around it, the
     * leaves over it or the stars behind it. `angles` rays are walked out from
     * the projected centre and the last key pixel on each is the limb. The
     * reader's own known answer is the datum sphere - `--inject sphere-globe`
     * puts it back, and every ray must then read the same radius.
     */
    shapeSilhouette(angles: number, widthPx: number, withLeaves = false): unknown {
      const b = deps.bodyScene.state.orrery?.byId(deps.bodyReaders.ladderBody()) ?? null;
      const mesh = b?.mesh ?? null;
      if (!mesh || !mesh.visible) return { err: 'the focused body is not drawn' };
      // The pop reading is the union a person sees, so the streamed leaves join
      // the mask; every other reading is the globe's own limb.
      const leaves: Array<{ o: THREE.Mesh; mat: THREE.Material | THREE.Material[]; mask: number }> = [];
      const w = Math.max(64, Math.min(4096, Math.round(widthPx)));
      const h = Math.max(1, Math.round(w / Math.max(0.1, deps.cameraRig.aspect())));
      const rt = new THREE.WebGLRenderTarget(w, h);
      rt.texture.name = 'shape-silhouette-probe';
      const was = mesh.material as THREE.Material;
      const key = new THREE.MeshBasicMaterial({ color: 0xff00ff });
      const wasLayers = mesh.layers.mask;
      const wasCamLayers = deps.mount.camera.layers.mask;
      const prevTarget = deps.renderer.getRenderTarget();
      const prevTone = deps.renderer.toneMapping;
      const px = new Uint8Array(w * h * 4);
      try {
        mesh.material = key;
        mesh.layers.set(SILHOUETTE_LAYER);
        if (withLeaves && deps.tileGroup.visible) {
          deps.tileGroup.traverse((o) => {
            if (!(o instanceof THREE.Mesh)) return;
            leaves.push({ o, mat: o.material, mask: o.layers.mask });
            o.material = key;
            o.layers.set(SILHOUETTE_LAYER);
          });
        }
        deps.mount.camera.layers.set(SILHOUETTE_LAYER);
        deps.renderer.toneMapping = THREE.NoToneMapping;
        deps.renderer.setRenderTarget(rt);
        deps.mount.subtree.activate();
        deps.mount.worldFrame.applyCameraRelative(deps.mount.cameraWorld);
        deps.renderer.render(deps.mount.scene, deps.mount.camera);
        // An instrument may have patched `render` and bound framebuffers with
        // raw GL behind three's state cache (descent-dojo's own capture does),
        // and a readback that trusts the cache then reads the canvas instead of
        // this target - a silent misread that looks like an empty body.
        deps.renderer.resetState();
        deps.renderer.readRenderTargetPixels(rt, 0, 0, w, h, px);
      } finally {
        deps.renderer.setRenderTarget(prevTarget);
        deps.renderer.toneMapping = prevTone;
        mesh.material = was;
        mesh.layers.mask = wasLayers;
        for (const l of leaves) { l.o.material = l.mat; l.o.layers.mask = l.mask; }
        deps.mount.camera.layers.mask = wasCamLayers;
        key.dispose();
        rt.dispose();
        deps.live.needsRender = true;
      }
      // readRenderTargetPixels hands back rows bottom-up, so a y here is
      // measured from the bottom and the caller never sees a flipped frame.
      const hit = (x: number, y: number): boolean => {
        if (x < 0 || y < 0 || x >= w || y >= h) return false;
        const i = ((y * w) + x) * 4;
        return px[i]! > 250 && px[i + 1]! < 8 && px[i + 2]! > 250;
      };
      let owned = 0;
      for (let i = 0; i < px.length; i += 4) {
        if (px[i]! > 250 && px[i + 1]! < 8 && px[i + 2]! > 250) owned++;
      }
      // A mask with no key pixel in it is a failed readback, not a body with no
      // silhouette: every ray would report -1 and the caller would read that as
      // a frame it may not judge rather than as the fault it is.
      if (owned === 0) return { err: 'the key mask is empty: the probe read no body pixels' };
      const p = _shapeV.copy(mesh.position).project(deps.mount.camera);
      const cx = (p.x * 0.5 + 0.5) * w;
      const cy = (p.y * 0.5 + 0.5) * h;
      const reach = Math.hypot(w, h);
      const limb: number[] = [];
      // A ray that runs out of frame with the body still under it is measuring
      // the pane, not the limb, so it is reported and the caller drops it.
      const clipped: number[] = [];
      for (let k = 0; k < angles; k++) {
        const a = (k / angles) * Math.PI * 2;
        const dx = Math.cos(a);
        const dy = Math.sin(a);
        let last = -1;
        let edge = -1;
        for (let t = 0.25; t <= reach; t += 0.25) {
          const x = Math.round(cx + dx * t);
          const y = Math.round(cy + dy * t);
          if (x < 0 || y < 0 || x >= w || y >= h) { edge = t; break; }
          if (hit(x, y)) last = t;
        }
        limb.push(last);
        if (last >= 0 && edge > 0 && edge - last < 2) clipped.push(k);
      }
      return {
        ok: true, w, h, cx, cy, angles, limb, clipped,
        leaves: leaves.length,
        paneFraction: +(owned / (w * h)).toFixed(5),
      };
    },
    /**
     * How much of the visible datum disc the pyramid's leaves cover.
     *
     * The globe sphere IS the datum, so the reading is two renders of the real
     * scene with it keyed: with the leaves hidden the keyed pixels are the
     * visible datum disc, and with them drawn the keyed pixels left over are
     * the datum showing THROUGH. A pixel count off the frame the renderer draws
     * rather than a walk of the traversal's own draw set, so it cannot agree
     * with the selection rule by construction - which is the whole point of
     * pointing it at a rule that selects too few tiles.
     */
    tileCoverage(): unknown {
      const mesh = deps.bodyReaders.bodyMesh();
      const open = deps.pyramidView.tileSource !== null;
      if (!mesh || !mesh.visible) {
        return { disc: 0, discPx: 0, throughPx: 0, coverage: null, open, drawn: deps.pyramidView.tileMeshes.size };
      }
      const w = 192;
      const h = Math.max(1, Math.round(w / Math.max(0.1, deps.cameraRig.aspect())));
      // Held across calls and freed with the Atlas: the arm reads this every
      // frame of a four-hundred-frame stop, and a target and a buffer per call
      // is that much GL churn inside the thing being measured.
      const probe = deps.pyramidView.coverProbe(w, h);
      const { rt, px } = probe;
      const was = mesh.material as THREE.Material;
      const key = probe.key;
      key.depthWrite = was.depthWrite;
      const prevTarget = deps.renderer.getRenderTarget();
      const prevTone = deps.renderer.toneMapping;
      const wasTiles = deps.tileGroup.visible;
      const keyed = (into: Uint8Array): number => {
        deps.renderer.setRenderTarget(rt);
        deps.mount.subtree.activate();
        deps.mount.worldFrame.applyCameraRelative(deps.mount.cameraWorld);
        deps.renderer.render(deps.mount.scene, deps.mount.camera);
        deps.renderer.readRenderTargetPixels(rt, 0, 0, w, h, px);
        let n = 0;
        for (let i = 0, j = 0; i < px.length; i += 4, j++) {
          const on = px[i]! > 250 && px[i + 1]! < 8 && px[i + 2]! > 250 ? 1 : 0;
          into[j] = on;
          n += on;
        }
        return n;
      };
      let discPx = 0;
      let throughPx = 0;
      try {
        mesh.material = key;
        deps.renderer.toneMapping = THREE.NoToneMapping;
        deps.tileGroup.visible = false;
        discPx = keyed(probe.disc);
        deps.tileGroup.visible = wasTiles;
        throughPx = keyed(probe.through);
      } finally {
        deps.tileGroup.visible = wasTiles;
        deps.renderer.setRenderTarget(prevTarget);
        deps.renderer.toneMapping = prevTone;
        mesh.material = was;
        deps.live.needsRender = true;
      }
      // Which of the datum showing through is a HOLE and which is the limb.
      // Everything not covered by a leaf is flooded from the frame's border:
      // what the flood reaches is the datum standing proud of a body whose real
      // surface is inside it, which is the globe sphere's radius and not the
      // traversal's business; what it cannot reach is datum with leaves all
      // round it, which is a tile the traversal is not asking for.
      const holePx = deps.pyramidView.enclosedKeyed(probe, w, h);
      return {
        open,
        drawn: deps.pyramidView.tileMeshes.size,
        panePx: w * h,
        discPx,
        throughPx,
        holePx,
        /** The disc as a fraction of the pane, so a stop with no body on screen reads as one. */
        disc: +(discPx / (w * h)).toFixed(5),
        coverage: discPx === 0 ? null : +(1 - throughPx / discPx).toFixed(5),
        /** The same, counting only the datum leaves enclose. */
        enclosed: discPx === 0 ? null : +(holePx / discPx).toFixed(5),
      };
    },

    /**
     * Of the ground the eye can see, how much is the pyramid actually drawing?
     *
     * The pixel reading above cannot answer this on a body that is not round:
     * a centre-anchored camera looks at the body's middle, so the limb - which
     * is the only place a visibility rule can cull anything the eye could have
     * seen - leaves the frame below LOCAL. This one is geometric instead. The
     * cap is the DATUM horizon from the eye's OWN radius, `acos(datum / |eye|)`
     * and no relief term, so it owes nothing to the rule under test and is a
     * floor on what has to be drawn rather than the rule's own opinion of it.
     * The instrument gives the sample count, the app answers with its own
     * addressing, and a direction counts as drawn when the leaf holding it or
     * any ancestor of that leaf carries a visible mesh.
     */
    tileCapCoverage(samples = 1024): unknown {
      const node = deps.bodyScene.state.bodyNode;
      const manifest = deps.pyramidView.tileSource?.manifest() ?? null;
      const body = deps.pyramidView.tileBody;
      if (!node || !manifest || body === null) {
        return { open: false, samples: 0, covered: 0, fraction: null, capDeg: null };
      }
      const datumM = manifest.datum_radius_m;
      const view = deps.pyramidView.tileView(node);
      const eyeRadiusM = Math.hypot(view.cameraM[0], view.cameraM[1], view.cameraM[2]);
      const capRad = Math.acos(Math.min(1, Math.max(-1, datumM / Math.max(datumM, eyeRadiusM))));
      const meshed = new Set<string>();
      for (const [id, handle] of deps.pyramidView.tileMeshes) if (handle.object.visible) meshed.add(id);
      // A basis about the sub-camera direction, so the plan is the cap and not
      // a patch of it.
      const w = new THREE.Vector3(view.cameraM[0], view.cameraM[1], view.cameraM[2]).normalize();
      const u = new THREE.Vector3(Math.abs(w.x) > 0.9 ? 0 : 1, Math.abs(w.x) > 0.9 ? 1 : 0, 0)
        .cross(w).normalize();
      const v = new THREE.Vector3().copy(w).cross(u).normalize();
      const cosCap = Math.cos(capRad);
      const golden = Math.PI * (3 - Math.sqrt(5));
      const dir = new THREE.Vector3();
      let covered = 0;
      let n = 0;
      const missed: string[] = [];
      for (let i = 0; i < samples; i++) {
        const ct = 1 - ((i + 0.5) / samples) * (1 - cosCap);
        const st = Math.sqrt(Math.max(0, 1 - ct * ct));
        const phi = golden * i;
        dir.copy(w).multiplyScalar(ct)
          .addScaledVector(u, st * Math.cos(phi))
          .addScaledVector(v, st * Math.sin(phi));
        n++;
        let hit = false;
        for (let level = deps.pyramidView.tileMaxLevel; level >= 0 && !hit; level--) {
          if (meshed.has(formatTileKey(tileForDirection(body, [dir.x, dir.y, dir.z], level)))) {
            hit = true;
          }
        }
        if (hit) covered++;
        else if (missed.length < 8) {
          missed.push(formatTileKey(tileForDirection(body, [dir.x, dir.y, dir.z], deps.pyramidView.tileMaxLevel)));
        }
      }
      return {
        open: true,
        samples: n,
        covered,
        fraction: n === 0 ? null : +(covered / n).toFixed(5),
        capDeg: +((capRad * 180) / Math.PI).toFixed(2),
        eyeRadiusM: Math.round(eyeRadiusM),
        datumM,
        altitudeM: Math.round(view.altitudeM),
        meshed: meshed.size,
        missed,
      };
    },

    /** Whether the globe sphere is drawn at all, and on what radius. */
    globeState(): unknown {
      const mesh = deps.bodyReaders.bodyMesh();
      const b = deps.bodyScene.state.orrery?.byId(deps.bodyReaders.ladderBody()) ?? null;
      return {
        inScene: mesh !== null,
        visible: mesh?.visible ?? false,
        underlay: deps.pyramidView.globeUnderlay,
        drawRadiusM: b?.drawRadiusM ?? null,
        rowRadiusM: b?.row.radius_m ?? null,
        /** The radius handed to the globe's aerial march, the shell's own
         *  ground under it as a floor, and where the march therefore stops:
         *  the first at 0 is the pre-fix state and the control's answer. */
        atmoGroundRadiusM: deps.atmosphereView.atmoTerrainUniforms['uAtmoGlobeRadius']!.value as number,
        shellGroundRadiusM: deps.atmosphereView.atmo ? deps.atmosphereView.atmo.params().rGroundM : null,
        marchGroundRadiusM: Math.max(
          deps.atmosphereView.atmoTerrainUniforms['uAtmoGlobeRadius']!.value as number,
          deps.atmosphereView.atmo ? deps.atmosphereView.atmo.params().rGroundM : 0),
        shellGround: deps.atmosphereView.aerialDebug.shellGround,
        drawnLeaves: deps.pyramidView.tileMeshes.size,
        /** What `--airorder` reads the shell's own place in the order off. */
        airBodyId: deps.atmosphereView.atmoBodyId,
        shellTopRadiusM: deps.atmosphereView.atmo ? deps.atmosphereView.atmo.params().rTopM : null,
        skyVisible: deps.atmosphereView.atmoSky?.mesh.visible ?? false,
        skyRenderOrder: deps.atmosphereView.atmoSky?.mesh.renderOrder ?? null,
        skyDepthTest: deps.atmosphereView.atmoSky
          ? (deps.atmosphereView.atmoSky.mesh.material as THREE.Material).depthTest : null,
        legacySkyOrder: deps.atmosphereView.aerialDebug.legacySkyOrder,
        behindAirOrder: deps.BEHIND_AIR_ORDER,
      };
    },
    /**
     * Put the pre-fix geometry back: the anchor and the bands on the registry's
     * mean radius, and the pitch floor on the body's radius with no clearance.
     *
     * The negative control for `surface-clamp`'s every-notch arm and for
     * `farside-check` - the state that read -58 m of eye clearance at 1,600 m
     * and -2,566 m of eye radius on a far-side descent.
     */
    setLegacyGeometry(on: boolean): void {
      deps.injects.state.legacyGeometry = on;
      deps.focusRig.focusCam.setLegacyPitchFloor(on);
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    /**
     * Put the pre-fix ascent back: the plot mesh gates on the eased `descent`
     * again instead of on `surfaceRegime()`.
     *
     * The negative control for `surface-clamp --ascent`.
     */
    setLegacyPlotFade(on: boolean): void {
      deps.injects.state.legacyPlotFade = on;
      deps.live.needsRender = true;
    },
    /**
     * Arm a one-shot clear-only frame at the next SURFACE->PLOT ascent
     * crossing: nothing drawn, whatever `renderer.clear()` leaves in the
     * buffer.
     *
     * The positive control for `surface-clamp --ascent`'s ground reading -
     * a reader that cannot fail on a frame with nothing drawn on it is blind.
     */
    setGreyFrameInject(on: boolean): void {
      deps.injects.state.greyFrameInject = on;
      deps.injects.state.greyFramePending = false;
    },
    /**
     * Put the pre-fix card back: the wheel handler stops calling
     * `renderOverlay`, so the card holds the tier's first reading.
     *
     * The negative control for `surface-clamp --card`.
     */
    setLegacyCard(on: boolean): void {
      deps.injects.state.legacyCard = on;
    },
    /**
     * Put the near plane back outside the eye: TWICE its own clearance over the
     * mesh, so no eye height can outgrow it.
     *
     * The negative control for `surface-clamp --band`, and it is the fault's
     * own geometry rather than a number of its own: `SCENE_NEAR` in plot units
     * put the clip 4.95 m out at Jezero with the bottom of the frame looking at
     * ground 2.79 m away.
     */
    setNearClipInject(on: boolean): void {
      deps.cameraRig.nearClipInject = on;
      deps.live.needsRender = true;
    },
    /**
     * Put the pre-fix `stopCinema()` back: the eye distance multiplied by the
     * extent instead of the clamp bounds it is checked against.
     *
     * The negative control for `descent-dojo --mover`'s `cinema-release` leg.
     */
    setCinemaReleaseInject(on: boolean): void {
      deps.cameraRig.cinemaReleaseInject = on;
    },
    /**
     * The overlay stage the plot is on and that stage's unfaded weights, off
     * the button's own table, beside whether the fade's control is armed.
     *
     * `descent-dojo --mover`'s `wash-fade` leg divides the drawn `uGridMix` by
     * these: what the frame drew is the material's, not this.
     */
    get washStage(): { stage: number; fill: number; cells: number; parcels: number; fadeInject: boolean } | null {
      const g = deps.GRID_STAGES[deps.solarScrub.solar.overlay];
      return g
        ? { stage: deps.solarScrub.solar.overlay, fill: g.fill, cells: g.cells, parcels: g.parcels, fadeInject: deps.injects.state.washFadeInject }
        : null;
    },
    /**
     * Start the verdict wash's fade at 0.12 of the cluster extent, not 0.10.
     *
     * The negative control for `descent-dojo --mover`'s `wash-fade` leg.
     */
    setWashFadeInject(on: boolean): void {
      deps.injects.state.washFadeInject = on;
      deps.live.needsRender = true;
    },
    /**
     * Put the pre-fix oblique back: the pitch ceiling closing onto 0.44 rad at
     * the clamp instead of onto the frame's own oblique ten metres up.
     *
     * The negative control for `surface-clamp --band` on a focused opening,
     * where the descent arrives carrying tier 2's own 0.75 rad and the ceiling
     * is all that unwinds it.
     */
    setLegacyFocusedAim(on: boolean): void {
      deps.focusRig.focusCam.setLegacyEyeOblique(on);
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    /**
     * Put the flat floor back: a leaf under the height floor takes no relief
     * from its ancestor and every vertex of it sits on the datum sphere.
     *
     * The negative control for `surface-clamp --relief`.
     */
    setFlatHeightFloor(on: boolean): void {
      if (deps.pyramidView.tileDebug.heightFallback === !on) return;
      deps.pyramidView.tileDebug.heightFallback = !on;
      deps.pyramidView.remeshTiles();
      deps.live.needsRender = true;
    },
    /**
     * Hand the SURFACE band to the walkaround's rig again whether or not there
     * is a mesh under it.
     *
     * The negative control for `surface-clamp --frames`, and the fault's own
     * state rather than a number of its own: on a body with no site the rig it
     * hands to has no ground, no extent and no local up.
     */
    /**
     * `surface-clamp --handover`'s two controls.
     *
     * `marsTint` repaints this body's plot with Mars's own row, which is the
     * state every body was in before the palette existed; `plotStepM` lifts the
     * drawn mesh off the ground the pyramid has under it.
     */
    setHandoverDebug(next: { marsTint?: boolean; plotStepM?: number }): void {
      if (next.marsTint !== undefined) {
        deps.cameraRig.handoverMarsTint = next.marsTint;
        const mars = deps.bodyScene.state.bodyRows.find((r) => r.id === 'mars');
        if (deps.live.plot) deps.live.plot.setPalette(deps.globeMaterial.plotPaletteFor(next.marsTint ? mars : deps.bodyReaders.rowById(deps.state.bodyId)));
      }
      if (next.plotStepM !== undefined) {
        deps.cameraRig.handoverStepM = next.plotStepM;
        deps.live.plot?.liftMeshM(next.plotStepM);
        if (deps.cameraRig.surfaceRegime()) deps.cameraRig.enterPlotRegime();
      }
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    get handoverDebug(): unknown {
      return {
        marsTint: deps.cameraRig.handoverMarsTint,
        plotStepM: deps.cameraRig.handoverStepM,
        meshLiftM: deps.live.plot?.meshLift ?? null,
        marsDeclaresPalette: deps.bodyScene.state.bodyRows.find((r) => r.id === 'mars')?.plot_palette !== undefined,
      };
    },
    /**
     * `--ship --inject plot-mainthread`: the pre-round-10 plot build.
     *
     * The lattice on this thread, the survey unsliced, the link and every
     * upload on the frame the mesh mounts - the shape the SURFACE edge had
     * before the build was staged, with the tile worker still alive. Set before
     * the ladder reaches PLOT; a mesh already up is not rebuilt.
     */
    setPlotBuildDebug(next: {
      mainThread?: boolean; edgeOnly?: boolean; buildBudgetOff?: boolean;
      surveyRecompute?: boolean; claimPriorityOff?: boolean; claimTierLate?: boolean;
    }): void {
      if (next.mainThread !== undefined) deps.plotBuild.state.plotBuildMainThread = next.mainThread;
      if (next.buildBudgetOff !== undefined) deps.plotBuild.state.buildBudgetOff = next.buildBudgetOff;
      if (next.edgeOnly !== undefined) deps.injects.state.plotEdgeOnly = next.edgeOnly;
      if (next.surveyRecompute !== undefined) deps.plotBuild.state.surveyRecompute = next.surveyRecompute;
      if (next.claimPriorityOff !== undefined) deps.descentLoad.state.claimPriorityOff = next.claimPriorityOff;
      if (next.claimTierLate !== undefined) deps.descentLoad.state.claimTierLate = next.claimTierLate;
    },
    get plotBuildDebug(): unknown {
      return {
        mainThread: deps.plotBuild.state.plotBuildMainThread,
        meshUp: deps.live.plot !== null,
        earlyReveal: deps.plotBuild.plotEarlyReveal,
        revealedWithRocks: deps.plotBuild.state.plotRevealedWithRocks,
        edgeOnly: deps.injects.state.plotEdgeOnly,
        surveyRecompute: deps.plotBuild.state.surveyRecompute,
        claimPriorityOff: deps.descentLoad.state.claimPriorityOff,
        claimTierLate: deps.descentLoad.state.claimTierLate,
        releaseClearOff: deps.descentLoad.noReleaseClear,
        wantRestoreOff: deps.descentLoad.noWantRestore,
        // A plot built from the leaves asks for nothing, so it is not a claim
        // ask however `claimUrl` reads while it holds the loaded field.
        claimAsked: deps.descentLoad.state.pyramidPlotBody === null && !deps.descentLoad.state.pyramidPlotBuilding
          && (deps.descentLoad.state.claimUrl !== null || deps.descentLoad.state.plotLoading),
        claimAskedAtTier: deps.descentLoad.state.claimAskedAtTier,
        claimStartAltM: deps.descentLoad.state.claimStartAltM === null ? null : Math.round(deps.descentLoad.state.claimStartAltM),
        measureMs: +deps.plotBuild.state.plotBuildSpans.measureMs.toFixed(1),
        rocksMs: +deps.plotBuild.state.plotBuildSpans.rocksMs.toFixed(1),
        surveyWorkMs: +deps.plotBuild.state.plotBuildSpans.surveyWorkMs.toFixed(1),
        scoreWorkMs: +deps.plotBuild.state.plotBuildSpans.scoreWorkMs.toFixed(1),
        surveyedHere: deps.plotBuild.state.plotBuildSpans.surveyedHere,
        handedSurvey: deps.plotBuild.state.plotBuildSpans.handedSurvey,
        // The build's own steps: the longest stretch it held the thread for,
        // the step it was in, the frames the steps spanned and each step's
        // arithmetic. The stretch and not the total, which is meant to spread.
        orthoMean: deps.plotBuild.state.plotBuildSpans.orthoMean === null
          ? null : +deps.plotBuild.state.plotBuildSpans.orthoMean.toFixed(9),
        stepWorstMs: deps.plotBuild.state.plotBuildSpans.stepWorstMs,
        stepWorstName: deps.plotBuild.state.plotBuildSpans.stepWorstName,
        stepFrames: deps.plotBuild.state.plotBuildSpans.stepFrames,
        steps: deps.plotBuild.state.plotBuildSpans.steps,
        buildBudgetOff: deps.plotBuild.state.buildBudgetOff,
        holdsOut: claimHoldsOut(),
        ringHoldOff: deps.descentLoad.ringHoldOff,
        holdMaxLevel: deps.descentLoad.claimHoldLevel() ?? null,
        heldPasses: deps.pyramidView.tileTree?.stats().heldPasses ?? 0,
        // In flight right now, so an instrument can name the phase a hitch
        // landed in; `claimAsked` above is a latch and answers a different one.
        claimLoading: deps.descentLoad.state.plotLoading,
        // Which region is loaded, which the page wants and which is waiting on
        // the pipe. A site switch while a claim is in flight is only readable
        // against all three, and `slug` is the field the ground is built from
        // rather than the url that was asked for.
        claim: {
          url: deps.descentLoad.state.claimUrl,
          want: deps.descentLoad.state.claimWantUrl,
          queued: deps.descentLoad.state.claimQueuedUrl,
          slug: deps.live.plotField?.meta.slug ?? null,
          retargetOff: deps.descentLoad.noClaimRetarget,
        },
        // The plot a siteless body stands on: which body's leaves it was built
        // from, the coarsest level that answered, the samples the ring had no
        // leaf for, and whether the control is holding it off.
        pyramidPlot: {
          body: deps.descentLoad.state.pyramidPlotBody,
          level: deps.descentLoad.state.pyramidPlotLevel,
          holes: deps.descentLoad.state.pyramidPlotHoles,
          building: deps.descentLoad.state.pyramidPlotBuilding,
          off: deps.descentLoad.pyramidPlotOff,
          sourceMPerPx: deps.live.plotField && isPyramidPlot(deps.live.plotField.meta)
            ? +deps.live.plotField.meta.source_resolution_m_per_px.toFixed(2) : null,
          // What the field carries over the patch, and how wide the patch is:
          // the leaves' own relief, against which a field flattened to the
          // datum reads the build's own millimetre floor.
          reliefM: deps.live.plotField && isPyramidPlot(deps.live.plotField.meta)
            ? +deps.live.plotField.meta.elevation.relief_m.toFixed(3) : null,
          acrossM: deps.live.plotField && isPyramidPlot(deps.live.plotField.meta)
            ? deps.live.plotField.meta.hex.across_corners_m : null,
          flat: deps.descentLoad.pyramidPlotFlat,
        },
        meshLoading: deps.live.plotMeshLoading,
        tilesInFlight: deps.pyramidView.tileTree?.stats().inFlight ?? 0,
        prefetchOff: deps.descentLoad.prefetchOff,
        // The focus warm: what it aimed at, how much lead it had on the press,
        // what the press found, and what a body nobody lands on costs.
        focusWarm: {
          off: deps.descentLoad.focusWarmOff,
          wrongSite: deps.descentLoad.warmWrongSite,
          wantClearOff: deps.descentLoad.noWantClear,
          body: deps.descentLoad.state.warmBody,
          site: deps.descentLoad.state.warmSiteId,
          askedAltM: deps.descentLoad.state.warmAskedAltM === null ? null : Math.round(deps.descentLoad.state.warmAskedAltM),
          leadMs: deps.descentLoad.state.warmAskedMs === null || deps.descentLoad.state.landPressMs === null
            ? null : Math.round(deps.descentLoad.state.landPressMs - deps.descentLoad.state.warmAskedMs),
          claimReadyBeforePressMs: deps.descentLoad.state.warmClaimReadyMs === null || deps.descentLoad.state.landPressMs === null
            ? null : Math.round(deps.descentLoad.state.landPressMs - deps.descentLoad.state.warmClaimReadyMs),
          landFound: deps.descentLoad.state.landAskFound,
          requests: deps.descentLoad.warmSpend.requests,
          bytes: deps.descentLoad.warmSpend.bytes,
          tiles: deps.descentLoad.warmSpend.tiles,
        },
        // The ring around the committed site: what the press did with the one
        // the focus had started, and what it has read since.
        ringAhead: {
          off: deps.descentLoad.ringAheadOff,
          fetchAtCrossing: deps.descentLoad.fetchAtCrossing,
          pressRingOff: deps.descentLoad.pressRingOff,
          pyramidRingOff: deps.descentLoad.pyramidRingOff,
          // The ring's centre tiles under the ground the descent comes down on:
          // the same anchor the descent ring itself centres on.
          under: (() => {
            const body = deps.bodyReaders.ladderBody();
            const at = deps.descentAnchorHandle.descentAnchor();
            return at === null ? null : deps.descentLoad.ringTilesUnder(body, at.lat, at.lon);
          })(),
          atPress: deps.descentLoad.state.ringAtPress,
          reading: deps.descentLoad.state.ringSite,
          committed: deps.descentLoad.state.ringCommitted,
          done: [...deps.descentLoad.ringDone],
          requests: deps.descentLoad.ringSpend.requests,
          bytes: deps.descentLoad.ringSpend.bytes,
          tiles: deps.descentLoad.ringSpend.tiles,
          atSurface: deps.descentLoad.state.ringAtSurface === null ? null
            : { warmTiles: deps.descentLoad.state.ringAtSurface.warmTiles, tiles: deps.descentLoad.state.ringAtSurface.tiles },
        },
        // The one order the descent's reads go out in. `holdsOut` above is the
        // claim rung's own count, kept where an instrument already reads it.
        queue: deps.descentLoad.descentQueue.stats(),
      };
    },
    /**
     * `--ship`: the handed-over survey against one taken fresh off the field.
     *
     * The known answer behind the hand-over - the plot has to be the plot a
     * recompute would have built. Every cell's elevation is the mean of 361
     * `heightAt` samples, so a zero here is the ground itself agreeing and not
     * just the cell list. It costs a whole survey, so only an instrument asks.
     *
     * The scatter is not in it: `buildRocksStaged` takes the field, the rock
     * law and the ground the descent is aimed at, and never reads a sub-plot,
     * so its count is carried here as a sighting and not as a comparison - it
     * moves with where the camera settled.
     */
    plotSurveyAudit(): unknown {
      if (!deps.live.plot || !deps.live.plotField) return null;
      return { ...deps.live.plot.surveyAudit(deps.descentLoad.surveyRings(deps.live.plotField)), rocks: deps.live.plot.rocks?.count ?? 0 };
    },
    /**
     * `--ship --inject link-in-leg`: the mount's pre-compile switched off.
     *
     * Read back with the program count so a control that armed too late - the
     * warm already run - cannot be mistaken for one that engaged.
     */
    setLinkDebug(next: { inLeg?: boolean; rockInLeg?: boolean }): void {
      if (next.inLeg !== undefined) deps.shaderWarm.linkInLeg = next.inLeg;
      if (next.rockInLeg !== undefined) deps.shaderWarm.rockLinkInLeg = next.rockInLeg;
    },
    /**
     * `--ship --inject tile-reveal`: the tile arrival back on the reveal frame.
     *
     * Off, a mesh built behind a held split goes up with no texture of its own
     * uploaded, and the frame the hold releases pays for the whole replacement
     * set. Read back with the count so a control armed after the split it was
     * about cannot be mistaken for one that engaged.
     */
    setTileWarmDebug(next: { off?: boolean }): void {
      if (next.off !== undefined) deps.pyramidView.tileWarmOff = next.off;
    },
    get tileWarmDebug(): unknown {
      return {
        off: deps.pyramidView.tileWarmOff,
        warmed: deps.pyramidView.tileWarmedTotal,
      };
    },
    /**
     * `--ship --inject builds-unbudgeted`: the per-frame mesh-build budget
     * withdrawn, so every pending mesh is built and warmed on the frame its
     * own decode landed in. `--inject slice-off` withdraws the ms slice alone
     * and leaves the count.
     */
    setTileBuildDebug(next: { unbudgeted?: boolean; sliceOff?: boolean }): void {
      if (next.unbudgeted !== undefined) deps.pyramidView.tileBuildUnbudgeted = next.unbudgeted;
      if (next.sliceOff !== undefined) deps.pyramidView.tileBuildSliceOff = next.sliceOff;
    },
    get tileBuildDebug(): unknown {
      return {
        unbudgeted: deps.pyramidView.tileBuildUnbudgeted,
        sliceOff: deps.pyramidView.tileBuildSliceOff,
        sliceMs: deps.pyramidView.TILE_BUILD_SLICE_MS,
        mountsPerFrame: deps.pyramidView.TILE_MOUNTS_PER_FRAME,
        // The longest stretch and not the total, which is meant to spread, and
        // the meshes that one frame built beside it.
        worstFrameMs: +deps.pyramidView.tileBuildFrameWorstMs.toFixed(2),
        worstFrameBuilt: deps.pyramidView.tileBuildFrameWorstBuilt,
        costMs: +deps.pyramidView.tileBuildCostMs.toFixed(2),
        builds: deps.pyramidView.tileBuildTotal,
        frames: deps.pyramidView.tileBuildFrames,
        deferred: deps.pyramidView.tileBuildDeferred,
      };
    },
    get linkDebug(): unknown {
      return {
        inLeg: deps.shaderWarm.linkInLeg,
        rockInLeg: deps.shaderWarm.rockLinkInLeg,
        rocksWarmed: deps.shaderWarm.warmRocks !== null,
        warmed: deps.shaderWarm.warmBodyId,
        settled: deps.shaderWarm.warmSettled(),
        census: deps.shaderWarm.warmCensus,
        now: deps.shaderWarm.lightCensus(),
        runs: deps.shaderWarm.warmRuns,
        maxMs: +deps.shaderWarm.warmMaxMs.toFixed(1),
        lastMs: +deps.shaderWarm.warmLastMs.toFixed(1),
        programs: deps.renderer.info.programs?.length ?? -1,
      };
    },
    /**
     * `surface-clamp --edge`'s two controls.
     *
     * `legacyHandover` retires the pyramid and the globe at the SURFACE edge
     * again, which leaves the plot's own surrounding plain as the far field -
     * the fault, and the state rather than a number. `marsCurve` levels the
     * plot's inherited colour against Mars's own albedo ramp, which is the
     * curve that stood in for the tone transfer the plot's materials did not
     * carry; on a body that declares no ramp of its own there is nothing to
     * force back and the arm refuses instead.
     *
     * `rockFloorOff` draws every band of the boulder scatter whatever a rock
     * covers on screen, which is what put a field of sub-pixel lit points over
     * the ground at the top of the SURFACE band.
     */
    setEdgeDebug(next: {
      legacyHandover?: boolean; marsCurve?: boolean; rockFloorOff?: boolean;
    }): void {
      if (next.rockFloorOff !== undefined) deps.farField.edgeRockFloorOff = next.rockFloorOff;
      if (next.legacyHandover !== undefined) deps.farField.legacyHandover = next.legacyHandover;
      if (next.marsCurve !== undefined) {
        deps.farField.edgeMarsCurve = next.marsCurve;
        deps.live.plot?.setTintCurve(next.marsCurve
          ? deps.globeMaterial.albedoRampFor(deps.bodyScene.state.bodyRows.find((r) => r.id === 'mars')) ?? null : null);
      }
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    get edgeDebug(): unknown {
      return {
        legacyHandover: deps.farField.legacyHandover,
        marsCurve: deps.farField.edgeMarsCurve,
        rockFloorOff: deps.farField.edgeRockFloorOff,
        rockScatter: deps.live.plot?.rocks?.count ?? null,
        rockMinDiameterM: deps.live.plot?.rocks?.minDiameterM ?? null,
        farField: deps.farField.farFieldDrawn,
        marsRamp: deps.globeMaterial.albedoRampFor(deps.bodyScene.state.bodyRows.find((r) => r.id === 'mars')) ?? null,
      };
    },
    /**
     * What is drawing the ground past the plot's own footprint, and where.
     *
     * `surface-clamp --edge` reads it either side of the edge: above it the
     * leaves are the whole ground, below it they are everything the region bake
     * does not cover.
     */
    farField(): unknown {
      const meta = deps.live.plotField?.meta ?? null;
      return {
        on: deps.farField.farFieldDrawn,
        wanted: deps.farField.farFieldWanted(),
        leavesDrawn: deps.tileGroup.visible,
        leaves: deps.pyramidView.tileMeshes.size,
        plotMesh: deps.live.plot !== null,
        footprintM: meta ? { x: +meta.bbox_m.width.toFixed(1), z: +meta.bbox_m.height.toFixed(1) } : null,
        clusterExtentM: deps.live.plot ? +deps.live.plot.clusterExtentM.toFixed(1) : null,
      };
    },
    setLegacyPlotHandover(on: boolean): void {
      deps.cameraRig.legacyPlotHandover = on;
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    /** `descent-dojo --band-pop --inject yaw-basis`: the shared yaw crosses the rig swap unconverted. */
    setLegacyYawBasis(on: boolean): void {
      deps.cameraRig.legacyYawBasis = on;
    },
    /** `descent-dojo --band-pop --inject siteless-stand`: `injects.state.legacySitelessStand`. */
    setLegacySitelessStand(on: boolean): void {
      deps.injects.state.legacySitelessStand = on;
    },
    /**
     * Put a siteless descent back on the body's CENTRE: the nadir view, and the
     * drag that slid the eye over the ground by kilometres a pixel.
     *
     * The landing point is dropped as well as the anchor, so the next framing
     * re-derives nothing and the rig is the pre-fix one exactly.
     */
    setLegacyStanding(on: boolean): void {
      deps.injects.state.legacyStanding = on;
      deps.descentAnchorHandle.landing = null;
      deps.descentAnchorHandle.solarSite = null;
      if (on && deps.site() === undefined && deps.bodyReaders.descendable() && deps.state.bodyId !== null) {
        deps.focusRig.focusCam.goTo(deps.state.bodyId, null, null, deps.focusRig.altitude());
      }
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    /**
     * Keep the landing point across an ascent: `--anchor`'s negative control.
     *
     * The pre-fix cache exactly - `descentAnchor` derives once per body and
     * nothing above the ORBIT edge re-points it - so the second descent aims
     * where the first one landed.
     */
    setLegacyStickyAnchor(on: boolean): void {
      deps.injects.state.legacyStickyAnchor = on;
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    /**
     * `--reentry --inject rover-snap`: the pre-split behaviour, where driving
     * alone re-engages the per-frame rover copy with no easing.
     */
    setLegacyRoverSnap(on: boolean): void {
      deps.injects.state.legacyRoverSnap = on;
    },
    /**
     * Read the camera's ground off the stored field again, not off the mesh's
     * own lattice: `--stand --inject field-height`'s negative control, and the
     * state the eye stood 12.7 m over its ground on Ceres in.
     */
    setLegacyFieldHeight(on: boolean): void {
      deps.injects.state.legacyFieldHeight = on;
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    /**
     * Where the descent is aimed and where the camera is over it, body-fixed
     * degrees: what `--anchor` differences across an ascent and a yaw.
     *
     * The anchor is read off the camera's own state rather than off `landing`,
     * so a control that stops re-pointing it is caught by the reading and not
     * by the flag.
     */
    descentAim(): unknown {
      const node = deps.bodyScene.state.bodyNode;
      if (!node) return null;
      const eye = deps.focusRig.focusCam.eyeWorld();
      deps.descentAnchorHandle._landV.set(eye.x - node.world.x, eye.y - node.world.y, eye.z - node.world.z)
        .applyQuaternion(deps.descentAnchorHandle._landQ.copy(node.quaternion).invert())
        .normalize();
      const subLat = THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(deps.descentAnchorHandle._landV.y, -1, 1)));
      let subLon = THREE.MathUtils.radToDeg(Math.atan2(-deps.descentAnchorHandle._landV.z, deps.descentAnchorHandle._landV.x));
      if (subLon < 0) subLon += 360;
      const lat = deps.focusRig.focusCam.state.latDeg;
      const lon = deps.focusRig.focusCam.state.lonDeg;
      const arc = lat === null || lon === null ? null : +THREE.MathUtils.radToDeg(Math.acos(
        THREE.MathUtils.clamp(
          Math.sin((lat * Math.PI) / 180) * Math.sin((subLat * Math.PI) / 180)
          + Math.cos((lat * Math.PI) / 180) * Math.cos((subLat * Math.PI) / 180)
            * Math.cos(((lon - subLon) * Math.PI) / 180), -1, 1))).toFixed(3);
      // How far round the body the eye can see: the anchor outside this is
      // behind the limb, which is the fault the owner reported by eye.
      const r = node.row.radius_m;
      const eyeR = Math.hypot(eye.x - node.world.x, eye.y - node.world.y, eye.z - node.world.z);
      return {
        bodyId: deps.bodyReaders.ladderBody(),
        band: deps.focusRig.band(),
        altitudeM: +deps.focusRig.altitude().toFixed(2),
        anchorLat: lat === null ? null : +lat.toFixed(4),
        anchorLon: lon === null ? null : +lon.toFixed(4),
        subLat: +subLat.toFixed(4),
        subLon: +subLon.toFixed(4),
        arcDeg: arc,
        horizonDeg: eyeR > r ? +THREE.MathUtils.radToDeg(Math.acos(r / eyeR)).toFixed(3) : null,
        convergence: deps.focusRig.focusCam.aim().convergence,
        yawDeg: +THREE.MathUtils.radToDeg(deps.focusRig.focusCam.state.yaw).toFixed(3),
        landing: deps.descentAnchorHandle.landing?.body === deps.bodyReaders.ladderBody()
          ? { lat: +deps.descentAnchorHandle.landing.lat.toFixed(4), lon: +deps.descentAnchorHandle.landing.lon.toFixed(4) } : null,
        sticky: deps.injects.state.legacyStickyAnchor,
      };
    },
    /** Force a device-pixel ratio, or 0 to give it back to the governor. */
    setSupersample(k: number): void {
      deps.frameGovernor.ssOverride = k > 0 ? k : 0;
      deps.mount.resize();
      deps.live.needsRender = true;
    },
    /**
     * `descent-dojo --colour --self`: mistag one imagery texture.
     *
     * Mars's own orrery map, because it is the one imagery texture that exists
     * at every band from the moment the registry lands - the tiles come and go
     * with the traversal and the site image with the descent. Tagged LINEAR,
     * which is the mistake the criterion exists to catch: a picture read as if
     * it carried no transfer function. `colourDebug` reports what was actually
     * changed, so an arm whose mistag never took exits rather than passing.
     */
    setColourDebug(next: { mistag?: boolean }): void {
      if (next.mistag === undefined) return;
      if (!next.mistag) {
        if (deps.injects.state.colourMistag) {
          deps.injects.state.colourMistag.texture.colorSpace = deps.injects.state.colourMistag.was;
          deps.injects.state.colourMistag = null;
        }
        return;
      }
      if (deps.injects.state.colourMistag) return;
      const map = (deps.bodyScene.state.bodyNode?.mesh.material as THREE.MeshStandardMaterial | undefined)?.map;
      if (!map) return;
      deps.injects.state.colourMistag = { texture: map, was: map.colorSpace };
      map.colorSpace = THREE.LinearSRGBColorSpace;
      map.needsUpdate = true;
      deps.live.needsRender = true;
    },
    get colourDebug(): unknown {
      return {
        mistag: deps.injects.state.colourMistag ? (deps.injects.state.colourMistag.texture.name || '(unnamed)') : null,
        mistagColorSpace: deps.injects.state.colourMistag ? deps.injects.state.colourMistag.texture.colorSpace : null,
        was: deps.injects.state.colourMistag ? deps.injects.state.colourMistag.was : null,
      };
    },
    /**
     * `descent-dojo --sky --self`: paint the frame a known colour.
     *
     * The arm's readers - upper-third mean, the R minus B chroma, the horizon
     * step - are all validated against this before the real sky is measured.
     * Null takes the paint away.
     */
    setSkyDebug(
      next: {
        flatSky?: number | null; injectFrom?: string | null; legacySun?: boolean; seasonSun?: boolean;
        progradeSpin?: boolean; slidingSun?: boolean; eyeOnDatum?: boolean;
      },
    ): void {
      if (next.flatSky !== undefined) deps.dojoPaints.setFlatSky(next.flatSky);
      if (next.injectFrom !== undefined) deps.dojoPaints.setInjectSky(next.injectFrom);
      if (next.eyeOnDatum !== undefined && next.eyeOnDatum !== deps.dojoPaints.skyDebug.eyeOnDatum) {
        deps.dojoPaints.skyDebug.eyeOnDatum = next.eyeOnDatum;
        // The air's own height over the ground moves with this flag, and the
        // Atlas draws on demand: without a frame asked for here, a caller that
        // arms the control and reads the pose gets the frame it drew before.
        deps.live.needsRender = true;
      }
      if (next.legacySun !== undefined && next.legacySun !== deps.dojoPaints.skyDebug.legacySun) {
        deps.dojoPaints.skyDebug.legacySun = next.legacySun;
        deps.solarScrub.syncSolar();
        deps.plotSun.applySun();
      }
      if (next.seasonSun !== undefined && next.seasonSun !== deps.dojoPaints.skyDebug.seasonSun) {
        deps.dojoPaints.skyDebug.seasonSun = next.seasonSun;
        deps.plotSun.applySun();
      }
      if (next.slidingSun !== undefined && next.slidingSun !== deps.dojoPaints.skyDebug.slidingSun) {
        deps.dojoPaints.skyDebug.slidingSun = next.slidingSun;
        deps.solarScrub.syncSolar();
        deps.plotSun.applySun();
      }
      if (next.progradeSpin !== undefined && next.progradeSpin !== deps.dojoPaints.skyDebug.progradeSpin) {
        deps.dojoPaints.skyDebug.progradeSpin = next.progradeSpin;
        deps.plotSun.applySun();
      }
    },
    /**
     * What the sky arms need to know about this body, off the row the page
     * LOADED rather than off the flag the run was launched with: `vacuum`
     * replaces `sky` and `horizon` on a body whose registry row declares no
     * atmosphere, so a criterion that read the flag could not catch a sky
     * routed onto the wrong body.
     */
    get skyDebug(): unknown {
      const row = deps.bodyReaders.rowById(deps.bodyReaders.ladderBody());
      const missing = climateMissing();
      return {
        flatSky: deps.dojoPaints.skyDebug.flatSky,
        injectFrom: deps.dojoPaints.skyDebug.injectFrom,
        legacySun: deps.dojoPaints.skyDebug.legacySun,
        seasonSun: deps.dojoPaints.skyDebug.seasonSun,
        progradeSpin: deps.dojoPaints.skyDebug.progradeSpin,
        slidingSun: deps.dojoPaints.skyDebug.slidingSun,
        /** `--inject eye-on-datum`: the air's eye height measured from the
         *  body's datum again, the reading before it read the drawn ground. */
        eyeOnDatum: deps.dojoPaints.skyDebug.eyeOnDatum,
        /** The body-fixed place a siteless body's sun is read over, or null
         *  where the anchor is already ground. */
        solarSite: deps.descentAnchorHandle.solarSite && deps.descentAnchorHandle.solarSite.body === deps.focusRig.focusCam.state.bodyId
          ? { lat: +deps.descentAnchorHandle.solarSite.lat.toFixed(4), lon: +deps.descentAnchorHandle.solarSite.lon.toFixed(4) } : null,
        bodyId: row ? row.id : null,
        /** Negative is a retrograde rotator, whose sun crosses west to east. */
        spinRateDegDay: deps.bodyScene.state.bodyNode?.row.rotation.W_rate_deg_day ?? null,
        atmosphereKind: row ? row.atmosphere.kind : null,
        seasonMissing: missing.length > 0,
        climateMissing: missing,
        /** The LADDER body's own air. A primary's air drawn behind a moon's
         *  focus is `backgroundAirOn` and never this: what the vacuum
         *  criterion asks is whether THIS body's ground draws a sky. */
        atmoMounted: deps.atmosphereView.atmo !== null && deps.atmosphereView.atmoBodyId === deps.bodyReaders.ladderBody(),
        skyDrawnOn: deps.atmosphereView.atmoBodyId === deps.bodyReaders.ladderBody() ? deps.atmosphereView.atmoBodyId : null,
        /** The body whose air is drawn in the background of an airless focus -
         *  its primary - or null. */
        backgroundAirOn: deps.atmosphereView.atmo !== null && deps.atmosphereView.atmoBodyId !== deps.bodyReaders.ladderBody() ? deps.atmosphereView.atmoBodyId : null,
        /** The sun the sky quad was last DRAWN with, which is the orrery's
         *  geometry above the claim and the plot's analytic angles inside it. */
        skySunAltitudeDeg: deps.atmosphereView.skySunAltitudeDeg,
      };
    },
    /** `descent-dojo --skyband --survey`, read once a frame: the sky quad's gate and what it drew. */
    get skyGate(): unknown {
      const v = deps.atmosphereView;
      const rover = deps.live.plot?.rover ?? null;
      const u = v.atmo?.uniforms;
      const num = (k: string): number | null => {
        const x: unknown = u?.[k]?.value;
        return typeof x === 'number' ? x : null;
      };
      const sun: unknown = u?.['uAtmoSunDir']?.value;
      const mat = v.atmoSky?.mesh.material;
      const camR: unknown = mat instanceof THREE.ShaderMaterial ? mat.uniforms['uSkyCamRadius']?.value : null;
      const stats = v.atmo?.stats() ?? null;
      return {
        ...v.skyGate,
        refused: stats ? stats.nonFiniteRefused : null,
        groundTintFinite: (() => {
          const t = v.atmoTerrainUniforms;
          const sd = t['uAtmoTerrainSunDir']?.value;
          const fr = t['uAtmoFrame']?.value;
          return sd instanceof THREE.Vector3 && fr instanceof THREE.Matrix3
            && [sd.x, sd.y, sd.z, t['uAtmoGroundScale']?.value, t['uAtmoSunIrradiance']?.value, ...fr.elements]
              .every((x) => Number.isFinite(x));
        })(),
        viewHeightM: num('uAtmoViewHeight'),
        eyeHeightM: num('uAtmoEyeHeight'),
        sunDir: sun instanceof THREE.Vector3 ? [sun.x, sun.y, sun.z] : null,
        skyCamRadiusM: typeof camR === 'number' ? camR : null,
        passes: stats ? stats.byPass : null,
        pending: stats ? stats.pending : null,
        pixelRatio: deps.frameGovernor.pixelRatio(),
        visible: v.atmoSky?.mesh.visible ?? false,
        inScene: v.atmoSky !== null && v.atmoSky.mesh.parent !== null,
        airBodyId: v.atmoBodyId,
        bodyId: deps.state.bodyId,
        driving: deps.roverDrive.state.driving,
        rover: rover ? { x: +rover.state.x.toFixed(2), z: +rover.state.z.toFixed(2), yaw: +rover.state.yaw.toFixed(3) } : null,
        frameTick: deps.injects.state.frameTick,
      };
    },
    /**
     * `surface-clamp --solctl`'s two controls, both of them the pre-fix code,
     * and each re-renders the overlay it changes since the Atlas draws on
     * demand.
     *
     * The plot controls back behind a loaded plot field, so the whole panel
     * disappears on a body with no `sites.json` row.
     */
    setLegacyPlotCtlGate(on: boolean): void {
      deps.solarScrub.plotCtlDebug.plotFieldGate = on;
      deps.overlayView.renderOverlay();
      deps.live.needsRender = true;
    },
    /** The Mars sol back under the clock, whatever body is focused. */
    setMarsSolFallback(on: boolean): void {
      deps.solarScrub.plotCtlDebug.marsSol = on;
      deps.plotControls.syncPlotLabels();
      deps.overlayView.renderOverlay();
      deps.live.needsRender = true;
    },
    /**
     * `descent-dojo --shadow --self`: paint a contact shadow, and switch the
     * cascades.
     *
     * `csm: false` is the negative control: every cascade's contribution
     * forces to full visibility (`uCsmOn` in `PLOT_CSM_GLSL`) without freeing
     * the lights, so flipping it back costs nothing. `undefined` leaves the
     * override alone - the band decides, via `updateShadowCascades`.
     */
    setShadowDebug(next: { fakeContact?: boolean; csm?: boolean; passHide?: boolean }): void {
      if (next.fakeContact !== undefined) deps.dojoPaints.setFakeContact(next.fakeContact);
      if (next.passHide !== undefined) {
        deps.farField.legacyPassHide = next.passHide;
        deps.live.needsRender = true;
      }
      if (next.csm !== undefined) {
        deps.dojoPaints.shadowDebug.csmOverride = next.csm;
        // The Atlas draws on demand: a control armed without this reads the
        // frame it was armed to change.
        deps.live.needsRender = true;
      }
    },
    get shadowDebug(): unknown {
      // The light's OWN aim, read back off the light rather than re-derived:
      // the arm's analytic shadow azimuth comes from its own sun model, so a
      // disagreement between these two numbers is the render's and not the
      // reader's.
      const dir = deps.cascades.csm0
        ? deps.cascades._csmProbe.copy(deps.cascades.csm0.position).sub(deps.cascades.csm0.target.position).normalize()
        : null;
      const deg = (r: number): number => +((r * 180) / Math.PI).toFixed(3);
      // The anchor in the plot's own metres, which is the frame the rover's
      // state is in: the two together say which cascade the contact patch is
      // read from, and a patch past the first split carries eight times the
      // texel and a penumbra wider than the annulus the arm measures.
      const anchorLocal = deps.live.plot
        ? deps.cascades._csmCoord.copy(deps.cameraRig.orbit.lookAt)
          .applyMatrix4(_probeInv.copy(deps.live.plot.group.matrix).invert())
        : null;
      return {
        fakeContact: deps.dojoPaints.shadowDebug.fakeContact,
        csmRequested: deps.dojoPaints.shadowDebug.csmOverride,
        /** Whether the cascades are actually contributing right now - the
         *  band, the plot and the debug override all agree. */
        csm: deps.cameraRig.surfaceRegime() && deps.live.plot !== null && deps.dojoPaints.shadowDebug.csmOverride !== false,
        /** Shadow passes since mount: zero here means nothing was ever baked. */
        csmPasses: deps.cascades.csmPasses,
        /** Which draw path the frame took, and what the last bake could see. */
        farField: deps.farField.farFieldDrawn,
        bakeCasters: deps.farField.csmBakeCasters,
        /** Draw calls the second pass issued, and whether the control is armed. */
        farPassCalls: deps.farField.farPassCalls,
        legacyPassHide: deps.farField.legacyPassHide,
        farCutM: +deps.farField.flatnessLimitM().toFixed(1),
        anchorVwM: anchorLocal ? +Math.hypot(anchorLocal.x, anchorLocal.z).toFixed(1) : null,
        /** The contact patch's distance from that anchor, against the splits. */
        roverFromAnchorM: anchorLocal && deps.live.plot?.rover
          ? +Math.hypot(anchorLocal.x - deps.live.plot.rover.state.x,
            anchorLocal.z - deps.live.plot.rover.state.z).toFixed(2)
          : null,
        csmSplitsM: [deps.cascades.CSM_SPLIT_M[0], deps.cascades.CSM_SPLIT_M[1]],
        csmHalfSizesM: [
          deps.cascades.CSM_HALF_SIZES_M[0], deps.cascades.CSM_HALF_SIZES_M[1], deps.cascades.CSM_HALF_SIZES_M[2],
        ],
        csmSunAzimuthDeg: dir ? (deg(Math.atan2(dir.x, -dir.z)) + 360) % 360 : null,
        csmSunAltitudeDeg: dir ? deg(Math.asin(THREE.MathUtils.clamp(dir.y, -1, 1))) : null,
        // Read between frames, so this is the SIM's shadow state and not the
        // Atlas's: the stage sets its own inside begin() and gives this one
        // back in end(), which is why `false` here is the restore working.
        shadowMapEnabled: deps.renderer.shadowMap.enabled,
        shadowMapType: deps.renderer.shadowMap.type,
      };
    },
    /**
     * `descent-dojo --rings`: one tau across every annulus.
     *
     * The control, not a paint: a flat optical depth leaves the geometry, the
     * lighting and the two scattering terms exactly as they are and takes away
     * only the STRUCTURE, so a reader that still sees a Cassini Division after
     * this is reading something other than the profile.
     */
    /**
     * Every body's name as it was drawn, with the rule it is under.
     *
     * `descent-dojo --labels` reads the app's own boxes and then reads the
     * pixels inside them: a name the code means to draw but never paints is a
     * box with nothing in it. The shares are the declutter's own thresholds,
     * so the arm does not carry a second copy of them.
     */
    labels(): unknown {
      const bandNow = deps.focusRig.band();
      return {
        bandGate: bandNow === 'SYSTEM' || bandNow === 'ORBIT',
        band: bandNow,
        focusId: deps.focusRig.focusCam.state.bodyId,
        viewportH: deps.dojoPaints.labelViewportH(),
        fadeGoneHalfDeg: NAME_FADE_GONE_HALF_DEG,
        clearMarginShare: LABEL_CLEAR_MARGIN,
        clearRampShare: LABEL_CLEAR_RAMP,
        pickMinOpacity: LABEL_PICK_MIN_OPACITY,
        rows: deps.bodyScene.state.orrery?.labelState() ?? [],
      };
    },
    setLabelDebug(next: {
      suppress?: boolean; noDeclutter?: boolean; surfaceGate?: boolean;
    }): void {
      if (next.suppress !== undefined) labelDebug.suppress = next.suppress;
      if (next.noDeclutter !== undefined) labelDebug.noDeclutter = next.noDeclutter;
      if (next.surfaceGate !== undefined) labelDebug.surfaceGate = next.surfaceGate;
      deps.bodyScene.state.orrery?.setLabelDebug(next);
      deps.live.needsRender = true;
    },
    get labelDebug(): unknown { return { ...labelDebug }; },
    setRingDebug(
      next: {
        flatTau?: number | null; albedo?: number | null; pointSample?: boolean;
        gain?: number | null; suppressNote?: boolean; suppress?: boolean;
        componentScale?: { index: number; factor: number } | null;
      },
    ): void {
      if (next.flatTau !== undefined) {
        deps.dojoPaints.ringDebug.flatTau = next.flatTau;
        deps.bodyScene.state.orrery?.setRingDebug(next.flatTau);
        deps.live.needsRender = true;
      }
      if (next.albedo !== undefined) {
        deps.dojoPaints.ringDebug.albedo = next.albedo;
        deps.bodyScene.state.orrery?.setRingAlbedo(next.albedo);
        deps.live.needsRender = true;
      }
      if (next.pointSample !== undefined) {
        deps.dojoPaints.ringDebug.pointSample = next.pointSample;
        deps.bodyScene.state.orrery?.setRingPointSample(next.pointSample);
        deps.live.needsRender = true;
      }
      if (next.gain !== undefined) {
        deps.dojoPaints.ringDebug.gain = next.gain;
        deps.bodyScene.state.orrery?.setRingGain(next.gain);
        deps.live.needsRender = true;
      }
      if (next.suppress !== undefined) {
        deps.dojoPaints.ringDebug.suppress = next.suppress;
        deps.bodyScene.state.orrery?.setRingSuppressed(next.suppress);
        deps.live.needsRender = true;
      }
      if (next.componentScale !== undefined) {
        deps.dojoPaints.ringDebug.componentScale = next.componentScale;
        deps.bodyScene.state.orrery?.setRingComponentScale(
          next.componentScale?.index ?? null, next.componentScale?.factor ?? 1);
        deps.live.needsRender = true;
      }
      if (next.suppressNote !== undefined) {
        deps.dojoPaints.ringDebug.suppressNote = next.suppressNote;
        // The card and the overlay are re-rendered from the reading, so the
        // control has to reach the DOM and not just the state.
        deps.overlayView.renderOverlay();
        deps.live.needsRender = true;
      }
    },
    get ringDebug(): unknown {
      return { flatTau: deps.dojoPaints.ringDebug.flatTau, albedo: deps.dojoPaints.ringDebug.albedo,
        pointSample: deps.dojoPaints.ringDebug.pointSample, gain: deps.dojoPaints.ringDebug.gain,
        suppressNote: deps.dojoPaints.ringDebug.suppressNote, suppress: deps.dojoPaints.ringDebug.suppress,
        componentScale: deps.dojoPaints.ringDebug.componentScale };
    },
    /**
     * Where a ring radius projects, and what the profile says is there.
     *
     * Points on the ring PLANE in the body's own frame, projected through the
     * frame's own camera, with the two reasons a sample is not ring returned
     * beside it: the body's disc in front of it, and the body's shadow across
     * it. `tau` comes off the same CPU copy of the bake the shader samples on
     * the GPU, so the arm can say which radius it asked for.
     */
    ringSamples(bodyId: string, plan: ReadonlyArray<readonly [number, number]>): unknown {
      const b = deps.bodyScene.state.orrery?.byId(bodyId);
      const rings = deps.bodyScene.state.orrery?.ringsFor(bodyId);
      if (!b || !rings) return { ok: false, why: `${bodyId} has no ring system` };
      deps.mount.camera.updateMatrixWorld();
      b.mesh.updateMatrixWorld();
      const radiusM = b.row.radius_m;
      const drawScale = b.mesh.scale.x;
      _ringQ.copy(b.quaternion).invert();
      _ringSunProbe.set(-b.world.x, -b.world.y, -b.world.z);
      if (_ringSunProbe.lengthSq() > 0) _ringSunProbe.normalize().applyQuaternion(_ringQ);
      else _ringSunProbe.set(0, 1, 0);
      _ringCamProbe
        .set(deps.mount.cameraWorld.x - b.world.x, deps.mount.cameraWorld.y - b.world.y, deps.mount.cameraWorld.z - b.world.z)
        .applyQuaternion(_ringQ);
      const camLen = Math.max(1e-9, _ringCamProbe.length());
      // The camera in the metres the ring GEOMETRY is in, which is what the
      // shader's own `uRingCamPos` is (`orrery.ts` divides by the drawn
      // scale): a view cosine taken against a scaled position is not the
      // cosine the fragment used.
      _ringCamLocal.copy(_ringCamProbe).divideScalar(Math.max(1e-9, drawScale));
      const deg = (r: number): number => (r * 180) / Math.PI;
      const drawnR = radiusM * drawScale;
      /** NDC of a ring-plane point, so a gradient can be taken by difference. */
      const ndcAt = (radiusKm: number, azimuthDeg: number, out: THREE.Vector3): THREE.Vector3 =>
        out.copy(rings.pointAt(radiusKm, azimuthDeg, _ringPoint))
          .applyMatrix4(b.mesh.matrixWorld).project(deps.mount.camera);
      const rows = plan.map(([radiusKm, azimuthDeg]) => {
        const p = rings.pointAt(radiusKm, azimuthDeg, _ringPoint);
        const pb = p.dot(_ringSunProbe);
        const pc = p.lengthSq() - radiusM * radiusM;
        const shadowed = pb < 0 && pb * pb - pc > 0;
        const world = _ringWorld.copy(p).applyMatrix4(b.mesh.matrixWorld);
        const dist = world.length();
        // The camera is the render frame's origin, so the occlusion test is one
        // ray from it against the body's own DRAWN sphere.
        const centre = b.mesh.position;
        const tc = _ringDir.copy(world).divideScalar(Math.max(1e-9, dist)).dot(centre);
        const perp2 = centre.lengthSq() - tc * tc;
        let occluded = false;
        if (tc > 0 && perp2 < drawnR * drawnR) {
          occluded = tc - Math.sqrt(drawnR * drawnR - perp2) < dist;
        }
        const onFrame = rings.mesh.visible && isOnScreen(deps.mount.camera, world);
        // The two cosines the shader floors, at THIS sample: the fragment's own
        // view direction and the sun's elevation over the plane. An arm that
        // predicts a brightness from the profile needs the same two.
        const viewLen = Math.max(1e-9, _ringView.subVectors(_ringCamLocal, p).length());
        const muV = Math.max(Math.abs(_ringView.y / viewLen), RING_MU_VIEW_FLOOR);
        const muS = Math.max(Math.abs(_ringSunProbe.y), RING_MU_SUN_FLOOR);
        const sameSide = _ringSunProbe.y * _ringView.y > 0;
        const ndc = _ringNdc.copy(world).project(deps.mount.camera);
        // How far the ring radius moves per unit of NDC, by difference through
        // the frame's own projection: one kilometre of radius and a tenth of a
        // degree of azimuth give the Jacobian of (ndcX, ndcY) against
        // (radius, azimuth), and its inverse's first row is the radial
        // gradient the shader's `fwidth(r)` is taken along. Read here rather
        // than derived from the pose, because the pose is not the projection.
        const dR = 1;
        const dA = 0.1;
        const nR = ndcAt(radiusKm + dR, azimuthDeg, _ringNdcDr);
        const jrx = (nR.x - ndc.x) / dR;
        const jry = (nR.y - ndc.y) / dR;
        const nA = ndcAt(radiusKm, azimuthDeg + dA, _ringNdcDa);
        const jax = (nA.x - ndc.x) / dA;
        const jay = (nA.y - ndc.y) / dA;
        const det = jrx * jay - jry * jax;
        const gradX = Math.abs(det) > 1e-30 ? jay / det : 0;
        const gradY = Math.abs(det) > 1e-30 ? -jax / det : 0;
        return {
          radiusKm,
          azimuthDeg,
          onRing: radiusKm >= rings.innerKm && radiusKm <= rings.outerKm,
          // Nine places, not four: a component published at 1e-8 rounds to
          // zero at four and an arm printing its own bound would print it as
          // a ring of no depth at all.
          tau: +rings.tauAt(radiusKm).toFixed(9),
          onFrame,
          occluded,
          shadowed,
          ndcX: +ndc.x.toFixed(5),
          ndcY: +ndc.y.toFixed(5),
          distanceKm: Math.round(dist / UNITS_PER_KM),
          muV: +muV.toFixed(6),
          muS: +muS.toFixed(6),
          sameSide,
          /** Kilometres of ring radius per unit of NDC, x and y. */
          radiusKmPerNdcX: +gradX.toFixed(6),
          radiusKmPerNdcY: +gradY.toFixed(6),
        };
      });
      return {
        ok: true,
        bodyId,
        rows,
        band: deps.focusRig.band(),
        altitude: Math.round(deps.focusRig.altitude()),
        visible: rings.mesh.visible,
        /** The annulus drawn empty at the same pose: `bound`'s second frame. */
        suppressed: rings.suppressed,
        /** The sub-pixel fade scales both radiance and alpha, so every grey
         *  level an instrument reads off the annulus is multiplied by it. */
        fade: +rings.fade.toFixed(4),
        innerKm: rings.innerKm,
        outerKm: rings.outerKm,
        flatTau: deps.dojoPaints.ringDebug.flatTau,
        /** What the annulus is drawn with, and the override that forced it:
         *  the albedo control has to be readable off the reading it changed. */
        particleAlbedo: +rings.albedo.toFixed(4),
        albedoOverride: deps.dojoPaints.ringDebug.albedo,
        /** Whether the annulus is drawn on the three-tap read the footprint
         *  mean replaced, so a control can be read off the reading it changed. */
        pointSample: rings.pointSample,
        /** The declared brightness stretch the annulus is drawn with, the
         *  override that forced it, and the words the card and the overlay
         *  carry for it - so `visible` and `declared` read one answer. */
        /** The stage's own device pixels per CSS pixel THIS frame, governor
         *  included. A pixel's radial footprint is set by the buffer the
         *  shader ran on, not by the size a readback is blitted to. */
        pixelRatio: +deps.frameGovernor.pixelRatio().toFixed(4),
        gain: +rings.gain.toFixed(4),
        gainOverride: deps.dojoPaints.ringDebug.gain,
        gainDeclared: deps.bodyReaders.rowById(bodyId)?.rings?.brightness_gain.factor ?? null,
        gainTarget: deps.bodyReaders.rowById(bodyId)?.rings?.brightness_gain.target_grey ?? null,
        gainTooFaint: deps.bodyReaders.rowById(bodyId)?.rings?.brightness_gain.too_faint ?? null,
        gainNote: deps.inspector.ringGainNote(deps.bodyReaders.rowById(bodyId)),
        gainNoteSuppressed: deps.dojoPaints.ringDebug.suppressNote,
        /** The sun's own elevation over the ring plane: which face is lit. */
        sunElevationDeg: +deg(Math.asin(
          THREE.MathUtils.clamp(_ringSunProbe.y, -1, 1))).toFixed(3),
        /** And the camera's, so a face can be named rather than assumed. */
        camElevationDeg: +deg(Math.asin(
          THREE.MathUtils.clamp(_ringCamProbe.y / camLen, -1, 1))).toFixed(3),
        bodyDistanceKm: Math.round(camLen / UNITS_PER_KM),
        bodyDrawnAngularDiameterDeg: b.mesh.userData['drawnAngularDiameterDeg'] ?? null,
        ringOuterAngularDiameterDeg: b.mesh.userData['ringOuterAngularDiameterDeg'] ?? null,
      };
    },
    /**
     * The baked profile itself, one value per texel, off the CPU copy.
     *
     * So an instrument can predict what a pixel of the annulus should read
     * from the same numbers the shader samples, rather than from a second
     * decode of the same bytes. Flat while a flat-tau control is armed, which
     * is what `tauAt` answers.
     */
    /**
     * The components the annulus is generated from, and the source it runs.
     *
     * `tauSource` is the generated `ringTauAt` the mesh's own material and the
     * body's shadow both carry, so an arm can assert that a component's
     * published radii and depth reached the SHADER rather than only the row.
     * `tau` is that same function on the CPU, at the radii asked for.
     */
    ringComponents(bodyId: string, radiiKm: readonly number[]): unknown {
      const rings = deps.bodyScene.state.orrery?.ringsFor(bodyId);
      const row = deps.bodyReaders.rowById(bodyId);
      if (!rings || !row?.rings) return { ok: false, why: `${bodyId} has no ring system` };
      return {
        ok: true,
        bodyId,
        innerKm: rings.innerKm,
        outerKm: rings.outerKm,
        components: rings.components,
        gaps: row.rings.gaps,
        particleAlbedo: row.rings.particle_albedo,
        tauSource: rings.tauSource,
        tau: radiiKm.map((km) => ({ km, tau: rings.tauAt(km) })),
      };
    },
    ringProfile(bodyId: string): unknown {
      const rings = deps.bodyScene.state.orrery?.ringsFor(bodyId);
      if (!rings) return { ok: false, why: `${bodyId} has no ring system` };
      const texels = Math.round(rings.outerKm - rings.innerKm);
      const tau = new Array<number>(texels);
      for (let i = 0; i < texels; i++) tau[i] = +rings.tauAt(rings.innerKm + i).toFixed(5);
      return {
        ok: true,
        innerKm: rings.innerKm,
        outerKm: rings.outerKm,
        kmPerTexel: 1,
        /** The shader's own cap on taps across a pixel footprint. */
        footprintTaps: RING_FOOTPRINT_TAPS,
        muViewFloor: RING_MU_VIEW_FLOOR,
        muSunFloor: RING_MU_SUN_FLOOR,
        tau,
      };
    },
    /**
     * `atmo-check --gpu`: the scattering tables, and what they were built from.
     *
     * `params` is what the passes actually ran with - the effective scale
     * heights and the seasonally scaled Mie coefficient - so the check can
     * re-derive them from the registry rather than trust them.
     */
    atmosphere(): unknown {
      if (!deps.atmosphereView.atmo) return { mounted: false };
      const t = deps.atmosphereView.atmoTerrainUniforms;
      return {
        mounted: true,
        stats: deps.atmosphereView.atmo.stats(),
        params: deps.atmosphereView.atmo.params(),
        lsDeg: deps.solarScrub.solar.lsDeg,
        solPhase: deps.solarScrub.solar.solPhase,
        /**
         * The two scales the terrain's own colour and the haze in front of it
         * are summed in, so `descent-dojo --haze` can read the gap rather than
         * re-derive it: `sunIrradiance` is the physical one the in-scatter is
         * added on, `orrerySunIntensity` the look the globe and the tiles are
         * lit by, and `groundScale` the conversion between them.
         */
        terrain: {
          sunIrradiance: t['uAtmoSunIrradiance']!.value as number,
          groundScale: t['uAtmoGroundScale']!.value as number,
          orrerySunIntensity: ORRERY_SUN_INTENSITY,
          aerialOff: (t['uAtmoAerialOff']!.value as number) > 0.5,
          meshDist: (t['uAtmoMeshDist']!.value as number) > 0.5,
          aerialTailOnGround: (t['uAtmoAerialTailOnGround']!.value as number) > 0.5,
          skirtHaze: (t['uAtmoSkirtHaze']!.value as number) > 0.5,
          fillScale: t['uAtmoFillScale']!.value as number,
          /** Whether the focused body's globe is drawing its cloud top rather
           *  than its own map, and the reflectance it is drawing. */
          deckDraw: (t['uAtmoDeckDraw']!.value as number) > 0.5,
          deckAlbedo: (t['uAtmoDeckAlbedo']!.value as THREE.Vector3).toArray(),
        },
      };
    },
    /**
     * One table, read off the GPU.
     *
     * Every pass still owed is run first: one pass a frame means a fresh mount
     * has not finished them, and a readback of a target nothing has drawn into
     * returns zeros, which is the failure whose symptom is a pass.
     */
    atmosphereLut(name: string): unknown {
      if (!deps.atmosphereView.atmo) return { ok: false, why: 'no atmosphere is mounted' };
      deps.atmosphereView.atmo.flush();
      return deps.atmosphereView.atmo.readLut(name);
    },
    /**
     * `atmo-check --gpu --self`: a deliberately wrong scale height.
     * `descent-dojo --sky`: GPU-synced pass timing and its injected cost.
     *
     * Both scale heights, not the Rayleigh one alone: on Mars the aerosol
     * column carries 0.50 of the 0.503 zenith optical depth the check gates, so
     * scaling only the Rayleigh one moves that number by 0.3 percent - under
     * the gate, which is a negative control that cannot fail.
     */
    setAtmosphereDebug(next: {
      scaleHeightScale?: number | null;
      betaMScale?: number | null;
      syncPasses?: boolean;
      injectPassMs?: number | null;
      deckOff?: boolean;
      deckFlip?: boolean;
      forceExposureScale?: number | null;
      hardSunShadow?: boolean;
      clearTables?: boolean;
      canvasViewport?: boolean;
      nonFiniteFrame?: 'altitude' | 'sun' | 'both' | null;
      nonFiniteGuardOff?: boolean;
      legacyGlobeProgram?: boolean;
      easeExposureAcrossBody?: boolean;
      meterAtSystem?: boolean;
      skySteps?: boolean;
      tilesEveryFrame?: boolean;
    }): void {
      if (next.forceExposureScale !== undefined) deps.exposureHandle.exposureDebug.forceScale = next.forceExposureScale;
      if (next.easeExposureAcrossBody !== undefined) {
        deps.exposureHandle.exposureDebug.easeAcrossBody = next.easeExposureAcrossBody;
      }
      if (next.meterAtSystem !== undefined) deps.exposureHandle.exposureDebug.meterAtSystem = next.meterAtSystem;
      if (next.legacyGlobeProgram !== undefined) {
        deps.globeMaterial.switchDebug.legacyGlobeProgram = next.legacyGlobeProgram;
      }
      if (next.nonFiniteFrame) deps.atmosphereView.aerialDebug.nanSkyFrame = true;
      if (next.nonFiniteGuardOff !== undefined) deps.atmosphereView.aerialDebug.nonFiniteViewGuardOff = next.nonFiniteGuardOff;
      deps.atmosphereView.atmo?.setDebug(next);
      // The globe reads the deck off `params().deckM`, which this can empty,
      // and the Atlas draws on demand.
      if (next.deckOff !== undefined) deps.atmosphereView.atmoDeckMCache = undefined;
      deps.live.needsRender = true;
    },
    /**
     * `descent-dojo --sky`'s `horizon` negative control: force the aerial
     * term to the identity - transmittance 1, in-scatter 0 - on every
     * terrain consumer at once (the tiles and the plot share the flag), so a
     * skirt or an un-hazed hillside reads the tens of grey levels the chunk
     * exists to remove. A bar that cannot fail is not a bar.
     *
     * `meshDist` is `descent-dojo --haze --inject quilt`'s own control: the
     * globe back on its tessellated mesh's distance, which quilts the ORBIT
     * disc on the sphere's lat/lon grid.
     */
    /**
     * `descent-dojo --honesty --inject k-none`: report the rock law as absent.
     *
     * The overlay's text is what the criterion reads, so the control has to
     * move the text and nothing else - the scatter already on the ground stays
     * exactly where it is, which is the inconsistency the criterion catches: a
     * panel claiming no law over ground that is drawing boulders.
     */
    /**
     * `descent-dojo --inject height-role`: the imagery floor's own control.
     *
     * Splices the named body's height role into this body's manifest and reads
     * its tiles from that body's pyramid, so the row stops declaring an
     * imagery-only floor and the traversal has height to ask for. The descent
     * then has to go PAST the floor, height has to appear on the wire, and the
     * overlay's string has to change - three readings that a stop hardcoded to
     * an altitude or to a body name would pass.
     *
     * The ground that arrives is the donor's, which is the point of a control
     * and not a defect.
     */
    setImageryFloorDebug(next: { heightRoleFrom?: string | null; ignoreSmoothGround?: boolean }): void {
      const dbg = deps.bodyReaders.imageryFloorDebug;
      const role = next.heightRoleFrom === undefined ? dbg.heightRoleFrom : next.heightRoleFrom;
      const ignore = next.ignoreSmoothGround ?? dbg.ignoreSmoothGround;
      if (dbg.heightRoleFrom === role && dbg.ignoreSmoothGround === ignore) return;
      dbg.heightRoleFrom = role;
      dbg.ignoreSmoothGround = ignore;
      // The role is spliced in where the source opens, so the pyramid has to be
      // reopened for the injection to reach the wire at all.
      deps.pyramidView.releaseTiles();
      deps.overlayView.renderOverlay();
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    /**
     * `descent-dojo --inject nodata-black`: unmeasured imagery drawn black.
     *
     * The substitution `coverageFill.ts` performs is the identity in normal
     * use - the texel already carries the fill the bake derived from the
     * ground beside it - so this is what proves the coverage the tiles and the
     * globe map carry is real and is what those pixels are drawn from. A
     * pyramid with an all-measured mask would leave the injected colour
     * nothing to paint, and the control would fail to fail.
     */
    setUnmeasuredFill(cssColor: string | null): void {
      deps.pyramidView.unmeasuredFill = cssColor;
      deps.pyramidView.tileCoverage.setUnmeasured(cssColor);
      deps.bodyScene.state.orrery?.setUnmeasuredFill(cssColor);
      deps.live.needsRender = true;
    },
    /** The coverage the open pyramid publishes, for the criterion to hold the
     *  frame to. */
    get coverage(): unknown {
      const manifest = deps.pyramidView.tileBody === deps.state.bodyId ? deps.pyramidView.tileSource?.manifest() ?? null : null;
      const cov = deps.inspector.imageryCoverage(manifest);
      return {
        body: deps.state.bodyId,
        unmeasuredFill: deps.pyramidView.unmeasuredFill,
        drawing: deps.pyramidView.tileCoverage.unmeasuredHex(),
        fraction: cov ? cov.fraction : null,
        here: cov ? cov.here : null,
        hereLevel: cov ? cov.hereLevel : null,
        maskLevel: manifest?.roles.get('albedo')?.coverage?.level ?? null,
        tilesDrawn: deps.pyramidView.tileMeshes.size,
      };
    },
    /** The floor as the app derives it, for the criterion to hold it to. */
    get imageryFloor(): unknown {
      const f = deps.bodyReaders.imageryFloor(deps.state.bodyId);
      const radiusM = deps.bodyReaders.registryDatumM(deps.state.bodyId);
      return {
        body: deps.state.bodyId,
        injectedHeightRoleFrom: deps.bodyReaders.imageryFloorDebug.heightRoleFrom,
        ignoreSmoothGround: deps.bodyReaders.imageryFloorDebug.ignoreSmoothGround,
        smoothGround: deps.bodyReaders.smoothGround(deps.state.bodyId),
        stopM: deps.bodyReaders.descentFloorM(deps.state.bodyId),
        level: f?.level ?? null,
        gsdM: f === null ? null : +f.gsdM.toFixed(4),
        altitudeM: f === null ? null : Math.round(f.altitudeM),
        band: f === null ? null : bandAt(f.altitudeM, radiusM),
        tier: deps.bodyReaders.floorTier(),
        radiusM,
      };
    },
    /**
     * `descent-dojo --inject corridor-edge`: one scale height onto the corridor
     * floor.
     *
     * The lowest band's own bottom edge moves, on the row, and everything the
     * class is built on has to move with it: the camera's stop, the rung the
     * breadcrumb titles, the shell the view draws and the sentence the overlay
     * prints. A stop hardcoded to an altitude or keyed off a body name passes
     * the criterion without moving, which is what this exists to catch.
     */
    setCorridorDebug(next: { liftLowestBottom?: boolean }): void {
      if (next.liftLowestBottom === undefined) return;
      if (deps.bodyReaders.corridorDebug.liftLowestBottom === next.liftLowestBottom) return;
      deps.bodyReaders.corridorDebug.liftLowestBottom = next.liftLowestBottom;
      deps.bodyReaders.corridorCache = null;
      // The camera may already be standing below the moved edge.
      deps.focusRig.focusCam.reclampAltitude();
      deps.overlayView.renderOverlay();
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    /** The corridor as the app derives it, for the criterion to hold it to. */
    get corridor(): unknown {
      const geo = deps.bodyReaders.corridorFor(deps.state.bodyId);
      if (geo === null) return { body: deps.state.bodyId, corridor: false, builds: deps.bodyReaders.corridorBuilds };
      const radiusM = deps.surfaceRadius.datumRadiusM(deps.state.bodyId) ?? deps.bodyReaders.registryDatumM(deps.state.bodyId);
      const here = corridorBandIndexAt(geo, deps.focusRig.altitude());
      return {
        body: deps.state.bodyId,
        corridor: true,
        builds: deps.bodyReaders.corridorBuilds,
        liftedLowestBottom: deps.bodyReaders.corridorDebug.liftLowestBottom,
        scaleHeightM: geo.scaleHeightM,
        deckAltitudeM: geo.deckAltitudeM,
        floorAltitudeM: geo.floorAltitudeM,
        topAltitudeM: geo.topAltitudeM,
        scoopAltitudeM: geo.scoopAltitudeM,
        floorBand: bandAt(geo.floorAltitudeM, radiusM),
        floorTier: deps.bodyReaders.floorTier(),
        bandIndex: here,
        bandTitle: here === null ? null : geo.bands[here]!.title,
        bands: geo.bands.map((b) => ({
          id: b.id,
          title: b.title,
          bottomAltitudeM: b.bottomAltitudeM,
          topAltitudeM: b.topAltitudeM,
          he3MoleFraction: b.he3MoleFraction,
        })),
        radiusM,
        // What this class does not have, read off the app's own handles rather
        // than off the scene graph's names.
        mounted: {
          hexCells: deps.hexViewSeam.hexOverlay.debug().cells,
          detailVisible: deps.detailGroup.visible,
          plotField: deps.live.plotField !== null,
          plot: deps.live.plot !== null,
          rover: deps.live.plot?.rover !== null && deps.live.plot?.rover !== undefined,
          tilesOpen: deps.pyramidView.tileSource !== null,
        },
        view: deps.overlayView.state.corridorView === null
          ? null
          : {
            visible: deps.overlayView.state.corridorView.handle.visible,
            litBand: deps.overlayView.state.corridorView.handle.litBand,
            shellRadiiM: [...deps.overlayView.state.corridorView.handle.shellRadiiM],
          },
      };
    },
    /** The same height source the overlay drapes onto, for `--hex --drape`. */
    heightAtLatLon(latDeg: number, lonDeg: number): number | null {
      return deps.hexViewSeam.hexHeightAt(latDeg, lonDeg);
    },
    /**
     * The radius the DRAWN ground stands at under a lat/lon, metres.
     *
     * `--hex --claims` reads the frame at a cell's own centre, and a point put
     * on the datum sphere instead is 2.5 km off the ground at Jezero - which at
     * that arm's grazing stops is most of the frame height, so the reading was
     * of the sky. The globe's own drawn radius when no tile covers the point,
     * which is the ground up there.
     */
    groundRadiusAtLatLon(latDeg: number, lonDeg: number): number | null {
      const base = deps.bodyScene.state.bodyNode?.drawRadiusM ?? deps.surfaceRadius.groundDatumM(deps.bodyReaders.ladderBody());
      if (!Number.isFinite(base) || base <= 0) return null;
      // The overlay's own ground, so a claim is read where the grid is drawn.
      const h = deps.hexViewSeam.hexHeightAt(latDeg, lonDeg);
      return h === null ? base : base + h * deps.solarScrub.solar.exag;
    },
    /**
     * Stakes or releases a claim in the cache only - the parcel panel's own
     * button goes through the server now (`stakeSelected` in this file).
     *
     * `--hex --claims` uses it two ways: to hold ONE plot of the lattice so
     * the plot mesh's own claim texture can be read against that plot's
     * footprint, and to produce the store's own bytes for the cross-tab check
     * (a second tab writes them back and this tab has to notice). It depends
     * on no boot read landing mid-arm: on a machine with a reachable craft
     * target, `bootGround`'s `applyGround` can wipe what this just wrote.
     */
    async setClaimAt(latDeg: number, lonDeg: number, held: boolean): Promise<boolean> {
      await ensureAddresses();
      if (held) setClaim(latDeg, lonDeg, CREW_HOLDER, Math.round(deps.solsSinceAnchorLive()), deps.bodyReaders.ladderBody());
      else releaseClaim(latDeg, lonDeg);
      deps.siteGround.refreshClaimOverlay();
      deps.globeMaterial.syncGlobeClaims();
      deps.globeMaterial.syncTileClaims();
      deps.live.needsRender = true;
      return true;
    },
    /** The sol a claim at this lat/lon was staked at, or null if it is unheld. */
    claimSolAt(latDeg: number, lonDeg: number): number | null {
      return getClaim(latDeg, lonDeg)?.sol ?? null;
    },
    /**
     * Is the ground under this lat/lon claimed, as the page's own aggregation
     * answers right now?
     *
     * The memo `claims.ts` keeps per resolution is what a claim staked in
     * another tab used to miss, and the frame is one step further downstream
     * than that fault: a tile already decoded carries the claim texture it was
     * built with whatever the store now says. `--hex --claims`'s cross-tab
     * check reads this beside the frame for that reason.
     */
    /**
     * The claim byte the DRAWN tile under a lat/lon carries, and which tile.
     *
     * One step downstream of `claimedAtLatLon`: the aggregation can hold a
     * cell while the texture over it still reads 0, which is what a claim
     * staked under drawn ground used to leave.
     */
    tileClaimAtLatLon(latDeg: number, lonDeg: number): unknown {
      return deps.globeMaterial.tileClaimAt(latDeg, lonDeg);
    },
    claimedAtLatLon(latDeg: number, lonDeg: number): unknown {
      const res = deps.hexViewSeam.hexOverlay.resolutionAt(deps.hexViewSeam.hexView());
      if (res === null) return null;
      const cell = cellAt(latDeg, lonDeg, res);
      if (cell === null) return null;
      return { res, cell, claimed: claimedCellsAt(res).has(cell) };
    },
    /**
     * The plot lattice, its claim texture, and the frame both are addressed in.
     *
     * `--hex --claims` at PLOT and SURFACE rebuilds the raster from these
     * numbers alone and requires the page's own bytes to equal the HEXAGON
     * one and differ from the circumcircle one, which is the reading round 5's
     * rasterisation fix had none of.
     */
    plotClaimState(): unknown {
      const field = deps.live.plotField;
      const p = deps.live.plot;
      if (!field || !p) return null;
      return {
        radiusM: field.meta.hex.radius_m,
        bboxW: field.meta.bbox_m.width,
        bboxH: field.meta.bbox_m.height,
        texels: 64,
        origin: p.latticeOriginM(),
        circum: deps.injects.state.claimCircumDebug,
        // Where the EYE stands in the lattice's own metres. The walkaround rig
        // owns the camera at SURFACE and stands at the plot mesh's own origin
        // whatever ground the focus names, so an arm that wants one plot's own
        // wash has to be told which plot it is already looking at.
        eye: (() => {
          const v = deps.mount.camera.position.clone();
          p.group.worldToLocal(v);
          return { x: v.x, z: v.z };
        })(),
        // Which way the rig FACES, in the same lattice metres as `eye`. The
        // middle of the frame lands ahead of the eye along this, so an arm
        // reading that pixel has to know which plot it is over, and where the
        // rig happens to point is not a property of the ground.
        face: (() => {
          const o = deps.mount.camera.position.clone();
          const t = o.clone().add(deps.mount.camera.getWorldDirection(new THREE.Vector3()));
          p.group.worldToLocal(o);
          p.group.worldToLocal(t);
          const x = t.x - o.x;
          const z = t.z - o.z;
          const n = Math.hypot(x, z);
          return n > 1e-9 ? { x: x / n, z: z / n } : { x: 0, z: 0 };
        })(),
        plots: field.placements.map((pl) => {
          const ll = deps.plotGeometry.latLonAt(pl.x_m, pl.z_m);
          return {
            q: pl.q, r: pl.r, x: pl.x_m, z: pl.z_m, lat: ll.lat, lon: ll.lon,
            enterable: Boolean(pl.tile),
            held: Boolean(getClaim(ll.lat, ll.lon)),
          };
        }),
        claims: Array.from(p.claimTexels()),
      };
    },
    /**
     * Every surveyed cell of the current plot, and where its outline lands.
     *
     * `--select` works out which cell a pixel is over by containment in these
     * projected corners, which is a different question from the one
     * `pickSubPlotOnTiles` answers (nearest centre inside its own radius in
     * pixels), so the arm's answer is not the pick's answer read back.
     */
    plotCells(): unknown {
      if (!deps.live.plotField || !deps.live.plotSubs.length) return null;
      const rect = deps.mount.surface.getBoundingClientRect();
      return {
        band: deps.focusRig.band(),
        tier: deps.state.tier,
        selected: deps.live.selectedSub?.id ?? null,
        hovered: deps.live.hoverSub?.id ?? null,
        drawnOnGround: deps.selection.selectionDrawn(),
        rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
        cells: deps.live.plotSubs.map((s) => {
          const place = s.parentQ === undefined ? undefined
            : deps.live.plotField?.placements.find((x) => x.q === s.parentQ && x.r === s.parentR);
          return {
            id: s.id,
            x: s.x,
            z: s.z,
            radiusM: s.radius_m,
            dataTier: s.dataTier,
            verdict: s.verdict,
            parentQ: s.parentQ ?? null,
            parentR: s.parentR ?? null,
            // `renderDescend`'s own condition, so `--select-enter` aims only at
            // cells the panel would offer the button on: nineteen of Jezero's
            // thirty-six plots lie beyond the stereo footprint and get a hint
            // instead. `plotClaimState` answers the same question off the plot
            // MESH, which does not exist above the SURFACE edge.
            enterable: s.parentQ !== undefined && !(place !== undefined && place.tile === null),
            centre: deps.plotGeometry.plotPointToScreen(s.x, s.z),
            corners: hexCorners(s.radius_m).map(
              ([dx, dz]) => deps.plotGeometry.plotPointToScreen(s.x + dx, s.z + dz),
            ),
          };
        }),
      };
    },
    /**
     * `--select --inject no-select-draw` / `wrong-cell`, `--select-enter
     * --inject plot-off-centre` and `--select-cells --inject
     * cells-mode-stuck`: the four controls.
     */
    setSelectDebug(
      next: {
        drawOff?: boolean; wrongCell?: boolean; enterOffCentre?: boolean; cellsStuck?: boolean;
      },
    ): unknown {
      if (next.drawOff !== undefined) deps.injects.state.selectDrawOff = next.drawOff;
      if (next.wrongCell !== undefined) deps.injects.state.selectWrongCell = next.wrongCell;
      if (next.enterOffCentre !== undefined) deps.injects.state.selectEnterOffCentre = next.enterOffCentre;
      if (next.cellsStuck !== undefined) { deps.injects.state.cellsModeStuck = next.cellsStuck; deps.plotControls.applyCellMode(); }
      deps.selection.applySelection();
      return {
        drawOff: deps.injects.state.selectDrawOff,
        wrongCell: deps.injects.state.selectWrongCell,
        enterOffCentre: deps.injects.state.selectEnterOffCentre,
        cellsStuck: deps.injects.state.cellsModeStuck,
      };
    },
    /** `--select-cells` presses the panel's own button; this is what it reads. */
    cellsMode(): unknown {
      return { mode: deps.plotControls.state.cellMode, drawn: deps.injects.state.cellsModeStuck ? 'fill' : deps.plotControls.state.cellMode, stuck: deps.injects.state.cellsModeStuck };
    },
    /**
     * The rock field's own fill: `last`/`max` are the staged mount fill's own
     * worst slice, `refillLast`/`refillMax` a walked threshold's own worst
     * per-frame total, and `rockField.ts` writes each pair from its own path
     * only, so reading one can never pick up instances the other placed.
     * `budget` is `FILL_SLICE_INSTANCES`; `refills` counts the band rebuilds a
     * walking camera has started. `refillBand` is the one band index the most
     * recent rebuild touched; `refillBandBaseDrawn`/`refillBandBaseObstacles`
     * are that band's own counts read at the crossing and `refillBandDrawn`/
     * `refillBandObstacles` its live counts now - a per-band partial-swap
     * check reads these four instead of the aggregate `rockProbe` totals.
     * `rockFillSliceReset` zeroes the walked fields
     * (`refillLast`/`refillMax`/`refills`/`refillInstances`/`refillBand`*) only.
     */
    rockFillSliceStats():
    {
      last: number; max: number; budget: number; refills: number; refillInstances: number;
      refillLast: number; refillMax: number;
      refillBand: number; refillBandBaseDrawn: number; refillBandBaseObstacles: number;
      refillBandDrawn: number; refillBandObstacles: number;
    } | null {
      return deps.live.plot ? deps.live.plot.rockFillSliceStats() : null;
    },
    /** Zero the walked-refill counters above; `?rockrefill=0` is the control that has to move them. */
    rockFillSliceReset(): void {
      if (deps.live.plot) deps.live.plot.rockFillSliceReset();
    },
    /**
     * `--hex --inject h3-step` / `hex-none` / `pick-facet` / `float` /
     * `hard-swap`, and `--hex --claims` / `--inject claim-shift`.
     *
     * `seedClaims` and `claimShift` route to `claims.ts`'s own debug seed
     * rather than the overlay's, and force every resident tile to decode again
     * - the same `releaseTiles` call `setImageryFloorDebug` makes when its own
     * injection has to reach the wire. The refill `syncTileClaims` does is not
     * a substitute for that: the seed replaces the aggregation wholesale, so a
     * tile has to be re-fetched to be judged as one the seed built.
     *
     * `claimStale` is `--hex --claims --inject claim-stale`'s hook: it holds
     * every drawn tile at the texture its own decode baked, which is what a
     * staked plot could not reach before `syncTileClaims`.
     *
     * `unlit` is `body-sweep --inject hex-unlit`'s hook: it puts the
     * overlay's flat, day/night-blind material back, which is the fault the
     * ground-Lambert term this file carries exists to fix.
     *
     * `cardStatic` is `--hex --card --inject card-static`'s hook: it freezes
     * the sampler card's own body/quad text, which is atlas.ts state rather
     * than the overlay's, so it is handled here and never forwarded.
     */
    setHexDebug(
      next: {
        resStep?: number | null; suppress?: boolean; pickFacet?: boolean;
        float?: boolean; hardSwap?: boolean; unlit?: boolean;
        seedClaims?: readonly string[] | null; claimShift?: boolean;
        cardStatic?: boolean; claimCircum?: boolean; claimStale?: boolean;
        forceShape?: boolean; capScale?: number | null; sinkM?: number | null;
        legacy?: boolean; fieldDrape?: boolean; datumHorizon?: boolean; datumDrape?: boolean;
        markerDatum?: boolean; pickDatum?: boolean;
      },
    ): void {
      if (next.fieldDrape !== undefined) deps.injects.state.fieldDrapeDebug = next.fieldDrape;
      if (next.datumDrape !== undefined) {
        deps.injects.state.datumDrapeDebug = next.datumDrape;
        deps.pyramidView.tileHeightVersion++;
      }
      if (next.pickFacet !== undefined) deps.injects.state.pickFacetGate = next.pickFacet;
      if (next.pickDatum !== undefined) deps.injects.state.pickDatumGate = next.pickDatum;
      if (next.cardStatic !== undefined) deps.injects.state.cardStaticDebug = next.cardStatic;
      if (next.claimCircum !== undefined) {
        deps.injects.state.claimCircumDebug = next.claimCircum;
        deps.live.plot?.setClaimCircum(deps.injects.state.claimCircumDebug);
        deps.siteGround.refreshClaimOverlay();
      }
      if (next.seedClaims !== undefined || next.claimShift !== undefined) {
        setClaimsDebug({ seed: next.seedClaims, shift: next.claimShift });
        deps.pyramidView.releaseTiles();
        deps.globeMaterial.syncGlobeClaims();
        deps.globeMaterial.syncTileClaims();
      }
      if (next.claimStale !== undefined) {
        deps.globeMaterial.claimStaleDebug = next.claimStale;
        // Turning the control back off has to catch up: the revision the
        // refill is keyed on may never move again.
        if (!deps.globeMaterial.claimStaleDebug) deps.globeMaterial.tileClaimKey = '';
        deps.globeMaterial.syncTileClaims();
      }
      deps.hexViewSeam.hexOverlay.setDebug(next);
      deps.hexViewSeam.syncHexOverlay();
      deps.overlayView.renderOverlay();
      deps.live.needsRender = true;
    },
    /** The "Disable Hexes" toggle's own hook, so an instrument can flip it without a click. */
    setHexesEnabled(on: boolean): void {
      deps.hexViewSeam.setHexesEnabled(on);
    },
    /**
     * The view-layer panel: the rows it offers, what each switch says, and
     * what the frame actually drew for it.
     *
     * `drawn` is walked off the scene graph and `on` is the switch, so an arm
     * comparing the two is reading whether the switch reached anything.
     */
    get viewLayersDebug(): unknown {
      const counts = deps.bodyScene.state.orrery?.layerCounts() ?? null;
      return {
        open: deps.hexViewSeam.viewLayers?.open ?? false,
        inert: deps.hexViewSeam.layerInert,
        tickPaintOff: deps.hexViewSeam.layerTickPaintOff,
        rows: deps.layerPrefs.rows().map((r) => ({ id: r.id, label: r.label, on: r.get() })),
        drawn: counts,
        hexVisible: deps.hexViewSeam.hexOverlay.object.visible,
        shipVisible: deps.bodyScene.state.shipMarker?.object.visible ?? false,
        beltsVisible: deps.bodyScene.state.belts?.visible ?? false,
        atmoVisible: deps.atmosphereView.atmoSky?.mesh.visible ?? false,
        // No mesh of its own either: the fill is a term in every globe's own
        // shader, so what it draws is how many globes are compositing it.
        artificialVisible: deps.bodyScene.state.orrery?.artificialCount() ?? { drawn: 0, total: 0 },
        // No mesh of its own to walk here: the line lives on simApi's side and
        // the sim does not tick while this menu is open, so `enabled` (not a
        // frozen `.visible`) is the only live reading of what it draws.
        trajectoryVisible: simApi.trajectory.enabled(),
      };
    },
    /**
     * The main asteroid belt and Kuiper belt: what each holds and draws.
     *
     * `minAAu`/`maxAAu` are the drawn semi-major-axis range, which is what the
     * belt's own published radial bound describes - an eccentric particle's
     * instantaneous distance from the Sun can carry it past that bound at
     * aphelion, the way a real asteroid's can.
     */
    get beltsDebug(): unknown {
      return deps.bodyScene.state.belts?.debug() ?? null;
    },
    /** `descent-dojo --belts --inject belts-suppress`: every belt drawn empty. */
    setBeltsDebug(next: { suppress?: boolean }): void {
      if (next.suppress !== undefined) deps.bodyScene.state.belts?.setSuppressed(next.suppress);
    },
    /**
     * The you-are-here marker: where it is, where it draws, and what the click
     * on it did.
     *
     * `at` is the sim's own heliocentric position and `world` the point the
     * marker was placed at this frame, so an arm comparing the two is reading
     * whether the marker follows the ship or a copy of it. `ndc` is where that
     * point projects, which is what a click has to be aimed at.
     */
    get shipMarkerDebug(): unknown {
      const w = deps.bodyScene.state.shipMarker?.world ?? null;
      const at = simApi.ship.at();
      const p = w === null ? null
        : deps._v.set(w.x - deps.mount.cameraWorld.x, w.y - deps.mount.cameraWorld.y, w.z - deps.mount.cameraWorld.z).project(deps.mount.camera);
      return {
        mounted: deps.bodyScene.state.shipMarker !== null,
        known: deps.bodyScene.state.shipMarker?.known ?? false,
        visible: deps.bodyScene.state.shipMarker?.object.visible ?? false,
        pickOff: deps.injects.state.shipPickOff,
        drawnAngularDiameterDeg: deps.bodyScene.state.shipMarker?.drawnAngularDiameterDeg ?? 0,
        at: at === null ? null : { x: at.x, y: at.y, z: at.z },
        world: w === null ? null : { x: w.x, y: w.y, z: w.z },
        ndc: p === null ? null : { x: p.x, y: p.y, z: p.z },
        near: (() => {
          const place = deps.cameraRig.shipPlace();
          return place === null ? null : { id: place.nearId, m: place.nearM };
        })(),
        focused: deps.focusRig.shipFocused(),
        focusId: deps.focusRig.focusCam.state.bodyId,
        focusAltitudeM: deps.focusRig.focusCam.state.altitudeM,
        yaw: deps.focusRig.focusCam.state.yaw,
        pitch: deps.focusRig.focusCam.state.pitch,
        bodyId: deps.state.bodyId,
        tier: deps.state.tier,
      };
    },
    /**
     * Where a camera-relative scene-axis point projects, in NDC.
     *
     * Those are the units the world frame writes into every registered node's
     * own position (`src/world/frame.ts`), so an arm holding two drawn points -
     * the ship marker and the trajectory line's near end - puts both through
     * this one camera instead of rebuilding a projection of its own.
     */
    projectFramePoint(x: number, y: number, z: number): { x: number; y: number; z: number } {
      deps.mount.camera.updateMatrixWorld();
      const p = deps._v.set(x, y, z).project(deps.mount.camera);
      return { x: p.x, y: p.y, z: p.z };
    },
    /**
     * What the cursor is on and what is drawn for it.
     *
     * `bodyId` and `cellId` are the pick's own answers, `haloVisible` and
     * `hexHoverDrawn` whether anything was actually put on the pane for them,
     * and `cursor` the shape the page is wearing. An arm reading only the ids
     * would pass on a build that answered the cursor and drew nothing.
     */
    get hoverDebug(): unknown {
      const hex = deps.hexViewSeam.hexOverlay.debug();
      return {
        off: deps.pick.hoverOff,
        inside: deps.pick.hoverInside,
        bodyId: deps.pick.hoverBodyId,
        cellId: deps.pick.hoverCellId,
        haloVisible: deps.bodyScene.state.hoverHalo?.visible ?? false,
        haloAngularDiameterDeg: deps.bodyScene.state.hoverHalo?.drawnAngularDiameterDeg ?? 0,
        /** Whether what is drawn is the rim on a disc's limb or the ring. */
        haloRim: deps.bodyScene.state.hoverHalo?.rim ?? false,
        legacyCap: deps.pick.haloLegacyCap,
        hexHoverCell: hex.hoverCell,
        hexHoverDrawn: hex.hoverDrawn,
        hexDrawnRes: hex.drawnRes,
        cursor: deps.mount.surface.style.cursor,
        tier: deps.state.tier,
        band: deps.focusRig.band(),
      };
    },
    /**
     * `--hover --inject hover-none`: a cursor nothing answers, and
     * `--hover --inject halo-cap`: the ring held at its ceiling over any disc.
     */
    setHoverDebug(next: { off?: boolean; legacyCap?: boolean }): void {
      if (next.off === undefined && next.legacyCap === undefined) return;
      if (next.off !== undefined) deps.pick.hoverOff = next.off;
      if (next.legacyCap !== undefined) deps.pick.haloLegacyCap = next.legacyCap;
      deps.pick.hoverDirty = true;
      deps.live.needsRender = true;
    },
    /** Focus the ship without a click, the way the pick path does. */
    focusShipMarker(): void { deps.navigation.focusShip(); },
    /** `--shipmark --inject ship-nearest`: the marker out of the pick path. */
    setShipMarkerDebug(next: { pickOff?: boolean }): void {
      if (next.pickOff !== undefined) { deps.injects.state.shipPickOff = next.pickOff; deps.live.needsRender = true; }
    },
    /** Flip one row without a click, the way `setHexesEnabled` already does. */
    setViewLayer(id: string, on: boolean): void {
      deps.layerPrefs.rows().find((r) => r.id === id)?.set(on);
      deps.hexViewSeam.viewLayers?.paint();
    },
    /** Open or close the list, which is what a click on its head does. */
    setViewLayersOpen(on: boolean): void {
      deps.hexViewSeam.viewLayers?.setOpen(on);
    },
    setViewLayerDebug(next: { inert?: boolean; tickPaintOff?: boolean }): void {
      if (next.inert !== undefined) deps.hexViewSeam.layerInert = next.inert;
      if (next.tickPaintOff !== undefined) deps.hexViewSeam.layerTickPaintOff = next.tickPaintOff;
    },
    setArtificialDebug(
      next: {
        overMeasured?: boolean; dropTileRole?: boolean; legacyTileFill?: boolean;
        inert?: boolean; reliefInert?: boolean; reliefUnconditioned?: boolean; reliefGlobeOnly?: boolean;
        reliefDatumFocus?: boolean; reliefDatumEye?: boolean; standUnguarded?: boolean; reliefRepeat?: number;
        reliefMainThread?: boolean;
      },
    ): void {
      deps.bodyScene.state.orrery?.setArtificialDebug(next);
      if (next.standUnguarded !== undefined) deps.focusRig.focusCam.setLegacyStandSolve(next.standUnguarded);
      if (next.reliefDatumFocus !== undefined) deps.injects.state.reliefDatumFocus = next.reliefDatumFocus;
      if (next.reliefDatumEye !== undefined) deps.injects.state.reliefDatumEye = next.reliefDatumEye;
      // The same control on the tile band: the bar that says a measured texel
      // did not move has to be able to read it moving there too.
      if (next.overMeasured !== undefined) deps.pyramidView.tileArtificial.setOverMeasured(next.overMeasured);
      // Both reopen the pyramid: `dropTileRole` because the FETCH PLAN itself
      // changes (the same reason `setImageryFloorDebug` reopens), and
      // `legacyTileFill` because a resident mesh's program was compiled
      // against whichever fallback was in effect when it was built.
      if (next.dropTileRole !== undefined && deps.bodyReaders.artificialDebug.dropTileRole !== next.dropTileRole) {
        deps.bodyReaders.artificialDebug.dropTileRole = next.dropTileRole;
        deps.pyramidView.releaseTiles();
        deps.live.cameraDirty = true;
      }
      if (next.legacyTileFill !== undefined
        && deps.bodyReaders.artificialDebug.legacyTileFill !== next.legacyTileFill) {
        deps.bodyReaders.artificialDebug.legacyTileFill = next.legacyTileFill;
        deps.pyramidView.releaseTiles();
        deps.live.cameraDirty = true;
      }
      // The same reason as `legacyTileFill`: a resident mesh's program was
      // compiled with or without the chunk and cannot gain one in place.
      if (next.reliefGlobeOnly !== undefined
        && deps.bodyReaders.artificialDebug.reliefGlobeOnly !== next.reliefGlobeOnly) {
        deps.bodyReaders.artificialDebug.reliefGlobeOnly = next.reliefGlobeOnly;
        deps.pyramidView.releaseTiles();
        deps.live.cameraDirty = true;
      } else if (next.reliefInert !== undefined || next.reliefUnconditioned !== undefined
        || next.reliefRepeat !== undefined || next.reliefMainThread !== undefined) {
        if (next.reliefMainThread !== undefined) deps.pyramidView.reliefMainThread = next.reliefMainThread;
        // The tile relief is baked into each mesh's attributes at build.
        deps.pyramidView.dropReliefHeights();
        deps.pyramidView.remeshTiles();
      }
      deps.live.needsRender = true;
    },
    /**
     * SUNRISE beside the warp row: its button as drawn, the run and sun behind
     * it, and the rover in the plot's own metres, which no warp may move.
     */
    get sunriseDebug(): unknown {
      const btn = deps.overlay.querySelector<HTMLElement>('#atlas-sunrise');
      const rover = deps.live.plot?.rover ?? null;
      return {
        rover: rover ? { x: rover.state.x, z: rover.state.z } : null,
        button: btn ? {
          shown: !btn.classList.contains('hidden'), blocked: btn.classList.contains('blocked'),
          active: btn.classList.contains('active'), title: btn.getAttribute('title'),
        } : null,
        ...(deps.hexViewSeam.sunrise?.debug as object | undefined),
        clockT: gameClock().t,
        clockRate: gameClock().rate,
      };
    },
    /**
     * The view's warp row: what it draws, and what the sim says beside it.
     *
     * `row` is read off the DOM the row actually built and `sim` off the bus,
     * so an arm comparing the two is reading whether the view is a face for
     * the sim's pick or a copy of it.
     */
    get warpRowDebug(): unknown {
      const el = deps.overlay.querySelector('.atlas-warp');
      const buttons = Array.from(deps.overlay.querySelectorAll<HTMLElement>('[data-warp]')).map((b) => ({
        idx: Number(b.dataset['warp']), rate: Number(b.dataset['value']),
        short: (b.textContent ?? '').trim(), title: b.getAttribute('title'),
        active: b.classList.contains('active'), blocked: b.classList.contains('blocked'),
      }));
      const label = deps.overlay.querySelector('#atlas-warp-label');
      const notice = deps.overlay.querySelector('#atlas-warp-notice');
      const levels = simApi.warp.levels();
      return {
        mounted: el !== null,
        shown: el !== null && !el.classList.contains('hidden'),
        copy: deps.hexViewSeam.warpCopy,
        tier: deps.state.tier,
        buttons,
        labelText: label ? (label.textContent ?? '') : null,
        noticeText: notice && !notice.classList.contains('hidden')
          ? (notice.textContent ?? '') : null,
        noticeStanding: notice ? notice.classList.contains('standing') : false,
        sim: {
          levels: levels.map((l) => ({ rate: l.rate, label: l.label, short: l.short })),
          pick: simApi.warp.pick(),
          label: simApi.warp.label(),
          locked: simApi.warp.locked(),
          reachable: levels.map((_l, i) => simApi.warp.reachable(i)),
          reason: levels.map((_l, i) => simApi.warp.reason(i)),
        },
      };
    },
    /**
     * The gap between one body as this view DRAWS it and the ship's own marker,
     * both in the heliocentric metres the view places them at.
     *
     * Read at two instants under warp, which is the only way to tell the two
     * apart: a constant separation is one solar system, a growing one is two
     * clocks running against each other (docs/VERIFICATION.md, --viewwarp).
     * A single sample says nothing, because any offset at all looks the same.
     */
    separationAt(id: string): unknown {
      const m = deps.bodyScene.state.shipMarker;
      if (m === null || !m.known) return { err: 'the view has no ship marker place yet' };
      const b = deps.bodyScene.state.orrery?.byId(id) ?? null;
      if (!b) return { err: `the view draws no body "${id}"` };
      const dx = b.world.x - m.world.x;
      const dy = b.world.y - m.world.y;
      const dz = b.world.z - m.world.z;
      const rect = deps.mount.surface.getBoundingClientRect();
      const halfW = Math.max(1, rect.width) * 0.5;
      const halfH = Math.max(1, rect.height) * 0.5;
      deps.mount.camera.updateMatrixWorld();
      const ps = deps._v.set(m.world.x - deps.mount.cameraWorld.x, m.world.y - deps.mount.cameraWorld.y,
        m.world.z - deps.mount.cameraWorld.z).project(deps.mount.camera);
      const shipNdcX = ps.x;
      const shipNdcY = ps.y;
      const pb = deps._v.set(b.world.x - deps.mount.cameraWorld.x, b.world.y - deps.mount.cameraWorld.y,
        b.world.z - deps.mount.cameraWorld.z).project(deps.mount.camera);
      return {
        ok: true,
        bodyId: id,
        jdTdb: deps.worldHostState.worldEpochJd ?? gameClock().jdTdb(),
        body: { x: b.world.x, y: b.world.y, z: b.world.z },
        ship: { x: m.world.x, y: m.world.y, z: m.world.z },
        separationM: Math.hypot(dx, dy, dz),
        // Where the marker sits on screen against the body, in pixels. The
        // camera's own motion is in both, so this is the ship against the body
        // and nothing else: a marker welded to its parent holds it at a
        // constant, and an orbit sweeps it.
        screenPx: { x: (shipNdcX - pb.x) * halfW, y: (shipNdcY - pb.y) * halfH },
      };
    },
    /** Pick a level through the VIEW's own row, with no click. */
    setViewWarpPick(idx: number): void {
      if (deps.hexViewSeam.warpCopy) deps.hexViewSeam.warpCopyPick = idx;
      else simApi.warp.setPick(idx);
      deps.hexViewSeam.warpRow?.paint();
    },
    setWarpRowDebug(next: { copy?: boolean }): void {
      if (next.copy !== undefined) { deps.hexViewSeam.warpCopy = next.copy; deps.hexViewSeam.warpRow?.paint(); }
    },
    /**
     * What the grid drew, and what it drew it from.
     *
     * The drawn resolution and the cell count are read off the overlay's own
     * build rather than recomputed, so an arm reading this is reading the
     * geometry that is on screen. `renderer.info.render.calls` is not here:
     * `--hex` counts the overlay's own draw call off the scene graph.
     */
    /**
     * Whether this ground lies inside a cell the grid actually DREW.
     *
     * `--hex`'s reach reading samples the visible disc through this rather
     * than through `pick`, which answers for any lat/lon on the body whether
     * a boundary was drawn over it or not.
     */
    hexCovers(latDeg: number, lonDeg: number): boolean {
      return deps.hexViewSeam.hexOverlay.covers(latDeg, lonDeg);
    },
    get hexDebug(): unknown {
      const d = deps.hexViewSeam.hexOverlay.debug();
      const size = deps.hexViewSeam.hexOverlay.cellSize();
      return {
        ...d,
        pickFacet: deps.injects.state.pickFacetGate,
        pickDatum: deps.injects.state.pickDatumGate,
        fieldDrape: deps.injects.state.fieldDrapeDebug,
        datumDrape: deps.injects.state.datumDrapeDebug,
        cardStatic: deps.injects.state.cardStaticDebug,
        claimCircum: deps.injects.state.claimCircumDebug,
        claimStale: deps.globeMaterial.claimStaleDebug,
        cellId: deps.state.cellId,
        visible: deps.hexViewSeam.hexOverlay.object.visible,
        enabled: deps.hexViewSeam.hexesEnabled,
        objects: deps.hexViewSeam.hexOverlay.object.children.filter((c) => c.visible).length,
        areaKm2: size?.areaKm2 ?? null,
        bodyId: deps.bodyReaders.ladderBody(),
        radiusM: deps.surfaceRadius.groundDatumM(deps.bodyReaders.ladderBody()),
        // The frame the rung and the cap are derived in, so an arm reads the
        // overlay's own viewport rather than assuming the browser window's.
        viewportH: Math.max(1, deps.root.clientHeight),
        viewportW: Math.max(1, deps.root.clientWidth),
        band: deps.focusRig.band(),
        tier: deps.state.tier,
        claims: claimsDebug(),
        // The revision `syncGlobeClaims` keys its rebuild off, so an arm can
        // tell a store this tab has not noticed from one it has.
        claimsVersion: claimsVersion(),
      };
    },
    setHonestyDebug(next: { kSource?: string | null; claimRelief?: boolean; creditBlank?: boolean }): void {
      let moved = false;
      if (next.kSource !== undefined) {
        deps.inspector.honestyDebug.kSource = next.kSource;
        moved = true;
      }
      if (next.claimRelief !== undefined) {
        deps.inspector.honestyDebug.claimRelief = next.claimRelief;
        moved = true;
      }
      if (next.creditBlank !== undefined) {
        deps.inspector.honestyDebug.creditBlank = next.creditBlank;
        moved = true;
      }
      if (moved) {
        // The panel is written on state changes, not per frame, so a control
        // armed without this reads the text it was armed to move.
        deps.overlayView.renderOverlay();
        deps.live.needsRender = true;
      }
    },
    /**
     * `--occlusion`'s two knobs.
     *
     * `pyramidHidden` holds the pyramid's meshes out of the frame with the tree
     * otherwise untouched, which is the globe-only state every descent passes
     * through while its tiles arrive - the reading state, not a control.
     * `legacyUnderlay` is the control: the underlay globe writes no depth
     * again, the way it did when a body 9,400 km away drew in front of the one
     * the camera was 12 km above.
     */
    setOcclusionDebug(next: { legacyUnderlay?: boolean; pyramidHidden?: boolean;
      parentAirDown?: boolean }): void {
      if (next.legacyUnderlay !== undefined) {
        deps.pyramidView.occlusionDebug.legacyUnderlay = next.legacyUnderlay;
        const mesh = deps.pyramidView.underlayMesh;
        if (mesh) {
          (mesh.material as THREE.Material).depthWrite = !deps.pyramidView.occlusionDebug.legacyUnderlay;
          deps.pyramidView.globeDepthPushM.value = deps.pyramidView.occlusionDebug.legacyUnderlay
            ? 0 : (deps.bodyScene.state.bodyNode?.drawRadiusM ?? 0) * deps.GLOBE_DEPTH_PUSH_RADII;
        }
      }
      if (next.pyramidHidden !== undefined) deps.pyramidView.occlusionDebug.pyramidHidden = next.pyramidHidden;
      if (next.parentAirDown !== undefined) {
        deps.pyramidView.occlusionDebug.parentAirDown = next.parentAirDown;
        if (next.parentAirDown) {
          if (deps.atmosphereView.atmoBodyId !== null && deps.atmosphereView.atmoBodyId !== deps.bodyReaders.ladderBody()) deps.atmosphereView.unmountAtmo();
        } else {
          deps.atmosphereView.syncAtmoMount();
        }
      }
      deps.live.needsRender = true;
    },
    /** Read back before either knob is believed, and what the globe is doing. */
    get occlusionDebug(): unknown {
      const mesh = deps.pyramidView.underlayMesh;
      return {
        legacyUnderlay: deps.pyramidView.occlusionDebug.legacyUnderlay,
        pyramidHidden: deps.pyramidView.occlusionDebug.pyramidHidden,
        parentAirDown: deps.pyramidView.occlusionDebug.parentAirDown,
        underlay: deps.pyramidView.globeUnderlay,
        underlayBody: mesh?.name ?? null,
        depthPushM: +deps.pyramidView.globeDepthPushM.value.toFixed(3),
        depthWrite: mesh ? (mesh.material as THREE.Material).depthWrite : null,
        renderOrder: mesh?.renderOrder ?? null,
        pyramidDrawn: deps.tileGroup.visible,
        drawnLeaves: deps.pyramidView.tileMeshes.size,
      };
    },
    /** `sea-check`'s controls: `seaDebug` in seaSurface.ts. */
    setSeaDebug(next: { off?: boolean; noFresnel?: boolean; noSky?: boolean }): { on: number; fresnelOn: number } {
      if (next.off !== undefined) seaDebug.off = next.off;
      if (next.noFresnel !== undefined) seaDebug.noFresnel = next.noFresnel;
      if (next.noSky !== undefined) seaDebug.noSky = next.noSky;
      const t = deps.atmosphereView.atmoTerrainUniforms;
      syncSeaDebug(t);
      deps.live.needsRender = true;
      return { on: t['uSeaOn']!.value as number, fresnelOn: t['uSeaFresnelOn']!.value as number };
    },
    /**
     * The frame as linear radiance ahead of the tone curve, averaged over 5x5
     * pixels at each client point, plus the brightest pixel inside `win`. With
     * the aerial term off, pi times it over `sunIrradiance` times mu is a reflectance.
     */
    linearFrame(points: ReadonlyArray<readonly [number, number]>, win: { x: number; y: number; r: number } | null): {
      rgb: number[][]; peak: { x: number; y: number; rgb: number[] } | null;
      sunIrradiance: number; sunColor: number[];
    } {
      const size = deps.renderer.getDrawingBufferSize(new THREE.Vector2());
      const w = size.x;
      const h = size.y;
      const rt = new THREE.WebGLRenderTarget(w, h, { type: THREE.FloatType });
      rt.texture.name = 'linear-frame-probe';
      const prevTarget = deps.renderer.getRenderTarget();
      const prevTone = deps.renderer.toneMapping;
      const px = new Float32Array(w * h * 4);
      try {
        deps.renderer.toneMapping = THREE.NoToneMapping;
        deps.renderer.setRenderTarget(rt);
        deps.mount.subtree.activate();
        deps.mount.worldFrame.applyCameraRelative(deps.mount.cameraWorld);
        deps.renderer.render(deps.mount.scene, deps.mount.camera);
        deps.renderer.resetState();
        deps.renderer.readRenderTargetPixels(rt, 0, 0, w, h, px);
      } finally {
        deps.renderer.setRenderTarget(prevTarget);
        deps.renderer.toneMapping = prevTone;
        rt.dispose();
        deps.live.needsRender = true;
      }
      const r = deps.mount.surface.getBoundingClientRect();
      const toPx = (cx: number, cy: number): [number, number] => [
        Math.min(w - 1, Math.max(0, Math.floor(((cx - r.left) / r.width) * w))),
        Math.min(h - 1, Math.max(0, Math.floor((1 - (cy - r.top) / r.height) * h))),
      ];
      const texel = (x: number, y: number): number[] => {
        const i = (y * w + x) * 4;
        return [px[i]!, px[i + 1]!, px[i + 2]!];
      };
      const rgb = points.map(([cx, cy]) => {
        const [x0, y0] = toPx(cx, cy);
        const sum = [0, 0, 0];
        let n = 0;
        for (let y = Math.max(0, y0 - 2); y <= Math.min(h - 1, y0 + 2); y++) {
          for (let x = Math.max(0, x0 - 2); x <= Math.min(w - 1, x0 + 2); x++) {
            const v = texel(x, y);
            for (let c = 0; c < 3; c++) sum[c]! += v[c]!;
            n++;
          }
        }
        return sum.map((s) => s / n);
      });
      let peak: { x: number; y: number; rgb: number[] } | null = null;
      if (win) {
        const [cx, cy] = toPx(win.x, win.y);
        const rr = Math.round((win.r * w) / r.width);
        let best = -1;
        for (let y = Math.max(0, cy - rr); y <= Math.min(h - 1, cy + rr); y++) {
          for (let x = Math.max(0, cx - rr); x <= Math.min(w - 1, cx + rr); x++) {
            const v = texel(x, y);
            const l = 0.2126 * v[0]! + 0.7152 * v[1]! + 0.0722 * v[2]!;
            if (l > best) {
              best = l;
              peak = { x: r.left + ((x + 0.5) / w) * r.width, y: r.top + (1 - (y + 0.5) / h) * r.height, rgb: v };
            }
          }
        }
      }
      const sun = new THREE.Color(ORRERY_SUN_COLOR);
      return {
        rgb, peak,
        sunIrradiance: deps.atmosphereView.atmoTerrainUniforms['uAtmoSunIrradiance']!.value as number,
        sunColor: [sun.r, sun.g, sun.b],
      };
    },
    setAerialDebug(next: {
      off?: boolean; meshDist?: boolean; fillScale?: boolean; shellGround?: boolean;
      legacySkyOrder?: boolean; aerialTailOnGround?: boolean; legacyLayerHaze?: boolean;
      tableSun?: boolean; skirtHaze?: boolean;
    }): void {
      if (next.tableSun !== undefined) deps.atmosphereView.aerialDebug.tableSun = next.tableSun;
      if (next.legacyLayerHaze !== undefined) {
        deps.atmosphereView.aerialDebug.legacyLayerHaze = next.legacyLayerHaze;
        deps.live.needsRender = true;
      }
      if (next.legacySkyOrder !== undefined
        && next.legacySkyOrder !== deps.atmosphereView.aerialDebug.legacySkyOrder) {
        deps.atmosphereView.aerialDebug.legacySkyOrder = next.legacySkyOrder;
        deps.pyramidView.applyBodyDrawOrder();
        // Before the early return below, or arming this control alone leaves
        // the Atlas holding the frame it drew under the other order.
        deps.live.needsRender = true;
      }
      if (next.off === undefined && next.meshDist === undefined
        && next.fillScale === undefined && next.shellGround === undefined
        && next.aerialTailOnGround === undefined && next.skirtHaze === undefined) return;
      if (next.off !== undefined) {
        deps.atmosphereView.aerialDebug.off = next.off;
        deps.atmosphereView.atmoTerrainUniforms['uAtmoAerialOff']!.value = next.off ? 1 : 0;
      }
      if (next.meshDist !== undefined) {
        deps.atmosphereView.atmoTerrainUniforms['uAtmoMeshDist']!.value = next.meshDist ? 1 : 0;
      }
      if (next.fillScale !== undefined) {
        deps.atmosphereView.atmoTerrainUniforms['uAtmoFillScale']!.value = next.fillScale ? 1 : 0;
      }
      if (next.shellGround !== undefined) {
        deps.atmosphereView.aerialDebug.shellGround = next.shellGround;
        deps.atmosphereView.atmoTerrainUniforms['uAtmoGlobeRadius']!.value = next.shellGround
          ? 0 : (deps.bodyScene.state.bodyNode?.drawRadiusM ?? 0);
      }
      if (next.aerialTailOnGround !== undefined) {
        deps.atmosphereView.atmoTerrainUniforms['uAtmoAerialTailOnGround']!.value = next.aerialTailOnGround ? 1 : 0;
      }
      if (next.skirtHaze !== undefined) {
        deps.atmosphereView.atmoTerrainUniforms['uAtmoSkirtHaze']!.value = next.skirtHaze ? 1 : 0;
      }
      // The Atlas draws on demand: without this a caller that arms the control
      // and screenshots without moving the camera reads the un-controlled frame.
      deps.live.needsRender = true;
    },
    /**
     * Does the sampling chunk the sky and the terrain inject compile and
     * evaluate?
     *
     * The sky is one consumer and the tiles and the plot are two more, so a
     * break in either half of the chunk shows up as a black frame or a
     * console error well before the probe is the thing that catches it; it
     * stays as the fast, isolated check that does not need a mounted tile or
     * plot to run.
     */
    atmosphereGlslProbe(): unknown {
      if (!deps.atmosphereView.atmo) return { ok: false, why: 'no atmosphere is mounted' };
      return deps.atmosphereView.atmo.probeGlsl();
    },
    /**
     * `atmo-check --gpu`: `atmoAerialPerspective` against `atmoSkyRadiance`
     * for the same direction and sun, off the real chunk - the aerial term's
     * convergence at the horizon, read rather than re-derived.
     */
    atmosphereProbeAerial(
      dirLocal: readonly [number, number, number],
      sunLocal: readonly [number, number, number],
      distanceM: number,
    ): unknown {
      if (!deps.atmosphereView.atmo) return { ok: false, why: 'no atmosphere is mounted' };
      return deps.atmosphereView.atmo.probeAerial(dirLocal, sunLocal, distanceM);
    },
    /**
     * Ground around the deployed rover, on the terrain, for `--shadow`.
     *
     * The instrument picks the sample plan and takes the verdict; the app owns
     * the heightfield and the frame the camera is drawn in, so every point
     * comes back as a world position the caller can project itself - the same
     * split `bodies()` uses for the pick. Azimuth is the plot's own convention,
     * the one `applySun` uses: 0 toward -z, +90 degrees toward +x, so the sun's
     * azimuth and a sample's are the same number.
     */
    roverGround(samples: ReadonlyArray<readonly [number, number]>): unknown {
      const rover = deps.live.plot?.rover ?? null;
      if (!deps.live.plot || !deps.live.plotField || !rover) {
        return { deployed: false, points: [] };
      }
      const { alt, az: azFromH } = sunAngles(
        deps.live.plotField.meta.center_lat_deg, deps.solarScrub.sunDeclinationDeg(deps.live.plot.climate),
        (deps.solarScrub.solar.solPhase - 0.5) * 2 * Math.PI);
      const az = deps.plotSun.localSunAzimuth(azFromH);
      const f = deps.live.plot.field;
      const minM = f.meta.elevation.min_m;
      const exag = deps.live.plot.exaggeration;
      const v = new THREE.Vector3();
      const cx = rover.state.x;
      const cz = rover.state.z;
      const groundWorld = (xM: number, zM: number): [number, number, number] => {
        v.set(xM, (f.heightAt(xM, zM) - minM) * exag, zM);
        deps.live.plot!.group.localToWorld(v);
        return [v.x, v.y, v.z];
      };
      const contact = groundWorld(cx, cz);
      const points: Array<[number, number, number]> = [];
      for (const [radM, azDeg] of samples) {
        const a = (azDeg * Math.PI) / 180;
        points.push(groundWorld(cx + radM * Math.sin(a), cz - radM * Math.cos(a)));
      }
      deps.live.plot.group.updateMatrixWorld();
      deps.mount.camera.updateMatrixWorld();
      const camLocal = deps.live.plot.group.worldToLocal(deps.mount.camera.getWorldPosition(v));
      return {
        deployed: true,
        solPhase: deps.solarScrub.solar.solPhase,
        sunAltitudeDeg: (alt * 180) / Math.PI,
        sunAzimuthDeg: (az * 180) / Math.PI,
        /** Where a shadow points on the ground: away from the sun. */
        shadowAzimuthDeg: (((az * 180) / Math.PI) + 180) % 360,
        cameraAzimuthDeg: (Math.atan2(camLocal.x - cx, -(camLocal.z - cz)) * 180) / Math.PI,
        clusterExtentM: deps.live.plot.clusterExtentM,
        contact,
        points,
      };
    },
    /** The orrery light rig's scale, the season factor behind it, and the exposure the renderer holds. */
    get lightRig(): unknown {
      return {
        sunScale: deps.plotSun.state.sunScale,
        seasonLight: deps.plotSun.seasonLight(),
        rendererExposure: deps.renderer.toneMappingExposure,
        exposure: deps.exposureHandle.state.exposure,
      };
    },
    /**
     * The sun the PLOT's own shaders use: analytic, from the site's latitude,
     * the season and the sol phase.
     *
     * NOT the same number as `sunAltitudeDeg`, which is the orrery's own
     * geometry - the body's position against the camera's radial. The two
     * disagree on this tree, and the sky arm reports both, because the dome and
     * the terrain at SURFACE are lit by this one while the tiles at CLAIM and
     * PLOT are lit by the other.
     */
    get plotSun(): unknown {
      const climate = deps.live.plot?.climate ?? deps.bodyReaders.bodyClimate();
      // The same latitude chain `applySun` sets `plotSunDirLocal` from, so a
      // body with NO SITE still reports the sun its own dome is drawn with
      // rather than nothing: reported as null, the sky arm labelled every
      // sample by the orrery's sun and called a lit sky night.
      const lat = deps.live.plotField ? deps.live.plotField.meta.center_lat_deg : (deps.solarScrub.solarLatLon()?.lat ?? null);
      if (lat === null || !climate) return null;
      const { alt, az } = sunAngles(
        lat, deps.solarScrub.sunDeclinationDeg(climate), (deps.solarScrub.solar.solPhase - 0.5) * 2 * Math.PI);
      // Read back off the direction `applySun` last drew the walkaround with (sin dec = sin lat sin alt + cos lat cos alt cos az).
      const d = deps.plotSun.plotSunDirLocal;
      const latR = (lat * Math.PI) / 180;
      const appliedDec = THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(Math.sin(latR) * d.y - Math.cos(latR) * d.z, -1, 1)));
      return {
        declinationDeg: appliedDec,
        altitudeDeg: (alt * 180) / Math.PI,
        azimuthDeg: (deps.plotSun.localSunAzimuth(az) * 180) / Math.PI,
        solPhase: deps.solarScrub.solar.solPhase,
        lsDeg: deps.solarScrub.solar.lsDeg,
        latDeg: lat,
      };
    },
    /** The fraction of the sol the sun is up at the plot's site, read through the panel-yield path's declination. */
    get sunDayFraction(): number | null {
      const climate = deps.live.plot?.climate ?? deps.bodyReaders.bodyClimate();
      const lat = deps.live.plotField ? deps.live.plotField.meta.center_lat_deg : (deps.solarScrub.solarLatLon()?.lat ?? null);
      if (lat === null || !climate) return null;
      return dayFraction(lat, deps.solarScrub.solar.lsDeg, climate, deps.solarScrub.sunDeclinationDeg(climate));
    },
    /** The sun's latitude over the focused body off the orrery node alone: its pole and the direction to Sol. */
    get orreryDeclinationDeg(): number {
      const node = deps.bodyScene.state.bodyNode;
      if (!node) return NaN;
      const toSun = new THREE.Vector3(-node.world.x, -node.world.y, -node.world.z);
      if (toSun.lengthSq() === 0) return NaN;
      const north = new THREE.Vector3(0, 1, 0).applyQuaternion(node.quaternion);
      return THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(toSun.normalize().dot(north), -1, 1)));
    },
    /**
     * The sun's altitude over the local horizon at the camera's ground point.
     *
     * Incidence on flat ground is 90 minus this, and it is read off the same
     * geometry that lights a tile - the body's own position against the
     * camera's own radial - rather than off the plot shader's analytic sun.
     */
    get sunAltitudeDeg(): number {
      const node = deps.bodyScene.state.bodyNode;
      if (!node) return NaN;
      const up = new THREE.Vector3(
        deps.mount.cameraWorld.x - node.world.x, deps.mount.cameraWorld.y - node.world.y, deps.mount.cameraWorld.z - node.world.z,
      );
      const toSun = new THREE.Vector3(-node.world.x, -node.world.y, -node.world.z);
      if (up.lengthSq() === 0 || toSun.lengthSq() === 0) return NaN;
      return (Math.asin(THREE.MathUtils.clamp(up.normalize().dot(toSun.normalize()), -1, 1)) * 180)
        / Math.PI;
    },
    /** Yaw and pitch outright, degrees: what turns a tile edge on the screen. */
    setOrientation(yawDeg: number, pitchDeg: number): void {
      deps.focusRig.focusCam.setOrientation((yawDeg * Math.PI) / 180, (pitchDeg * Math.PI) / 180);
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    /** The walkaround's turn up about the eye, degrees, clamped the way a drag is. */
    setTilt(tiltDeg: number): void {
      deps.cameraRig.plotTilt = THREE.MathUtils.clamp((tiltDeg * Math.PI) / 180, 0, deps.cameraRig.plotTiltMax());
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    get orientation(): { yawDeg: number; pitchDeg: number; tiltDeg: number } {
      return {
        yawDeg: (deps.focusRig.focusCam.state.yaw * 180) / Math.PI,
        pitchDeg: (deps.focusRig.focusCam.state.pitch * 180) / Math.PI,
        tiltDeg: (deps.cameraRig.plotTilt * 180) / Math.PI,
      };
    },
    /** `descent-dojo --lookup`'s and `--sunset`'s controls; returns what holds. */
    setLookUpDebug(next: {
      noTilt?: boolean; skyHidden?: boolean; skipReturnTiltReset?: boolean; skipJumpTiltReset?: boolean;
      sunUv?: boolean; starsHidden?: boolean; dayStars?: boolean; whiteSun?: boolean; whiteGlow?: boolean;
      whiteLight?: boolean; noGreenTint?: boolean; unlitFar?: boolean;
    }): unknown {
      if (next.noTilt !== undefined) deps.cameraRig.lookUpDebug.noTilt = next.noTilt;
      if (next.sunUv !== undefined) deps.cameraRig.lookUpDebug.sunUv = next.sunUv;
      if (next.starsHidden !== undefined) deps.cameraRig.lookUpDebug.starsHidden = next.starsHidden;
      if (next.dayStars !== undefined) deps.cameraRig.lookUpDebug.dayStars = next.dayStars;
      if (next.whiteSun !== undefined) deps.cameraRig.lookUpDebug.whiteSun = next.whiteSun;
      if (next.whiteGlow !== undefined) deps.cameraRig.lookUpDebug.whiteGlow = next.whiteGlow;
      if (next.whiteLight !== undefined) deps.cameraRig.lookUpDebug.whiteLight = next.whiteLight;
      if (next.noGreenTint !== undefined) deps.cameraRig.lookUpDebug.noGreenTint = next.noGreenTint;
      if (next.unlitFar !== undefined) deps.cameraRig.lookUpDebug.unlitFar = next.unlitFar;
      if (next.skyHidden !== undefined) deps.cameraRig.lookUpDebug.skyHidden = next.skyHidden;
      if (next.skipReturnTiltReset !== undefined) deps.cameraRig.lookUpDebug.skipReturnTiltReset = next.skipReturnTiltReset;
      if (next.skipJumpTiltReset !== undefined) deps.cameraRig.lookUpDebug.skipJumpTiltReset = next.skipJumpTiltReset;
      if (deps.cameraRig.lookUpDebug.noTilt) deps.cameraRig.plotTilt = 0;
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
      return { ...deps.cameraRig.lookUpDebug };
    },
    get lookUpDebug(): unknown { return { ...deps.cameraRig.lookUpDebug }; },
    /**
     * Freeze or run the one clock, for `descent-dojo --static`.
     *
     * The clock runs while the Atlas is open (src/main.ts), so a held camera
     * still sees the body turn under the sun. The static arm is about the
     * renderer's own determinism, so it stops time rather than measuring it.
     */
    setClockRate(rate: number): void {
      gameClock().setRate(rate);
      deps.live.needsRender = true;
    },
    get clockRate(): number { return gameClock().rate; },
    /**
     * Pin the one clock at an instant, for a reading that must mean the same
     * thing on two runs.
     *
     * The clock starts at the mission anchor and runs from page load, so the
     * sun stands wherever a page's own load time left it and a body's disc is
     * a different crescent on a slow machine (`tools/preview/body-sweep.mjs`).
     */
    setInstant(secondsFromJ2000: number): void {
      gameClock().setInstant(secondsFromJ2000);
      deps.bodyScene.state.orrery?.propagate();
      deps.solarScrub.syncSolar();
      deps.plotSun.applySun();
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    /** Seconds TDB from J2000, as the one clock stands now. */
    get instantT(): number { return gameClock().t; },
    /** This frame's share of the Sun past the bodies in the eye's sky (`sunCover.ts`), and its debug state. */
    sunCover(): unknown {
      const c = deps.plotSun.cover;
      return { ...c.eye, ground: c.ground, ...c.debug };
    },
    /** `eclipse-light-check`: pass every occulter over, or hold the share at a value (null for the sky's own). */
    setSunCoverDebug(d: { ignoreOcculters?: boolean; force?: number | null; skyIgnores?: boolean }): void {
      const debug = deps.plotSun.cover.debug;
      if (d.skyIgnores !== undefined) debug.skyIgnores = d.skyIgnores;
      if (d.ignoreOcculters !== undefined) debug.ignoreOcculters = d.ignoreOcculters;
      if (d.force !== undefined) debug.force = d.force;
      deps.live.needsRender = true;
    },
    /**
     * Every pass of a body across or near the Sun seen from a ground point
     * between two instants: the clock is stepped at `stepS` and each close
     * approach walked again at one second, then the clock is put back.
     */
    sunCoverScan(latDeg: number, lonDeg: number, t0: number, t1: number, stepS: number, standM = 2): unknown {
      const node = deps.bodyScene.state.bodyNode;
      const orrery = deps.bodyScene.state.orrery;
      if (!node || !orrery) return { err: 'no focused body' };
      if (!(stepS >= 1) || !(t1 > t0)) return { err: 'needs t1 > t0 and a step of 1 s or more' };
      const id = node.row.id;
      const cover = deps.plotSun.cover;
      const held = { ignoreOcculters: cover.debug.ignoreOcculters, force: cover.debug.force };
      cover.debug.ignoreOcculters = false;
      cover.debug.force = null;
      const keepT = gameClock().t;
      const r: SunCoverReading = {
        visible: 1, occulterId: null, sunRadiusDeg: 0, occulterRadiusDeg: 0, separationDeg: 0,
        nearestId: null, marginDeg: Infinity,
      };
      const eye = { x: 0, y: 0, z: 0 };
      let evaluations = 0;
      const read = (t: number): SunCoverReading | null => {
        gameClock().setInstant(t);
        orrery.propagate();
        evaluations++;
        const s = deps.focusRig.focusBodies.surface(id, latDeg, lonDeg);
        const c = deps.focusRig.focusBodies.centre(id);
        if (!s || !c) return null;
        const ux = s.x - c.x, uy = s.y - c.y, uz = s.z - c.z;
        const k = standM / Math.hypot(ux, uy, uz);
        eye.x = s.x + ux * k;
        eye.y = s.y + uy * k;
        eye.z = s.z + uz * k;
        return cover.measure(eye, r);
      };
      const wall0 = performance.now();
      const near: number[] = [];
      let prev = Infinity;
      let prev2 = Infinity;
      try {
        for (let t = t0; t <= t1; t += stepS) {
          const m = read(t)?.marginDeg ?? Infinity;
          if (prev < m && prev <= prev2 && prev < 2) near.push(t - stepS);
          prev2 = prev;
          prev = m;
        }
        const passes: unknown[] = [];
        for (const tc of near) {
          // Deepest cover first; a pass that misses the disc by its nearest approach.
          let best: (SunCoverReading & { t: number }) | null = null;
          let bestScore = Infinity;
          for (let t = tc - stepS; t <= tc + stepS; t += 1) {
            const q = read(t);
            if (!q) continue;
            const score = q.visible < 1 ? q.visible - 2 : q.marginDeg;
            if (score < bestScore) {
              bestScore = score;
              best = { t, ...q };
            }
          }
          if (best) passes.push(best);
        }
        return { bodyId: id, latDeg, lonDeg, evaluations, wallMs: Math.round(performance.now() - wall0), passes };
      } finally {
        cover.debug.ignoreOcculters = held.ignoreOcculters;
        cover.debug.force = held.force;
        gameClock().setInstant(keepT);
        orrery.propagate();
        deps.solarScrub.syncSolar();
        deps.plotSun.applySun();
        deps.live.cameraDirty = true;
        deps.live.needsRender = true;
      }
    },
    /** `body-shadow-check`: every receiver's direct light held at a share (null for its own), or each caster moved off its Sun line. */
    setBodyShadowDebug(d: { force?: number | null; offsetRadii?: number }): void {
      const orrery = deps.bodyScene.state.orrery;
      if (!orrery) return;
      const s = orrery.shadows;
      if (d.force !== undefined) s.debug.force = d.force;
      if (d.offsetRadii !== undefined) s.debug.offsetRadii = d.offsetRadii;
      // Into the uniforms now: a read taken before the next frame's update would otherwise draw the old setting.
      s.update(orrery.bodies);
      deps.live.needsRender = true;
    },
    /**
     * Put the eye over where a caster's shadow axis meets the focused body,
     * or over the sub-solar point (`casterId` null, or an axis that misses).
     */
    bodyShadowAim(casterId: string | null): unknown {
      const node = deps.bodyScene.state.bodyNode;
      const orrery = deps.bodyScene.state.orrery;
      if (!node || !orrery) return { err: 'no focused body' };
      const c = node.world;
      const R = node.row.radius_m;
      const caster = casterId === null ? null : orrery.byId(casterId) ?? null;
      let dir = { x: -c.x, y: -c.y, z: -c.z };
      let hit = false;
      if (caster) {
        const w = caster.world;
        const dw = Math.hypot(w.x, w.y, w.z);
        const ux = w.x / dw, uy = w.y / dw, uz = w.z / dw;
        const ox = w.x - c.x, oy = w.y - c.y, oz = w.z - c.z;
        const b = ox * ux + oy * uy + oz * uz;
        const disc = b * b - (ox * ox + oy * oy + oz * oz - R * R);
        // Nearest the receiver's centre along the axis where it misses.
        const t = disc >= 0 ? -b - Math.sqrt(disc) : -b;
        hit = disc >= 0 && t > 0;
        dir = { x: ox + t * ux, y: oy + t * uy, z: oz + t * uz };
      }
      deps.focusRig.focusCam.faceDirection(dir);
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
      return { bodyId: node.row.id, casterId: caster ? caster.row.id : null, hit };
    },
    /**
     * The focused body's shadows as the share function has them: a grid of
     * screen points cast onto its sphere, each with `sunCover.measure`'s
     * visible share a metre over it, how square it faces the Sun and the eye,
     * and how far the share moves `edgeNdc` either way; the same at each
     * direction in `atEcl`; the casters the frame drew; and the system's
     * positions in ecliptic km.
     */
    bodyShadowReading(cols = 24, rows = 14, edgeNdc = 0,
      atEcl: ReadonlyArray<readonly number[]> | null = null): unknown {
      const node = deps.bodyScene.state.bodyNode;
      const orrery = deps.bodyScene.state.orrery;
      if (!node || !orrery) return { err: 'no focused body' };
      const cover = deps.plotSun.cover;
      const held = { ignoreOcculters: cover.debug.ignoreOcculters, force: cover.debug.force };
      cover.debug.ignoreOcculters = false;
      cover.debug.force = null;
      const r: SunCoverReading = {
        visible: 1, occulterId: null, sunRadiusDeg: 0, occulterRadiusDeg: 0, separationDeg: 0,
        nearestId: null, marginDeg: Infinity,
      };
      const cw = deps.mount.cameraWorld;
      const c = node.world;
      const R = node.row.radius_m;
      const rc = deps.pick.raycaster;
      const q = { x: 0, y: 0, z: 0 };
      const ecl = (v: WorldVec): number[] => [v.x / 1000, -v.z / 1000, v.y / 1000];
      const points: unknown[] = [];
      const bodyMesh = (node as { mesh?: THREE.Mesh }).mesh;
      const shapedMesh = bodyMesh && deps.bodyScene.state.shapeGlobes?.field(node.row.id)
        && bodyMesh.geometry.type === 'BufferGeometry' ? bodyMesh : null;
      const cast = (nx: number, ny: number): { visible: number; sunDot: number; facing: number } | null => {
        deps.pick.ndc.set(nx, ny);
        rc.setFromCamera(deps.pick.ndc, deps.mount.camera);
        const o = rc.ray.origin;
        const d = rc.ray.direction;
        let px: number, py: number, pz: number, nX: number, nY: number, nZ: number;
        if (shapedMesh) {
          // A shaped body is drawn displaced: the point is where the ray meets that mesh, with the facet's normal.
          const hit = rc.intersectObject(shapedMesh, false)[0];
          if (!hit?.face) return null;
          const n = hit.face.normal.clone().transformDirection(shapedMesh.matrixWorld);
          px = hit.point.x + cw.x - c.x; py = hit.point.y + cw.y - c.y; pz = hit.point.z + cw.z - c.z;
          nX = n.x; nY = n.y; nZ = n.z;
        } else {
          const ox = o.x + cw.x - c.x, oy = o.y + cw.y - c.y, oz = o.z + cw.z - c.z;
          const b = ox * d.x + oy * d.y + oz * d.z;
          const disc = b * b - (ox * ox + oy * oy + oz * oz - R * R);
          if (disc < 0) return null;
          const t = -b - Math.sqrt(disc);
          if (!(t > 0)) return null;
          px = ox + t * d.x; py = oy + t * d.y; pz = oz + t * d.z;
          nX = px / R; nY = py / R; nZ = pz / R;
        }
        q.x = c.x + px + nX;
        q.y = c.y + py + nY;
        q.z = c.z + pz + nZ;
        const dS = Math.hypot(q.x, q.y, q.z);
        cover.measure(q, r);
        return {
          visible: r.visible,
          sunDot: -(nX * q.x + nY * q.y + nZ * q.z) / dS,
          facing: -(nX * d.x + nY * d.y + nZ * d.z),
        };
      };
      const pointAt = (nx: number, ny: number) => {
        const hit = cast(nx, ny);
        if (!hit) return null;
        const occulterId = r.occulterId;
        // How far the share moves a pixel either way: a reading on a steep edge is held to that.
        let lo = hit.visible, hi = hit.visible;
        if (edgeNdc > 0) {
          for (const [ex, ey] of [[edgeNdc, 0], [-edgeNdc, 0], [0, edgeNdc], [0, -edgeNdc]] as const) {
            const n = cast(nx + ex, ny + ey);
            if (!n) continue;
            lo = Math.min(lo, n.visible);
            hi = Math.max(hi, n.visible);
          }
        }
        return {
          ndcX: nx, ndcY: ny, visible: hit.visible, occulterId, range: hi - lo,
          sunDot: +hit.sunDot.toFixed(4), facing: +hit.facing.toFixed(4),
        };
      };
      try {
        for (let j = 0; j < rows; j++) {
          for (let i = 0; i < cols; i++) {
            const p = pointAt(-1 + (2 * (i + 0.5)) / cols, 1 - (2 * (j + 0.5)) / rows);
            if (p) points.push(p);
          }
        }
        // Named directions off the receiver's centre, ecliptic, any length: where a known answer puts its point.
        const at = (atEcl ?? []).map(([ex, ey, ez]) => {
          const sx = ex!, sy = ez!, sz = -ey!;
          const n = Math.hypot(sx, sy, sz);
          const nX = sx / n, nY = sy / n, nZ = sz / n;
          q.x = c.x + nX * (R + 1);
          q.y = c.y + nY * (R + 1);
          q.z = c.z + nZ * (R + 1);
          cover.measure(q, r);
          deps._v.set(q.x - cw.x, q.y - cw.y, q.z - cw.z);
          const onScreen = isOnScreen(deps.mount.camera, deps._v);
          deps._v.project(deps.mount.camera);
          const dS = Math.hypot(q.x, q.y, q.z);
          const visible = r.visible, occulterId = r.occulterId, ndcX = deps._v.x, ndcY = deps._v.y;
          return {
            visible, occulterId, ndcX, ndcY, onScreen,
            sunDot: +(-(nX * q.x + nY * q.y + nZ * q.z) / dS).toFixed(4),
            // What the frame draws under that pixel: the near face, which is this point only when the point faces the eye.
            drawn: onScreen ? pointAt(ndcX, ndcY) : null,
          };
        });
        // The sub-solar point, the deepest a caster on the sunward face can reach.
        const dc = Math.hypot(c.x, c.y, c.z);
        q.x = c.x - (c.x / dc) * (R + 1);
        q.y = c.y - (c.y / dc) * (R + 1);
        q.z = c.z - (c.z / dc) * (R + 1);
        cover.measure(q, r);
        const subSolar = { visible: r.visible, occulterId: r.occulterId };
        const shadow = node.shadow;
        const system = orrery.bodies
          .filter((b) => b === node || b.row.parent === node.row.id || b.row.id === node.row.parent
            || (node.row.parent !== null && node.row.parent !== 'sol' && b.row.parent === node.row.parent))
          .map((b) => ({ id: b.row.id, radiusKm: b.row.radius_m / 1000, eclKm: ecl(b.world) }));
        return {
          bodyId: node.row.id, radiusKm: R / 1000, t: gameClock().t,
          casters: shadow ? [...shadow.casterIds] : null,
          eye: shadow ? [shadow.uniforms.uBsEye.value.x, shadow.uniforms.uBsEye.value.y] : null,
          subSolar, system, cols, rows, points, at,
        };
      } finally {
        cover.debug.ignoreOcculters = held.ignoreOcculters;
        cover.debug.force = held.force;
      }
    },
    /**
     * The frame as drawn, but into a float target with no tone mapping: Rec.
     * 709 luminance in the renderer's linear units, rows top first, so a ratio
     * of two readings is a ratio of light.
     */
    linearLuma(width = 160): { w: number; h: number; luma: number[] } {
      const w = Math.max(8, Math.round(width));
      const h = Math.max(1, Math.round(w / Math.max(0.1, deps.cameraRig.aspect())));
      const rt = new THREE.WebGLRenderTarget(w, h, { type: THREE.FloatType });
      rt.texture.name = 'linear-frame-probe';
      const prevTarget = deps.renderer.getRenderTarget();
      const prevTone = deps.renderer.toneMapping;
      const px = new Float32Array(w * h * 4);
      try {
        deps.renderer.toneMapping = THREE.NoToneMapping;
        deps.renderer.setRenderTarget(rt);
        deps.mount.subtree.activate();
        deps.mount.worldFrame.applyCameraRelative(deps.mount.cameraWorld);
        deps.farField.drawFrame();
        deps.renderer.resetState();
        deps.renderer.readRenderTargetPixels(rt, 0, 0, w, h, px);
      } finally {
        deps.renderer.setRenderTarget(prevTarget);
        deps.renderer.toneMapping = prevTone;
        rt.dispose();
        deps.live.needsRender = true;
      }
      const luma: number[] = new Array<number>(w * h);
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = ((h - 1 - y) * w + x) * 4;
          luma[y * w + x] = 0.2126 * px[i]! + 0.7152 * px[i + 1]! + 0.0722 * px[i + 2]!;
        }
      }
      return { w, h, luma };
    },
    /** The instant a season or sol scrub, or a sweep, is previewing; null while the view reads the clock. */
    get previewT(): number | null { return deps.viewClockHandle.state.previewT; },
    /** The instant the app opens at, so an instrument carries no epoch of its own. */
    get anchorT(): number { return MISSION_ANCHOR_T; },
    /** Scrub within the sol, which is what turns the body under the sun. */
    setSol(phase: number): void {
      deps.solarScrub.scrubToSolPhase(phase);
      deps.plotSun.applySun();
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    /**
     * Daylight over a body point, for any check that needs it: scrub the one
     * clock to the nearest instant at which the sun stands at least
     * `minAltitudeDeg` over (`latDeg`, `lonDeg`), and report the altitude it
     * landed on.
     *
     * The real clock and the real body rotation, so it reads the same at every
     * tier - the tiles at ORBIT are lit by this geometry and the plot's own
     * shaders at SURFACE follow the same instant. Above the ORBIT band edge
     * the EYE does not move with the body, so a caller that also needs the lit
     * ground in frame turns the eye with `eyeOverPoint`.
     */
    sunlightAt(latDeg: number, lonDeg: number, minAltitudeDeg: number): unknown {
      const r = deps.solarScrub.scrubToSunlight(latDeg, lonDeg, minAltitudeDeg);
      deps.plotSun.applySun();
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
      return r;
    },
    /**
     * Turn the eye until its own ground point is this one, leaving the aim and
     * the altitude alone.
     *
     * Exact where the rig is anchored on the body's centre, which is every
     * framing at and above the ORBIT band edge; below it the aim converges on
     * the focus point and the eye's own radial is no longer the basis's, so
     * the convergence and the arc actually achieved come back with it.
     */
    eyeOverPoint(latDeg: number, lonDeg: number): unknown {
      // Off `focusBodies` and not off `bodyNode`, which is the orrery's globe
      // for this body and is null on the one the focus camera can address
      // without having: Sol, whose disc is its own mesh.
      const id = deps.bodyScene.state.bodyNode?.row.id ?? deps.bodySwitch.state.appliedBodyId;
      const centre = id === null ? null : deps.focusRig.focusBodies.centre(id);
      if (id === null || centre === null) return { err: 'no focused body' };
      const s = deps.focusRig.focusBodies.surface(id, latDeg, lonDeg);
      if (!s) return { err: `${id} has no surface point at that lat/lon` };
      deps.focusRig.focusCam.faceDirection({
        x: s.x - centre.x, y: s.y - centre.y, z: s.z - centre.z,
      });
      deps.focusRig.focusCam.pinPitch();
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
      const eye = deps.focusRig.focusCam.eyeWorld();
      deps.solarScrub._litUp.set(eye.x - centre.x, eye.y - centre.y, eye.z - centre.z);
      deps.solarScrub._litSun.set(s.x - centre.x, s.y - centre.y, s.z - centre.z);
      const off = deps.solarScrub._litUp.lengthSq() === 0 || deps.solarScrub._litSun.lengthSq() === 0 ? null
        : (deps.solarScrub._litUp.normalize().angleTo(deps.solarScrub._litSun.normalize()) * 180) / Math.PI;
      const aim = deps.focusRig.focusCam.aim();
      return {
        yawDeg: (deps.focusRig.focusCam.state.yaw * 180) / Math.PI,
        pitchDeg: (deps.focusRig.focusCam.state.pitch * 180) / Math.PI,
        convergence: aim.convergence,
        offDeg: off === null ? null : +off.toFixed(3),
      };
    },
    setAltitude(m: number): void {
      deps.exposureHandle.setAltitude(m);
      deps.live.needsRender = true;
    },
    goTo,
    /** A LAND is committed: start the ground's own reads now. Called after
     *  `goTo`, which is what puts the ladder on the body and picks its site. */
    landCommitted(): void {
      deps.descentLoad.state.landPressMs = performance.now();
      // From the press the pipe is the descent's. Which of the focus's reads
      // that leaves running is `prefetchDescent`'s call, since it is what
      // resolves the site the press actually came down on.
      deps.descentLoad.prefetchDescent();
    },
    /**
     * The body the ship is at and the point it is over, as the LAND affordance
     * reads them, or null where no landing is offered.
     *
     * The warm's own trigger from the bridge: nothing is focused in the Atlas
     * there, and the ground a press would want is the ground under the ship.
     * Nothing here moves the camera or the framing.
     */
    shipFocus(at: { bodyId: string; lat: number; lon: number } | null): void {
      deps.descentLoad.state.shipTrack = at;
      deps.descentLoad.armGroundWarm();
    },
    /** What the registry's landing did with a focus asked for before it. */
    get landEntryDebug(): unknown {
      return { pending: deps.bodyScene.state.pendingFocus !== null, fits: deps.bodyScene.state.registryFits };
    },
    /** Every registry body: where it projects, how big it is drawn, how far. */
    bodies(): unknown[] {
      const r = deps.mount.surface.getBoundingClientRect();
      deps.mount.camera.updateMatrixWorld();
      const out: unknown[] = [];
      const sunPoint = new THREE.Vector3();
      const sunDir = new THREE.Vector3();
      const camDir = new THREE.Vector3();
      const push = (
        id: string, pickBand: string, mesh: THREE.Mesh, radiusM: number, helio: WorldVec | null,
      ): void => {
        const p = deps._v.copy(mesh.position).project(deps.mount.camera);
        // One draw radius from the centre TOWARD Sol, in the stage's own NDC.
        // An instrument reading a body's NIGHT side needs the sub-solar
        // direction on SCREEN and the app owns the projection, the same split
        // `body-sweep`'s disc plan already uses. The direction comes off the
        // HELIOCENTRIC position and the point off the mesh's: the scene is
        // camera-relative, so Sol is not at its origin, but the two frames
        // share their axes. Null where there is no direction: Sol itself.
        let sunNdcX: number | null = null;
        let sunNdcY: number | null = null;
        // The PHASE, as the cosine of the sun-body-camera angle: which part of
        // the anti-sun side is unlit ground follows from it and nothing else,
        // and the reader cannot get it from |sunNdc - ndc| alone, which is
        // sin of the same angle and cannot tell a crescent from a gibbous.
        let sunPhaseCos: number | null = null;
        if (radiusM > 0 && helio && (helio.x !== 0 || helio.y !== 0 || helio.z !== 0)) {
          sunDir.set(helio.x, helio.y, helio.z).normalize();
          sunPoint.copy(mesh.position).addScaledVector(sunDir, -radiusM).project(deps.mount.camera);
          sunNdcX = sunPoint.x;
          sunNdcY = sunPoint.y;
          if (mesh.position.lengthSq() > 0) {
            sunPhaseCos = sunDir.dot(camDir.copy(mesh.position).normalize());
          }
        }
        // NDC z alone cannot say in front from behind at this scale - see
        // isOnScreen - so the front/behind test is done there, not on p.z.
        const onFrame = mesh.visible && isOnScreen(deps.mount.camera, mesh.position);
        out.push({
          id,
          pickBand,
          // What the marker is drawn on: `availability.descend`, as the orrery
          // recorded it. NOT what the opening view is fit to hold - that is
          // `inFit`, and the two were the same field until the fit became a
          // rule about the solar system rather than about the bake.
          navigable: mesh.userData['available'] === true,
          inFit: deps.bodyReaders.rowById(id) !== null && deps.focusRig.inOpeningFit(deps.bodyReaders.rowById(id)!),
          visible: mesh.visible,
          ndcX: p.x,
          ndcY: p.y,
          ndcZ: p.z,
          sunNdcX,
          sunNdcY,
          sunPhaseCos,
          onFrame,
          clientX: r.left + (p.x * 0.5 + 0.5) * r.width,
          clientY: r.top + (-p.y * 0.5 + 0.5) * r.height,
          trueAngularDiameterDeg: mesh.userData['trueAngularDiameterDeg'] ?? null,
          drawnAngularDiameterDeg: mesh.userData['drawnAngularDiameterDeg'] ?? null,
          distanceKm: mesh.userData['distanceKm'] ?? null,
        });
      };
      for (const b of deps.bodyScene.state.orrery?.bodies ?? []) {
        push(b.row.id, b.row.pick_band, b.mesh, b.drawRadiusM, b.world);
      }
      if (deps.bodyScene.state.orrery) {
        const sol = deps.bodyScene.state.orrery.sol;
        sol.mesh.userData['trueAngularDiameterDeg'] = sol.state.angularDiameterDeg;
        sol.mesh.userData['drawnAngularDiameterDeg'] = sol.state.drawnAngularDiameterDeg;
        sol.mesh.userData['distanceKm'] = sol.state.distanceKm;
        push('sol', 'SYSTEM', sol.mesh, 0, null);
      }
      return out;
    },
    /**
     * The sun and the focused body's own system as they stand over a ground
     * point, or over the eye: elevation and azimuth off the registry's
     * positions, and where each is drawn in the frame. `descent-dojo --lookup`.
     */
    skyAt(latDeg?: number, lonDeg?: number): unknown {
      const node = deps.bodyScene.state.bodyNode;
      const orrery = deps.bodyScene.state.orrery;
      if (!node || !orrery) return { err: 'no focused body' };
      const id = node.row.id;
      const centreW = deps.focusRig.focusBodies.centre(id);
      if (!centreW) return { err: `${id} has no centre` };
      const centre = new THREE.Vector3(centreW.x, centreW.y, centreW.z);
      const at = (la: number, lo: number): THREE.Vector3 | null => {
        const s = deps.focusRig.focusBodies.surface(id, la, lo);
        return s ? new THREE.Vector3(s.x, s.y, s.z) : null;
      };
      const eye = new THREE.Vector3(
        deps.mount.cameraWorld.x + deps.mount.camera.position.x,
        deps.mount.cameraWorld.y + deps.mount.camera.position.y,
        deps.mount.cameraWorld.z + deps.mount.camera.position.z);
      const ground = deps.hexViewSeam.cameraGroundPoint();
      const own = latDeg !== undefined && lonDeg !== undefined;
      const la = own ? latDeg : ground.lat;
      const lo = own ? lonDeg : ground.lon;
      const obs = own ? at(la, lo) : eye;
      const here = at(la, lo);
      const eastOf = at(la, lo + 0.01);
      if (!obs || !here || !eastOf) return { err: `${id} has no surface point at that lat/lon` };
      const up = obs.clone().sub(centre).normalize();
      const east = eastOf.sub(here);
      east.addScaledVector(up, -east.dot(up)).normalize();
      const north = new THREE.Vector3().crossVectors(up, east);
      const DEG = 180 / Math.PI;
      const angles = (d: THREE.Vector3): { elevationDeg: number; azimuthDeg: number } => {
        const az = Math.atan2(d.dot(east), d.dot(north)) * DEG;
        return {
          elevationDeg: +(Math.asin(THREE.MathUtils.clamp(d.dot(up), -1, 1)) * DEG).toFixed(4),
          azimuthDeg: +((az + 360) % 360).toFixed(4),
        };
      };
      const r = deps.mount.surface.getBoundingClientRect();
      deps.mount.camera.updateMatrixWorld();
      const tanHalfFov = Math.tan((deps.mount.camera.fov * Math.PI) / 360);
      const drawnAt = new THREE.Vector3();
      const toSun = new THREE.Vector3();
      const toObs = new THREE.Vector3();
      const bodies: unknown[] = [];
      const push = (
        rowId: string, mesh: THREE.Object3D, world: THREE.Vector3, drawnDeg: number, trueDeg: number,
        distanceKm: number, row: BodyRow | null,
      ): void => {
        const d = world.clone().sub(obs).normalize();
        mesh.getWorldPosition(drawnAt);
        const onScreen = isOnScreen(deps.mount.camera, drawnAt);
        const p = drawnAt.clone().project(deps.mount.camera);
        let phaseDeg: number | null = null;
        let eclipsed = false;
        if (row) {
          toSun.copy(world).negate().normalize();
          toObs.copy(obs).sub(world).normalize();
          phaseDeg = +(toSun.angleTo(toObs) * DEG).toFixed(2);
          // In the shadow of its primary: the sun line from the body passes
          // within the primary's radius. The primary is never Sol here.
          const parent = row.parent === null ? null : orrery.byId(row.parent);
          if (parent && row.parent !== 'sol') {
            const pc = new THREE.Vector3(parent.world.x, parent.world.y, parent.world.z).sub(world);
            const along = pc.dot(toSun);
            eclipsed = along > 0 && pc.addScaledVector(toSun, -along).length() < parent.row.radius_m;
          }
        }
        let visible = mesh.visible;
        for (let o = mesh.parent; o && visible; o = o.parent) visible = o.visible;
        bodies.push({
          id: rowId,
          ...angles(d),
          phaseDeg,
          eclipsed,
          visible,
          onScreen,
          ndcX: +p.x.toFixed(4),
          ndcY: +p.y.toFixed(4),
          clientX: r.left + (p.x * 0.5 + 0.5) * r.width,
          clientY: r.top + (-p.y * 0.5 + 0.5) * r.height,
          pxRadius: (Math.tan((drawnDeg * Math.PI) / 360) / tanHalfFov) * (r.height / 2),
          drawnAngularDiameterDeg: drawnDeg,
          trueAngularDiameterDeg: trueDeg,
          distanceKm,
        });
      };
      const sol = orrery.sol;
      push('sol', sol.mesh, new THREE.Vector3(0, 0, 0), sol.state.drawnAngularDiameterDeg,
        sol.state.angularDiameterDeg, sol.state.distanceKm, null);
      for (const b of orrery.bodies) {
        if (!deps.cameraRig.inLocalSky(b.row)) continue;
        push(b.row.id, b.mesh, new THREE.Vector3(b.world.x, b.world.y, b.world.z),
          (b.mesh.userData['drawnAngularDiameterDeg'] as number | undefined) ?? 0,
          (b.mesh.userData['trueAngularDiameterDeg'] as number | undefined) ?? 0,
          (b.mesh.userData['distanceKm'] as number | undefined) ?? 0, b.row);
      }
      const fwd = deps.mount.camera.getWorldDirection(new THREE.Vector3());
      return {
        bodyId: id,
        latDeg: la,
        lonDeg: lo,
        observer: own ? 'ground' : 'eye',
        view: { ...angles(fwd), tiltDeg: (deps.cameraRig.plotTilt * 180) / Math.PI },
        stage: { left: r.left, top: r.top, width: r.width, height: r.height },
        bodies,
      };
    },
    /**
     * The catalogue's brightest stars inside the frame, where each projects,
     * and whether the field is drawn at all. `descent-dojo --lookup`'s night
     * reading looks for a star at each of these.
     *
     * DEV only. A shipped build has no use for it and this returns null there.
     */
    starsInFrame(max = 40): unknown {
      if (!import.meta.env.DEV) return null;
      const pts = deps.stars.points;
      let drawn = pts.visible;
      for (let o = pts.parent; o && drawn; o = o.parent) drawn = o.visible;
      const pos = pts.geometry.getAttribute('position');
      const col = pts.geometry.getAttribute('aColor');
      const size = pts.geometry.getAttribute('aSize');
      const r = deps.mount.surface.getBoundingClientRect();
      const stage = { left: r.left, top: r.top, width: r.width, height: r.height };
      if (!pos || !col || !size) return { drawn, count: 0, inFrame: 0, stars: [], stage };
      pts.updateMatrixWorld();
      deps.mount.camera.updateMatrixWorld();
      const v = new THREE.Vector3();
      const inFrame: Array<{ k: number; sizePx: number; clientX: number; clientY: number }> = [];
      for (let i = 0; i < pos.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(pts.matrixWorld);
        if (!isOnScreen(deps.mount.camera, v)) continue;
        v.project(deps.mount.camera);
        inFrame.push({
          k: +(0.2126 * col.getX(i) + 0.7152 * col.getY(i) + 0.0722 * col.getZ(i)).toFixed(4),
          sizePx: +size.getX(i).toFixed(2),
          clientX: r.left + (v.x * 0.5 + 0.5) * r.width,
          clientY: r.top + (-v.y * 0.5 + 0.5) * r.height,
        });
      }
      inFrame.sort((a, b) => b.k - a.k);
      return {
        drawn, count: pos.count, inFrame: inFrame.length, stars: inFrame.slice(0, max), stage,
      };
    },
    /**
     * Frame a scope's bodies, the way a mount or a resize does.
     *
     * `descent-dojo`'s pick criterion is about every SYSTEM row projecting in
     * front of the camera and staying hittable, which is the `all` framing; the
     * opening view reaches `OPENING_FIT_MAX_A_AU` instead, so the sweep asks for
     * the framing it is about rather than assuming the default is it.
     */
    frameAll(scope: FramingScope): void {
      deps.focusRig.fitOpening(deps.cameraRig.aspect(), scope);
      deps.live.cameraDirty = true;
      deps.live.needsRender = true;
    },
    /** The rule the opening fit is, for a criterion to read rather than copy. */
    get openingFit(): { reachAu: number; injectedReachAu: number | null; ids: readonly string[] } {
      return {
        reachAu: deps.focusRig.state.openingFitReachAu ?? deps.focusRig.OPENING_FIT_MAX_A_AU,
        injectedReachAu: deps.focusRig.state.openingFitReachAu,
        ids: deps.bodyScene.state.bodyRows.filter((r) => deps.focusRig.inOpeningFit(r)).map((r) => r.id),
      };
    },
    /**
     * `descent-dojo --inject fit-pluto`: widen the opening fit past Pluto.
     *
     * The only control the framing bar has. Every px the criterion reads is a
     * fraction of the frame, and the frame is the fit - so a bar on Mars's
     * separation from Sol can only be moved by moving the fit's reach, which
     * is what the named constant refuses to do.
     */
    setOpeningFitReachAu(au: number | null): void {
      if (deps.focusRig.state.openingFitReachAu === au) return;
      deps.focusRig.state.openingFitReachAu = au;
      if (deps.state.tier === 0) {
        deps.focusRig.fitOpening(deps.cameraRig.aspect());
        deps.live.cameraDirty = true;
        deps.live.needsRender = true;
      }
    },
    /** `descent-dojo --manifest --inject manifest-miswire`'s own control. */
    setManifestDebug(next: { swap?: boolean }): { swap: readonly string[] | null; paints: number } {
      return deps.manifest.setDebug(next);
    },
    /** The eye, scene axes, metres, as of the frame this tick last drew. */
    /** The view's compass heading, degrees from the body's true north at the eye, whichever rig draws it. */
    headingDeg(): number | null {
      const node = deps.bodyScene.state.bodyNode;
      if (!node) return null;
      const up = new THREE.Vector3(
        deps.mount.cameraWorld.x - node.world.x, deps.mount.cameraWorld.y - node.world.y, deps.mount.cameraWorld.z - node.world.z,
      ).normalize();
      const north = new THREE.Vector3(0, 1, 0).applyQuaternion(node.quaternion);
      north.addScaledVector(up, -north.dot(up)).normalize();
      const east = new THREE.Vector3().crossVectors(north, up);
      const d = deps.mount.camera.getWorldDirection(new THREE.Vector3());
      const h = (Math.atan2(d.dot(east), d.dot(north)) * 180) / Math.PI;
      return Number.isFinite(h) ? +((h + 360) % 360).toFixed(3) : null;
    },
    eyeWorld(): WorldVec {
      return { x: deps.mount.cameraWorld.x, y: deps.mount.cameraWorld.y, z: deps.mount.cameraWorld.z };
    },
    /**
     * Whatever is actually drawn under a screen point - the globe or a
     * pyramid tile, whichever the raycast meets first - in the same
     * camera-relative frame `eyeWorld()` and `bodies()` both use.
     *
     * `descent-dojo`'s drag arm's own ground point: add `eyeWorld()` to get an
     * absolute position, drag, then hand that sum to `projectPoint` rather
     * than re-picking a point that may have moved under a different pixel.
     */
    groundPointAt(clientX: number, clientY: number): WorldVec | null {
      const r = deps.mount.surface.getBoundingClientRect();
      deps.pick.ndc.x = ((clientX - r.left) / r.width) * 2 - 1;
      deps.pick.ndc.y = -((clientY - r.top) / r.height) * 2 + 1;
      deps.pick.raycaster.setFromCamera(deps.pick.ndc, deps.mount.camera);
      const drawn = deps.pick.drawnGroundAtRay();
      if (drawn !== undefined) return drawn && { x: drawn.x, y: drawn.y, z: drawn.z };
      // A mounted tile wins the pixel over the globe whenever both cover it -
      // the globe stops writing depth once tiles are drawn, so it is what is
      // geometrically nearer and not what is drawn that has to lose the tie.
      const tileTargets = [...deps.pyramidView.tileMeshes.values()].map((h) => h.object);
      const tileHit = tileTargets.length
        ? deps.pick.raycaster.intersectObjects(tileTargets, false)[0]
        : undefined;
      if (tileHit) return { x: tileHit.point.x, y: tileHit.point.y, z: tileHit.point.z };
      const globe = deps.bodyReaders.bodyMesh();
      const hit = globe ? deps.pick.raycaster.intersectObject(globe, false)[0] : undefined;
      return hit ? { x: hit.point.x, y: hit.point.y, z: hit.point.z } : null;
    },
    /**
     * What the frame as it stands shows above its centre row, off rays and not pixels: the share of a
     * `cols` x `rows` grid over the upper half that meets no leaf or globe, and how far a ray straight
     * down from the eye travels to them. The plot is displaced in its vertex shader, where no raycast
     * sees it, so it is stood in for by the leaves `plotAgainstLeaves` holds it to.
     */
    skyAboveCentre(cols = 16, rows = 8): unknown {
      const node = deps.bodyScene.state.bodyNode;
      if (!node) return { err: 'no body node' };
      const rc = deps.pick.raycaster;
      const tileTargets = [...deps.pyramidView.tileMeshes.values()].map((h) => h.object);
      const globe = deps.bodyReaders.bodyMesh();
      const groundM = (): number | null => {
        const drawn = deps.pick.drawnGroundAtRay();
        if (drawn) return drawn.distanceTo(rc.ray.origin);
        if (drawn === null) return null;
        const t = tileTargets.length ? rc.intersectObjects(tileTargets, false)[0] : undefined;
        const g = t ?? (globe ? rc.intersectObject(globe, false)[0] : undefined);
        return g ? g.distance : null;
      };
      let sky = 0;
      for (let j = 0; j < rows; j++) {
        for (let i = 0; i < cols; i++) {
          deps.pick.ndc.set(-1 + (2 * (i + 0.5)) / cols, 1 - (j + 0.5) / rows);
          rc.setFromCamera(deps.pick.ndc, deps.mount.camera);
          if (groundM() === null) sky++;
        }
      }
      const radial = new THREE.Vector3(
        deps.mount.cameraWorld.x + deps.mount.camera.position.x - node.world.x,
        deps.mount.cameraWorld.y + deps.mount.camera.position.y - node.world.y,
        deps.mount.cameraWorld.z + deps.mount.camera.position.z - node.world.z,
      ).normalize();
      rc.set(deps.mount.camera.getWorldPosition(new THREE.Vector3()), radial.clone().negate());
      const down = groundM();
      return {
        skyFrac: +(sky / (cols * rows)).toFixed(4), rays: cols * rows,
        eyeOverLeavesM: down === null ? null : +down.toFixed(3),
        plotUpVsRadialDeg: +THREE.MathUtils.radToDeg(
          Math.acos(THREE.MathUtils.clamp(deps.plotMount.plotUpWorld.dot(radial), -1, 1))).toFixed(4),
      };
    },
    /** Project a point held in the same absolute frame `eyeWorld()` answers in. */
    projectPoint(point: WorldVec): { clientX: number; clientY: number; onScreen: boolean } {
      const r = deps.mount.surface.getBoundingClientRect();
      deps._v.set(point.x - deps.mount.cameraWorld.x, point.y - deps.mount.cameraWorld.y, point.z - deps.mount.cameraWorld.z);
      const onScreen = isOnScreen(deps.mount.camera, deps._v);
      deps._v.project(deps.mount.camera);
      return {
        clientX: r.left + (deps._v.x * 0.5 + 0.5) * r.width,
        clientY: r.top + (-deps._v.y * 0.5 + 0.5) * r.height,
        onScreen,
      };
    },
    /** A body-fixed lat/lon on the datum sphere, absolute scene axes, metres. */
    bodySurfacePoint(bodyId: string, latDeg: number, lonDeg: number): WorldVec | null {
      return deps.focusRig.focusBodies.surface(bodyId, latDeg, lonDeg);
    },
    /** `descent-dojo --drag --inject invert-yaw`: the pre-fix horizontal control. */
    setDragDebug(next: { invertYaw?: boolean }): { invertYaw: boolean } {
      if (next.invertYaw !== undefined) deps.input.dragInvertYaw = next.invertYaw;
      return { invertYaw: deps.input.dragInvertYaw };
    },
    get dragDebug(): { invertYaw: boolean } { return { invertYaw: deps.input.dragInvertYaw }; },
    /**
     * Pin the point the descent is anchored on, body-fixed degrees, or null to
     * hand it back to the app.
     *
     * `descent-dojo --crossing --lat/--lon`: the arm has to walk the sites the
     * yaw reference is worst at, and no pose it can reach derives them.
     */
    setAnchorDebug(next: { lat: number; lon: number } | null): unknown {
      const id = deps.bodyReaders.ladderBody();
      deps.descentAnchorHandle.pinDescentAnchor(next && id !== null ? { body: id, lat: next.lat, lon: next.lon } : null);
      return deps.descentAnchorHandle.anchorPin;
    },
    /**
     * Hold the descent on the point a ship landed on, or hand the aim back.
     *
     * The sim's own entry, beside `goTo` and called before it: a grounded
     * ship's hand-off has a contact point and the ladder must not re-aim off
     * it at the surveyed site. Null is what `src/main.ts` sends when the
     * hand-off closes, so the next descent aims where it always did - which
     * means dropping `landing` too: left standing, `descentAnchor()`'s
     * `landing?.body === id` branch would hand a later siteless descent from
     * cruise the old contact point instead of re-deriving from where the eye
     * is looking.
     */
    setGroundAnchor(next: { bodyId: string; lat: number; lon: number } | null): void {
      deps.descentAnchorHandle.pinDescentAnchor(next === null
        ? null : { body: next.bodyId, lat: next.lat, lon: next.lon });
      if (next === null) {
        deps.descentAnchorHandle.landing = null;
        // The LAND point belongs to the hand-off that is closing too: left
        // standing, `namedLanding` would still match a later visit to this
        // body on it, whether or not that visit ever reruns `syncBody()`.
        // Drops the cached LAND point directly: `descentFor` only refreshes when
        // `syncBody()` reruns, which a same-body retarget skips, so a clear that
        // only reaches `focusCam.state` would still read back a stale one.
        deps.descentLoad.state.descentFor = null;
        deps.focusRig.focusCam.setAnchor(null, null);
      }
    },
    /**
     * The claim card `renderParcel` builds for the loaded plot's own first
     * surveyed cell, and the same gate it renders with.
     *
     * `grounded-handoff-check.mjs`'s UNSURVEYED bar reads the real card HTML
     * the STAKE button ships in, not a second copy of the gate it is built
     * from - a check that only ever agrees with its own boolean is not one.
     */
    get parcelDebug(): { unsurveyed: boolean; cardHtml: string } | null {
      const cell = deps.live.plotSubs[0];
      return cell ? { unsurveyed: deps.descentAnchorHandle.groundUnsurveyed(), cardHtml: deps.inspector.renderParcel(cell) } : null;
    },
    // Each seam carries the probe members that read only its own state; the
    // descriptors are copied rather than spread because `honestyDebug` is an
    // accessor and a spread would freeze its first reading here.
  }, Object.assign(
    Object.getOwnPropertyDescriptors(deps.inspector.probe),
    Object.getOwnPropertyDescriptors(deps.solarScrub.probe),
    Object.getOwnPropertyDescriptors(deps.siteChips.probe),
    Object.getOwnPropertyDescriptors(deps.pick.probe),
    Object.getOwnPropertyDescriptors(deps.structureView.probe),
  ));
}

export type ProbeHandle = ReturnType<typeof createProbe>;
